import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  Alert,
  alpha,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
   IconButton,
  InputAdornment,
  InputLabel,
  LinearProgress,
  MenuItem,
  Pagination,
  Select,
  Skeleton,
  Snackbar,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useTheme,
  keyframes,
} from "@mui/material";

import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import InventoryIcon from "@mui/icons-material/Inventory";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import PointOfSaleIcon from "@mui/icons-material/PointOfSale";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import CategoryIcon from "@mui/icons-material/Category";
import StraightenIcon from "@mui/icons-material/Straighten";
import BusinessIcon from "@mui/icons-material/Business";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import ContentCutIcon from "@mui/icons-material/ContentCut";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import BarChartIcon from "@mui/icons-material/BarChart";
import HistoryIcon from "@mui/icons-material/History";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import SettingsIcon from "@mui/icons-material/Settings";
import PaymentsIcon from "@mui/icons-material/Payments";
import PersonIcon from "@mui/icons-material/Person";
import StorefrontIcon from "@mui/icons-material/Storefront";
import SpaIcon from "@mui/icons-material/Spa";
import ScienceIcon from "@mui/icons-material/Science";
import SellIcon from "@mui/icons-material/Sell";
import WarehouseIcon from "@mui/icons-material/Warehouse";
import SyncIcon from "@mui/icons-material/Sync";
import LowPriorityIcon from "@mui/icons-material/LowPriority";
import BoltIcon from "@mui/icons-material/Bolt";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import TuneIcon from "@mui/icons-material/Tune";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import AssignmentReturnIcon from "@mui/icons-material/AssignmentReturn";
import LocalFireDepartmentIcon from "@mui/icons-material/LocalFireDepartment";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

const API_BASE =
  process.env.REACT_APP_API_BASE ||
  "https://panel.haymestasarim.com/api";

const BRAND = {
  blue: "#0B4D8B",
  blue2: "#1565C0",
  orange: "#F58220",
  orange2: "#FFB15C",
  dark: "#0F172A",
  ink: "#1E293B",
  muted: "#64748B",
  bg: "#F8FAFC",
  softBlue: "#EAF4FF",
  softOrange: "#FFF3E6",
  success: "#10B981",
  danger: "#EF4444",
  warning: "#F59E0B",
  info: "#06B6D4",
  border: "rgba(15,23,42,0.10)",
};

function Grid({
  container = false,
  item = false,
  xs = 12,
  sm,
  md,
  lg,
  xl,
  spacing = 0,
  rowSpacing,
  columnSpacing,
  alignItems,
  justifyContent,
  children,
  sx = {},
  ...props
}) {
  const makeSpan = (v) => {
    const n = Math.max(1, Math.min(12, Number(v || 12)));
    return `span ${n} / span ${n}`;
  };

  if (container) {
    const gap = spacing || 0;
    const rowGap = rowSpacing ?? gap;
    const colGap = columnSpacing ?? gap;

    return (
      <Box
        {...props}
        sx={{
          width: "100%",
          minWidth: 0,
          display: "grid",
          gridTemplateColumns: "repeat(12, minmax(0, 1fr))",
          rowGap: (theme) =>
            typeof rowGap === "number" ? theme.spacing(rowGap) : rowGap,
          columnGap: (theme) =>
            typeof colGap === "number" ? theme.spacing(colGap) : colGap,
          alignItems,
          justifyContent,
          ...sx,
        }}
      >
        {children}
      </Box>
    );
  }

  return (
    <Box
      {...props}
      sx={{
        minWidth: 0,
        width: "100%",
        gridColumn: {
          xs: makeSpan(xs),
          sm: makeSpan(sm ?? xs),
          md: makeSpan(md ?? sm ?? xs),
          lg: makeSpan(lg ?? md ?? sm ?? xs),
          xl: makeSpan(xl ?? lg ?? md ?? sm ?? xs),
        },
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

const STOCK_TEXT = {
  tr: {
    pageTitle: "Kurumsal Stok Yönetimi",
    pageSubtitle:
      "QR Menü, Beauty, restoran, cafe ve hizmet işletmeleri için ürün, hammadde, sarf malzeme ve satış stoklarını tek panelden yönetin.",
    dashboard: "Anasayfa",
    stockList: "Stok Listesi",
    quickSale: "Ürün Satışı",
    movements: "Stok Hareketleri",
    critical: "Kritik Stok",
    counting: "Stok Sayımı",
    reports: "Raporlar",
    templates: "Şablonlar",
    definitions: "Tanımlar",
    settings: "Ayarlar",
    help: "Yardım",
    refresh: "Yenile",
    addStockCard: "Stok Kartı Ekle",
    addFromTemplate: "Şablondan Ekle",
    stockIn: "Stok Girişi",
    stockOut: "Stok Çıkışı",
    sale: "Satış",
    return: "İade",
    waste: "Fire / Zayi",
    countAdjustment: "Sayım Düzeltmesi",
    transfer: "Transfer",
    production: "Üretim Kullanımı",
    itemName: "Stok / Ürün Adı",
    itemNamePlaceholder: "Örn: Şampuan, Kıyma, Kola 2.5L",
    barcode: "Barkod / Kod",
    sku: "Stok Kodu",
    category: "Kategori",
    subCategory: "Alt Kategori",
    unit: "Birim",
    brand: "Marka",
    supplier: "Tedarikçi",
    currentStock: "Mevcut Stok",
    minStock: "Kritik Seviye",
    maxStock: "Maksimum Seviye",
    purchasePrice: "Alış Fiyatı",
    salePrice: "Satış Fiyatı",
    vatRate: "KDV %",
    currency: "Para Birimi",
    stockValue: "Stok Değeri",
    potentialSaleValue: "Satış Potansiyeli",
    profit: "Tahmini Kâr",
    profitRate: "Kâr Oranı",
    status: "Durum",
    active: "Aktif",
    passive: "Pasif",
    all: "Tümü",
    module: "Modül",
    qrMenu: "QR Menü",
    beauty: "Beauty",
    general: "Genel",
    restaurant: "Restoran",
    cafe: "Cafe",
    clinic: "Klinik",
    stockType: "Stok Tipi",
    rawMaterial: "Hammadde",
    saleProduct: "Satış Ürünü",
    consumable: "Sarf Malzeme",
    equipment: "Ekipman",
    serviceConsumable: "Hizmet Sarfı",
    packageProduct: "Paket Ürünü",
    searchPlaceholder: "Ürün adı, barkod, kategori, marka veya tedarikçi ara",
    categoryFilter: "Kategori filtresi",
    moduleFilter: "Modül filtresi",
    stockStatusFilter: "Stok durumu",
    clearFilters: "Filtreleri Temizle",
    lowStock: "Kritik",
    outOfStock: "Tükendi",
    normalStock: "Normal",
    overStock: "Fazla Stok",
    totalStockCards: "Toplam Stok Kartı",
    totalValue: "Toplam Değer",
    criticalCount: "Kritik Stok",
    outCount: "Tükenen",
    todayMovements: "Bugünkü Hareket",
    todaySales: "Bugünkü Satış",
    monthlySales: "Aylık Satış",
    stockHealth: "Stok Sağlığı",
    topValueItems: "En Değerli Stoklar",
    mostCriticalItems: "En Kritik Ürünler",
    recentMovements: "Son Hareketler",
    bestSelling: "En Çok Satanlar",
    slowMoving: "Az Hareket Görenler",
    noData: "Henüz kayıt yok.",
    noFilteredData: "Filtrelere uygun kayıt bulunamadı.",
    edit: "Düzenle",
    delete: "Sil",
    save: "Kaydet",
    cancel: "Vazgeç",
    close: "Kapat",
    confirm: "Onayla",
    deleteTitle: "Stok kartı silinsin mi?",
    deleteText:
      "Bu işlem stok kartını pasifleştirir. Geçmiş hareketler korunur.",
    stockSaved: "Stok kartı kaydedildi.",
    stockDeleted: "Stok kartı pasifleştirildi.",
    movementSaved: "Stok hareketi işlendi.",
    saleCompleted: "Satış tamamlandı ve stoktan düşüldü.",
    exported: "CSV dışa aktarıldı.",
    exportCsv: "CSV İndir",
    movementType: "Hareket Tipi",
    quantity: "Miktar",
    previousStock: "Önceki Stok",
    nextStock: "Sonraki Stok",
    unitPrice: "Birim Fiyat",
    totalAmount: "Toplam Tutar",
    description: "Açıklama",
    date: "Tarih",
    staff: "Personel",
    customer: "Müşteri",
    paymentMethod: "Ödeme Türü",
    cash: "Nakit",
    card: "Kart",
    bank: "Banka",
    online: "Online",
    mixed: "Karışık",
    addToBasket: "Sepete Ekle",
    basket: "Sepet",
    completeSale: "Satışı Tamamla",
    emptyBasket: "Sepet boş.",
    scanBarcode: "Barkod okut veya ürün ara",
    stockWillDrop: "Satış tamamlanınca stok otomatik düşer.",
    allowNegativeStock: "Negatif stok satışına izin ver",
    autoCashTransfer: "Satışta kasaya aktarım hazırla",
    openCashPanel: "Kasada Aç",
    autoCriticalWarning: "Kritik stok uyarılarını göster",
    defaultCurrency: "Varsayılan para birimi",
    savedLocal:
      "Backend hazır olmadığı için kayıtlar geçici olarak tarayıcıda saklandı.",
    backendReady:
      "Backend bağlandığında aynı ekran kurum_id ile gerçek veritabanına yazacak.",
    requiredName: "Ürün adı zorunludur.",
    requiredQty: "Miktar sıfırdan büyük olmalıdır.",
    insufficientStock: "Yeterli stok yok.",
    countSystemQty: "Sistem Stoğu",
    countRealQty: "Gerçek Sayım",
    countDiff: "Fark",
    applyCount: "Sayımı İşle",
    supplierName: "Tedarikçi Adı",
    phone: "Telefon",
    email: "E-posta",
    address: "Adres",
    taxNo: "Vergi No",
    authorized: "Yetkili",
    addSupplier: "Tedarikçi Ekle",
    addCategory: "Kategori Ekle",
    addUnit: "Birim Ekle",
    unitName: "Birim Adı",
    unitShort: "Kısa Ad",
    categoryName: "Kategori Adı",
    templateRestaurant: "Restoran / Kebap",
    templateCafe: "Cafe",
    templateBeauty: "Kuaför / Beauty",
    templateClinic: "Klinik / Medikal",
    templateMarket: "Market / Perakende",
    applyTemplate: "Şablonu Ekle",
    selected: "Seçili",
    selectAll: "Tümünü Seç",
    unselectAll: "Seçimi Temizle",
    itemsAdded: "Şablon ürünleri eklendi.",
    moduleCompatibility: "QR Menü & Beauty Uyumu",
    moduleCompatibilityText:
      "Stok kartları QR Menü ürünlerine, Beauty ürün satışına, Kasa & Tahsilat ve ileride reçete/maliyet sistemine bağlanacak şekilde hazırlanmıştır.",
    recipeReady: "Reçete / Maliyet Hazır",
    recipeReadyText:
      "Hammadde ve satış ürünü ayrımı sayesinde restoranlarda reçete maliyet yönetimine hazırdır.",
    staffCommissionReady: "Personel Prim Hazır",
    staffCommissionReadyText:
      "Satış hareketlerinde personel alanı bulunduğu için ileride hakediş ve prim raporuna bağlanabilir.",
    barcodeReady: "Barkod Hazır",
    barcodeReadyText:
      "Barkod alanı ve hızlı satış araması barkod cihazıyla çalışacak şekilde tasarlanmıştır.",
    pageRows: "Sayfa satırı",
    sortBy: "Sıralama",
    sortName: "Ada Göre",
    sortStockAsc: "Stok Azdan Çoğa",
    sortStockDesc: "Stok Çoktan Aza",
    sortValueDesc: "Değer Çoktan Aza",
    sortCritical: "Kritik Öncelikli",
    imageUrl: "Görsel URL",
    notes: "Not",
    purchase: "Alış",
    sales: "Satış",
    inventory: "Stok",
    cost: "Maliyet",
    margin: "Marj",
    product: "Ürün",
    products: "Ürünler",
    record: "Kayıt",
      records: "Kayıtlar",
    settingsSubtitle:
      "Stok modülünün çalışma kurallarını, satış entegrasyonunu ve ekran görünümünü kuruma göre yönetin.",
    stockRules: "Stok Güvenliği",
    stockRulesText:
      "Eksik stok, kritik seviye ve sayım disiplini gibi işletme güvenliğini etkileyen kurallar burada yönetilir.",
    salesAndFinance: "Satış & Finans",
    salesAndFinanceText:
      "Ürün satışlarının stoktan düşmesi ve kasaya aktarım mantığı bu bölümden hazırlanır.",
    screenPreferences: "Ekran Tercihleri",
    screenPreferencesText:
      "Liste yoğunluğu, pasif kayıt görünümü ve mobil kullanım tercihlerinin tamamı kurum ihtiyacına göre ayarlanır.",
    negativeStockText:
      "Kapalı olursa mevcut stoktan fazla satış veya çıkış yapılmaz. Restoran, cafe, güzellik salonu ve kliniklerde güvenli kullanım için önerilir.",
    criticalWarningText:
      "Açık olursa minimum seviyenin altına düşen ürünler kritik olarak görünür ve satın alma takibi kolaylaşır.",
    cashTransferText:
      "Açık olursa stoktan yapılan satışların Kasa & Tahsilat sistemine aktarılması için altyapı hazır çalışır.",
    currencyText:
      "TRY, EUR, CHF veya USD gibi kurumun ana para birimini yazabilirsiniz.",
    rowsPerPageText:
      "Yoğun stok kullanan işletmeler daha fazla satır seçebilir. Mobil kullanım için 12 satır önerilir.",
    passiveRecords: "Pasif kayıtları göster",
    passiveRecordsText:
      "Silinen veya pasifleştirilen stok kartlarını listede tekrar görmek için açılır.",
    mobileCardViewLabel: "Mobil kart görünümü",
    mobileCardViewText:
      "Telefon ve tablet kullanımında ürünleri tablo yerine daha okunabilir kart düzeninde göstermeye hazır ayardır.",
    settingsAutoSaveTitle: "Ayarlar otomatik korunur",
    settingsAutoSaveText:
      "Bu ekranda yaptığınız tercihler kurumun stok kullanım mantığını belirler.",
    helpHeroTitle: "Stok Modülü Kullanım Rehberi",
    helpHeroText:
      "Bu bölüm restoran, cafe, kuaför, güzellik salonu, klinik, market ve hizmet işletmeleri için ortak stok kullanım mantığını anlatır.",
    helpUseTitle: "Stok bölümü nasıl kullanılmalı?",
    helpUseText:
      "Önce stok kartları oluşturulur, sonra giriş-çıkış hareketleri düzenli işlenir. Satış ürünleri hızlı satıştan düşülür, hammadde ve sarf malzemeler sayım ile kontrol edilir. Kritik stoklar takip edilerek satın alma ihtiyacı zamanında görülür.",
    helpStartTitle: "1. Stok kartı oluştur",
    helpStartText:
      "Her ürün, hammadde veya sarf malzeme için ayrı stok kartı açılır. Ürün adı, kategori, birim, alış fiyatı, satış fiyatı, mevcut stok ve kritik seviye doğru girilmelidir.",
    helpSaleTitle: "2. Satış ve çıkışları işle",
    helpSaleText:
      "Satılan ürünler Ürün Satışı bölümünden sepete eklenerek tamamlanır. Kullanılan, bozulan veya fire olan ürünler Stok Çıkışı / Fire hareketiyle stoktan düşülür.",
    helpCountTitle: "3. Düzenli sayım yap",
    helpCountText:
      "Belirli aralıklarla gerçek stok sayılır. Sistem stoğu ile gerçek stok farklıysa Stok Sayımı bölümünden düzeltme yapılır.",
    helpReportTitle: "4. Raporları takip et",
    helpReportText:
      "Kritik ürünler, en değerli stoklar, hareket geçmişi ve satış performansı raporlardan izlenir. Böylece gereksiz alım ve stok bitmesi engellenir.",
    helpBusinessTitle: "Her kurum için kullanım örnekleri",
    helpRestaurantText:
      "Restoranlarda et, tavuk, sebze ve içecekler stokta takip edilir. Hammadde tüketimi ve fire kontrolü maliyeti düşürür.",
    helpCafeText:
      "Cafelerde kahve, süt, bardak, şurup ve unlu mamuller takip edilir. Kritik stok uyarısı günlük operasyonu rahatlatır.",
    helpBeautyText:
      "Kuaför ve güzellik salonlarında şampuan, boya, serum, lazer jel ve satılan bakım ürünleri düzenli izlenir.",
    helpClinicText:
      "Kliniklerde medikal sarf, eldiven, dezenfektan ve bakım ürünleri kontrollü şekilde takip edilir.",
    helpRetailText:
      "Market ve perakende işletmelerinde barkodlu ürünler hızlı satışa hazır kullanılır.",
    helpFaqTitle: "Sık Sorulan Sorular",
    helpFaqStockCardTitle: "Stok kartı ne zaman açılmalı?",
    helpFaqStockCardText:
      "Takip edilmesi gereken her ürün için stok kartı açılmalıdır. Satış ürünü, hammadde, sarf malzeme ve ekipman ayrı ayrı tutulursa raporlar daha doğru çalışır.",
    helpFaqCriticalTitle: "Kritik stok seviyesi ne işe yarar?",
    helpFaqCriticalText:
      "Minimum seviyeyi belirler. Ürün bu seviyeye düşünce sistem kritik olarak gösterir ve yeniden sipariş zamanı anlaşılır.",
    helpFaqCountingTitle: "Stok sayımı neden önemli?",
    helpFaqCountingText:
      "Fiili stok ile sistem stoğu her zaman aynı olmayabilir. Sayım düzeltmesi işletmenin gerçek stok değerini doğru gösterir.",
    helpFaqSaleTitle: "Ürün satışı stoktan düşer mi?",
    helpFaqSaleText:
      "Evet. Ürün Satışı bölümünden tamamlanan satış mevcut stoktan düşer. Ayar açıksa kasa aktarımı için de hazır yapı kullanılır.",
    helpFaqBarcodeTitle: "Barkod kullanabilir miyim?",
    helpFaqBarcodeText:
      "Evet. Barkod / Kod alanı doldurulan ürünler hızlı arama ve barkod okuyucu kullanımına hazırdır.",
    helpFaqCashTitle: "Kasa ile bağlantı nasıl çalışır?",
    helpFaqCashText:
      "Stoktan yapılan satışlar gelir kaydı mantığıyla Kasa & Tahsilat sistemine aktarılmaya hazırdır. Böylece stok ve ciro aynı iş akışında izlenir.",
    helpBestPracticeTitle: "Önerilen profesyonel kullanım",
    helpBestPracticeText:
      "Ürünleri önce kategori ve birimlerle düzenleyin, kritik seviyeleri gerçek ihtiyaca göre girin, satış ve fireleri günlük işleyin, haftalık veya aylık sayım yapın ve raporları düzenli kontrol edin.",
    mobileHint:
      "Mobilde kart görünümü aktif. Masaüstünde daha geniş filtre ve rapor alanları görünür.",
  },

  en: {
    pageTitle: "Corporate Stock Management",
    pageSubtitle:
      "Manage products, raw materials, consumables and sales stock for QR Menu, Beauty, restaurants, cafes and service businesses from one panel.",
    dashboard: "Dashboard",
    stockList: "Stock List",
    quickSale: "Product Sale",
    movements: "Stock Movements",
    critical: "Critical Stock",
    counting: "Stock Count",
    reports: "Reports",
    templates: "Templates",
    definitions: "Definitions",
    settings: "Settings",
    help: "Help",
    openCashPanel: "Open in Cash",
    refresh: "Refresh",
    addStockCard: "Add Stock Card",
    addFromTemplate: "Add from Template",
    stockIn: "Stock In",
    stockOut: "Stock Out",
    sale: "Sale",
    return: "Return",
    waste: "Waste",
    countAdjustment: "Count Adjustment",
    transfer: "Transfer",
    production: "Production Usage",
    itemName: "Stock / Product Name",
    itemNamePlaceholder: "Example: Shampoo, Minced Meat, Cola 2.5L",
    barcode: "Barcode / Code",
    sku: "SKU",
    category: "Category",
    subCategory: "Subcategory",
    unit: "Unit",
    brand: "Brand",
    supplier: "Supplier",
    currentStock: "Current Stock",
    minStock: "Critical Level",
    maxStock: "Max Level",
    purchasePrice: "Purchase Price",
    salePrice: "Sale Price",
    vatRate: "VAT %",
    currency: "Currency",
    stockValue: "Stock Value",
    potentialSaleValue: "Potential Sales",
    profit: "Estimated Profit",
    profitRate: "Profit Rate",
    status: "Status",
    active: "Active",
    passive: "Passive",
    all: "All",
    module: "Module",
    qrMenu: "QR Menu",
    beauty: "Beauty",
    general: "General",
    restaurant: "Restaurant",
    cafe: "Cafe",
    clinic: "Clinic",
    stockType: "Stock Type",
    rawMaterial: "Raw Material",
    saleProduct: "Sales Product",
    consumable: "Consumable",
    equipment: "Equipment",
    serviceConsumable: "Service Consumable",
    packageProduct: "Package Product",
    searchPlaceholder: "Search product name, barcode, category, brand or supplier",
    categoryFilter: "Category filter",
    moduleFilter: "Module filter",
    stockStatusFilter: "Stock status",
    clearFilters: "Clear Filters",
    lowStock: "Critical",
    outOfStock: "Out",
    normalStock: "Normal",
    overStock: "Overstock",
    totalStockCards: "Total Stock Cards",
    totalValue: "Total Value",
    criticalCount: "Critical Stock",
    outCount: "Out of Stock",
    todayMovements: "Today Movements",
    todaySales: "Today Sales",
    monthlySales: "Monthly Sales",
    stockHealth: "Stock Health",
    topValueItems: "Top Value Items",
    mostCriticalItems: "Most Critical Items",
    recentMovements: "Recent Movements",
    bestSelling: "Best Sellers",
    slowMoving: "Slow Moving",
    noData: "No records yet.",
    noFilteredData: "No records match your filters.",
    edit: "Edit",
    delete: "Delete",
    save: "Save",
    cancel: "Cancel",
    close: "Close",
    confirm: "Confirm",
    deleteTitle: "Delete stock card?",
    deleteText:
      "This action deactivates the stock card. Movement history remains protected.",
    stockSaved: "Stock card saved.",
    stockDeleted: "Stock card deactivated.",
    movementSaved: "Stock movement saved.",
    saleCompleted: "Sale completed and stock decreased.",
    exported: "CSV exported.",
    exportCsv: "Download CSV",
    movementType: "Movement Type",
    quantity: "Quantity",
    previousStock: "Previous Stock",
    nextStock: "Next Stock",
    unitPrice: "Unit Price",
    totalAmount: "Total Amount",
    description: "Description",
    date: "Date",
    staff: "Staff",
    customer: "Customer",
    paymentMethod: "Payment Method",
    cash: "Cash",
    card: "Card",
    bank: "Bank",
    online: "Online",
    mixed: "Mixed",
    addToBasket: "Add to Basket",
    basket: "Basket",
    completeSale: "Complete Sale",
    emptyBasket: "Basket is empty.",
    scanBarcode: "Scan barcode or search product",
    stockWillDrop: "Stock automatically decreases when sale is completed.",
    allowNegativeStock: "Allow negative stock sales",
    autoCashTransfer: "Prepare cash transfer on sale",
    autoCriticalWarning: "Show critical stock warnings",
    defaultCurrency: "Default currency",
    savedLocal:
      "Backend is not ready, so records were temporarily saved in the browser.",
    backendReady:
      "When backend is connected, the same screen will write to the real database with kurum_id.",
    requiredName: "Product name is required.",
    requiredQty: "Quantity must be greater than zero.",
    insufficientStock: "Insufficient stock.",
    countSystemQty: "System Qty",
    countRealQty: "Real Count",
    countDiff: "Difference",
    applyCount: "Apply Count",
    supplierName: "Supplier Name",
    phone: "Phone",
    email: "Email",
    address: "Address",
    taxNo: "Tax No",
    authorized: "Authorized",
    addSupplier: "Add Supplier",
    addCategory: "Add Category",
    addUnit: "Add Unit",
    unitName: "Unit Name",
    unitShort: "Short Name",
    categoryName: "Category Name",
    templateRestaurant: "Restaurant / Kebab",
    templateCafe: "Cafe",
    templateBeauty: "Hairdresser / Beauty",
    templateClinic: "Clinic / Medical",
    templateMarket: "Market / Retail",
    applyTemplate: "Apply Template",
    selected: "Selected",
    selectAll: "Select All",
    unselectAll: "Clear Selection",
    itemsAdded: "Template items added.",
    moduleCompatibility: "QR Menu & Beauty Compatibility",
    moduleCompatibilityText:
      "Stock cards are prepared to be connected to QR Menu products, Beauty product sales, Cash Register and future recipe/cost modules.",
    recipeReady: "Recipe / Cost Ready",
    recipeReadyText:
      "Raw material and sales product separation makes it ready for restaurant recipe cost management.",
    staffCommissionReady: "Staff Commission Ready",
    staffCommissionReadyText:
      "Because sales movements include staff field, it can be connected to commission reports later.",
    barcodeReady: "Barcode Ready",
    barcodeReadyText:
      "Barcode field and quick sale search are designed to work with barcode scanners.",
    pageRows: "Rows",
    sortBy: "Sort By",
    sortName: "By Name",
    sortStockAsc: "Stock Low to High",
    sortStockDesc: "Stock High to Low",
    sortValueDesc: "Value High to Low",
    sortCritical: "Critical First",
    imageUrl: "Image URL",
    notes: "Notes",
    purchase: "Purchase",
    sales: "Sales",
    inventory: "Inventory",
    cost: "Cost",
    margin: "Margin",
    product: "Product",
    products: "Products",
    record: "Record",
    records: "Records",
    settingsSubtitle:
      "Manage stock rules, sales integration and screen preferences according to each business.",
    stockRules: "Stock Safety",
    stockRulesText:
      "Rules that affect operational safety such as negative stock, critical levels and stock count discipline are managed here.",
    salesAndFinance: "Sales & Finance",
    salesAndFinanceText:
      "Prepare how product sales decrease stock and how cash transfer logic works.",
    screenPreferences: "Screen Preferences",
    screenPreferencesText:
      "Manage list density, passive records and mobile usage preferences according to business needs.",
    negativeStockText:
      "When disabled, sales or exits above current stock are blocked. Recommended for restaurants, cafes, beauty salons and clinics.",
    criticalWarningText:
      "When enabled, products below the minimum level are shown as critical and purchasing follow-up becomes easier.",
    cashTransferText:
      "When enabled, stock sales are prepared for Cash & Collection transfer.",
    currencyText:
      "You can enter the main currency of the business such as TRY, EUR, CHF or USD.",
    rowsPerPageText:
      "Businesses with heavy stock usage can select more rows. 12 rows are recommended for mobile use.",
    passiveRecords: "Show passive records",
    passiveRecordsText:
      "Enable this to display deleted or deactivated stock cards again in the list.",
    mobileCardViewLabel: "Mobile card view",
    mobileCardViewText:
      "Prepared to show products in a readable card layout instead of tables on phones and tablets.",
    settingsAutoSaveTitle: "Settings are protected automatically",
    settingsAutoSaveText:
      "The preferences on this screen define how the business uses the stock module.",
    helpHeroTitle: "Stock Module User Guide",
    helpHeroText:
      "This guide explains the shared stock usage logic for restaurants, cafes, hairdressers, beauty salons, clinics, markets and service businesses.",
    helpUseTitle: "How should the stock section be used?",
    helpUseText:
      "First create stock cards, then process stock in/out movements regularly. Sales products are decreased through quick sale, while raw materials and consumables are controlled by stock counting. Critical stock helps you see purchase needs on time.",
    helpStartTitle: "1. Create a stock card",
    helpStartText:
      "Create a separate stock card for every product, raw material or consumable. Product name, category, unit, purchase price, sale price, current stock and critical level should be entered correctly.",
    helpSaleTitle: "2. Process sales and exits",
    helpSaleText:
      "Sold products are added to the basket and completed from Product Sale. Used, damaged or wasted products are decreased through Stock Out / Waste movement.",
    helpCountTitle: "3. Count stock regularly",
    helpCountText:
      "Count real stock periodically. If system stock and real stock are different, apply a correction from Stock Count.",
    helpReportTitle: "4. Follow reports",
    helpReportText:
      "Critical items, top value items, movement history and sales performance are monitored from reports. This helps prevent unnecessary purchases and stockouts.",
    helpBusinessTitle: "Usage examples for every business",
    helpRestaurantText:
      "Restaurants can track meat, chicken, vegetables and beverages. Raw material and waste control reduces cost.",
    helpCafeText:
      "Cafes can track coffee, milk, cups, syrups and bakery items. Critical warnings support daily operations.",
    helpBeautyText:
      "Hairdressers and beauty salons can track shampoo, dye, serum, laser gel and retail care products.",
    helpClinicText:
      "Clinics can track medical consumables, gloves, disinfectants and care products in a controlled way.",
    helpRetailText:
      "Markets and retail businesses can use barcode-ready products for quick sales.",
    helpFaqTitle: "Frequently Asked Questions",
    helpFaqStockCardTitle: "When should a stock card be created?",
    helpFaqStockCardText:
      "A stock card should be created for every item that needs tracking. Keeping sales products, raw materials, consumables and equipment separate makes reports more accurate.",
    helpFaqCriticalTitle: "What is the critical stock level?",
    helpFaqCriticalText:
      "It defines the minimum level. When the product reaches this level, the system marks it as critical and reorder timing becomes clear.",
    helpFaqCountingTitle: "Why is stock counting important?",
    helpFaqCountingText:
      "Real stock and system stock may not always match. Count adjustment shows the real stock value of the business correctly.",
    helpFaqSaleTitle: "Does product sale decrease stock?",
    helpFaqSaleText:
      "Yes. Sales completed from Product Sale decrease current stock. If the setting is enabled, cash transfer preparation is also used.",
    helpFaqBarcodeTitle: "Can I use barcode?",
    helpFaqBarcodeText:
      "Yes. Products with Barcode / Code filled in are ready for quick search and barcode scanner usage.",
    helpFaqCashTitle: "How does the cash connection work?",
    helpFaqCashText:
      "Sales made from stock are prepared to be transferred to Cash & Collection as income records. This allows stock and revenue to be tracked in the same workflow.",
    helpBestPracticeTitle: "Recommended professional usage",
    helpBestPracticeText:
      "Organize products with categories and units first, enter critical levels according to real needs, process sales and waste daily, count weekly or monthly and review reports regularly.",
        mobileHint:
      "Card view is active on mobile. Wider filters and reports appear on desktop.",
  },

  de: {
    pageTitle: "Professionelle Lagerverwaltung",
    pageSubtitle:
      "Produkte, Rohstoffe, Verbrauchsmaterialien und Verkaufsbestände für QR-Menü, Beauty, Restaurants, Cafes und Dienstleister zentral verwalten.",
    dashboard: "Startseite",
    stockList: "Lagerliste",
    quickSale: "Produktverkauf",
    movements: "Lagerbewegungen",
    critical: "Kritischer Bestand",
    counting: "Inventur",
    reports: "Berichte",
    templates: "Vorlagen",
    definitions: "Definitionen",
    settings: "Einstellungen",
    help: "Hilfe",
    refresh: "Aktualisieren",
    addStockCard: "Lagerkarte hinzufügen",
    addFromTemplate: "Aus Vorlage hinzufügen",
    stockIn: "Wareneingang",
    stockOut: "Warenausgang",
    sale: "Verkauf",
    return: "Retoure",
    waste: "Schwund",
    countAdjustment: "Inventurkorrektur",
    transfer: "Transfer",
    production: "Produktionsverbrauch",
    itemName: "Lager- / Produktname",
    itemNamePlaceholder: "Beispiel: Shampoo, Hackfleisch, Cola 2.5L",
    barcode: "Barcode / Code",
    sku: "Artikelnummer",
    category: "Kategorie",
    subCategory: "Unterkategorie",
    openCashPanel: "In Kasse öffnen",
    unit: "Einheit",
    brand: "Marke",
    supplier: "Lieferant",
    currentStock: "Aktueller Bestand",
    minStock: "Kritischer Wert",
    maxStock: "Max. Bestand",
    purchasePrice: "Einkaufspreis",
    salePrice: "Verkaufspreis",
    vatRate: "MwSt. %",
    currency: "Währung",
    stockValue: "Lagerwert",
    potentialSaleValue: "Verkaufspotenzial",
    profit: "Geschätzter Gewinn",
    profitRate: "Gewinnrate",
    status: "Status",
    active: "Aktiv",
    passive: "Passiv",
    all: "Alle",
    module: "Modul",
    qrMenu: "QR-Menü",
    beauty: "Beauty",
    general: "Allgemein",
    restaurant: "Restaurant",
    cafe: "Cafe",
    clinic: "Klinik",
    stockType: "Lagertyp",
    rawMaterial: "Rohstoff",
    saleProduct: "Verkaufsprodukt",
    consumable: "Verbrauchsmaterial",
    equipment: "Ausrüstung",
    serviceConsumable: "Service-Verbrauch",
    packageProduct: "Paketprodukt",
    searchPlaceholder: "Produktname, Barcode, Kategorie, Marke oder Lieferant suchen",
    categoryFilter: "Kategorie-Filter",
    moduleFilter: "Modul-Filter",
    stockStatusFilter: "Bestandsstatus",
    clearFilters: "Filter löschen",
    lowStock: "Kritisch",
    outOfStock: "Leer",
    normalStock: "Normal",
    overStock: "Überbestand",
    totalStockCards: "Lagerkarten",
    totalValue: "Gesamtwert",
    criticalCount: "Kritisch",
    outCount: "Leer",
    todayMovements: "Heutige Bewegungen",
    todaySales: "Heutige Verkäufe",
    monthlySales: "Monatsverkäufe",
    stockHealth: "Lagergesundheit",
    topValueItems: "Wertvollste Bestände",
    mostCriticalItems: "Kritischste Produkte",
    recentMovements: "Letzte Bewegungen",
    bestSelling: "Bestseller",
    slowMoving: "Langsam drehend",
    noData: "Noch keine Einträge.",
    noFilteredData: "Keine passenden Einträge gefunden.",
    edit: "Bearbeiten",
    delete: "Löschen",
    save: "Speichern",
    cancel: "Abbrechen",
    close: "Schließen",
    confirm: "Bestätigen",
    deleteTitle: "Lagerkarte löschen?",
    deleteText:
      "Diese Aktion deaktiviert die Lagerkarte. Bewegungsverlauf bleibt erhalten.",
    stockSaved: "Lagerkarte gespeichert.",
    stockDeleted: "Lagerkarte deaktiviert.",
    movementSaved: "Lagerbewegung gespeichert.",
    saleCompleted: "Verkauf abgeschlossen und Bestand reduziert.",
    exported: "CSV exportiert.",
    exportCsv: "CSV herunterladen",
    movementType: "Bewegungstyp",
    quantity: "Menge",
    previousStock: "Vorheriger Bestand",
    nextStock: "Neuer Bestand",
    unitPrice: "Einzelpreis",
    totalAmount: "Gesamtbetrag",
    description: "Beschreibung",
    date: "Datum",
    staff: "Personal",
    customer: "Kunde",
    paymentMethod: "Zahlungsart",
    cash: "Bar",
    card: "Karte",
    bank: "Bank",
    online: "Online",
    mixed: "Gemischt",
    addToBasket: "In Warenkorb",
    basket: "Warenkorb",
    completeSale: "Verkauf abschließen",
    emptyBasket: "Warenkorb ist leer.",
    scanBarcode: "Barcode scannen oder Produkt suchen",
    stockWillDrop: "Beim Abschluss wird der Bestand automatisch reduziert.",
    allowNegativeStock: "Negative Bestände erlauben",
    autoCashTransfer: "Kassenübertragung bei Verkauf vorbereiten",
    autoCriticalWarning: "Kritische Lagerwarnungen anzeigen",
    defaultCurrency: "Standardwährung",
    savedLocal:
      "Backend ist noch nicht bereit, daher wurden Datensätze vorübergehend im Browser gespeichert.",
    backendReady:
      "Nach Backend-Anbindung schreibt diese Maske mit kurum_id in die echte Datenbank.",
    requiredName: "Produktname ist erforderlich.",
    requiredQty: "Menge muss größer als null sein.",
    insufficientStock: "Nicht genügend Bestand.",
    countSystemQty: "Systembestand",
    countRealQty: "Echter Bestand",
    countDiff: "Differenz",
    applyCount: "Inventur buchen",
    supplierName: "Lieferantenname",
    phone: "Telefon",
    email: "E-Mail",
    address: "Adresse",
    taxNo: "Steuernummer",
    authorized: "Ansprechpartner",
    addSupplier: "Lieferant hinzufügen",
    addCategory: "Kategorie hinzufügen",
    addUnit: "Einheit hinzufügen",
    unitName: "Einheitsname",
    unitShort: "Kurzname",
    categoryName: "Kategoriename",
    templateRestaurant: "Restaurant / Kebab",
    templateCafe: "Cafe",
    templateBeauty: "Friseur / Beauty",
    templateClinic: "Klinik / Medizin",
    templateMarket: "Markt / Einzelhandel",
    applyTemplate: "Vorlage anwenden",
    selected: "Ausgewählt",
    selectAll: "Alle auswählen",
    unselectAll: "Auswahl löschen",
    itemsAdded: "Vorlagenartikel hinzugefügt.",
    moduleCompatibility: "QR-Menü & Beauty kompatibel",
    moduleCompatibilityText:
      "Lagerkarten sind für QR-Menü-Produkte, Beauty-Verkäufe, Kasse und spätere Rezept-/Kostenmodule vorbereitet.",
    recipeReady: "Rezept / Kosten bereit",
    recipeReadyText:
      "Die Trennung zwischen Rohstoff und Verkaufsprodukt ist für Rezeptkosten im Restaurant vorbereitet.",
    staffCommissionReady: "Provision bereit",
    staffCommissionReadyText:
      "Da Verkaufsbewegungen ein Personalfeld enthalten, kann später ein Provisionsbericht angebunden werden.",
    barcodeReady: "Barcode bereit",
    barcodeReadyText:
      "Barcodefeld und Schnellsuche sind für Barcode-Scanner vorbereitet.",
    pageRows: "Zeilen",
    sortBy: "Sortierung",
    sortName: "Nach Name",
    sortStockAsc: "Bestand aufsteigend",
    sortStockDesc: "Bestand absteigend",
    sortValueDesc: "Wert absteigend",
    sortCritical: "Kritisch zuerst",
    imageUrl: "Bild-URL",
    notes: "Notizen",
    purchase: "Einkauf",
    sales: "Verkauf",
    inventory: "Lager",
    cost: "Kosten",
    margin: "Marge",
    product: "Produkt",
    products: "Produkte",
    record: "Datensatz",
    records: "Datensätze",
    settingsSubtitle:
      "Lagerregeln, Verkaufsintegration und Bildschirmansicht je nach Betrieb verwalten.",
    stockRules: "Lagersicherheit",
    stockRulesText:
      "Regeln für negative Bestände, kritische Werte und Inventurdisziplin werden hier verwaltet.",
    salesAndFinance: "Verkauf & Finanzen",
    salesAndFinanceText:
      "Hier wird vorbereitet, wie Verkäufe den Bestand reduzieren und wie die Kassenlogik funktioniert.",
    screenPreferences: "Anzeigeeinstellungen",
    screenPreferencesText:
      "Listendichte, passive Datensätze und mobile Nutzung werden nach Betriebsbedarf eingestellt.",
    negativeStockText:
      "Wenn deaktiviert, werden Verkäufe oder Ausgänge über dem aktuellen Bestand blockiert. Empfohlen für Restaurants, Cafes, Beauty-Salons und Kliniken.",
    criticalWarningText:
      "Wenn aktiviert, werden Produkte unter dem Mindestbestand als kritisch angezeigt und der Einkauf wird einfacher geplant.",
    cashTransferText:
      "Wenn aktiviert, werden Lagerverkäufe für die Übertragung an Kasse & Zahlung vorbereitet.",
    currencyText:
      "Sie können die Hauptwährung des Betriebs eingeben, z. B. TRY, EUR, CHF oder USD.",
    rowsPerPageText:
      "Betriebe mit vielen Lagerartikeln können mehr Zeilen auswählen. Für mobile Nutzung werden 12 Zeilen empfohlen.",
    passiveRecords: "Passive Datensätze anzeigen",
    passiveRecordsText:
      "Aktivieren, um gelöschte oder deaktivierte Lagerkarten wieder in der Liste zu sehen.",
    mobileCardViewLabel: "Mobile Kartenansicht",
    mobileCardViewText:
      "Bereit, Produkte auf Telefonen und Tablets statt als Tabelle in einer lesbaren Kartenansicht zu zeigen.",
    settingsAutoSaveTitle: "Einstellungen werden automatisch geschützt",
    settingsAutoSaveText:
      "Die Einstellungen auf dieser Seite bestimmen, wie der Betrieb das Lagermodul nutzt.",
    helpHeroTitle: "Anleitung für das Lagermodul",
    helpHeroText:
      "Diese Anleitung erklärt die gemeinsame Lagerlogik für Restaurants, Cafes, Friseure, Beauty-Salons, Kliniken, Märkte und Dienstleister.",
    helpUseTitle: "Wie sollte der Lagerbereich genutzt werden?",
    helpUseText:
      "Zuerst werden Lagerkarten erstellt, danach werden Ein- und Ausgänge regelmäßig gebucht. Verkaufsprodukte werden über den Schnellverkauf reduziert, Rohstoffe und Verbrauchsmaterialien werden durch Inventur kontrolliert. Kritische Bestände zeigen den Einkaufsbedarf rechtzeitig.",
    helpStartTitle: "1. Lagerkarte erstellen",
    helpStartText:
      "Für jedes Produkt, jeden Rohstoff oder jedes Verbrauchsmaterial wird eine eigene Lagerkarte erstellt. Produktname, Kategorie, Einheit, Einkaufspreis, Verkaufspreis, aktueller Bestand und kritischer Wert sollten korrekt eingetragen werden.",
    helpSaleTitle: "2. Verkäufe und Ausgänge buchen",
    helpSaleText:
      "Verkaufte Produkte werden im Produktverkauf in den Warenkorb gelegt und abgeschlossen. Verbrauchte, beschädigte oder verschwendete Produkte werden über Warenausgang / Schwund reduziert.",
    helpCountTitle: "3. Regelmäßig Inventur machen",
    helpCountText:
      "Der echte Bestand wird regelmäßig gezählt. Wenn Systembestand und echter Bestand unterschiedlich sind, wird die Korrektur über Inventur gebucht.",
    helpReportTitle: "4. Berichte verfolgen",
    helpReportText:
      "Kritische Produkte, wertvollste Bestände, Bewegungsverlauf und Verkaufsleistung werden über Berichte kontrolliert. So werden unnötige Einkäufe und leere Bestände vermieden.",
    helpBusinessTitle: "Nutzungsbeispiele für jeden Betrieb",
    helpRestaurantText:
      "Restaurants können Fleisch, Hähnchen, Gemüse und Getränke verfolgen. Rohstoff- und Schwundkontrolle senkt Kosten.",
    helpCafeText:
      "Cafes können Kaffee, Milch, Becher, Sirup und Backwaren verfolgen. Kritische Warnungen unterstützen den Tagesbetrieb.",
    helpBeautyText:
      "Friseure und Beauty-Salons können Shampoo, Farbe, Serum, Lasergel und Verkaufsprodukte verfolgen.",
    helpClinicText:
      "Kliniken können medizinische Verbrauchsmaterialien, Handschuhe, Desinfektion und Pflegeprodukte kontrolliert verfolgen.",
    helpRetailText:
      "Märkte und Einzelhandel können barcodefähige Produkte für den Schnellverkauf nutzen.",
    helpFaqTitle: "Häufige Fragen",
    helpFaqStockCardTitle: "Wann sollte eine Lagerkarte erstellt werden?",
    helpFaqStockCardText:
      "Für jeden Artikel, der verfolgt werden soll, sollte eine Lagerkarte erstellt werden. Verkaufsprodukte, Rohstoffe, Verbrauchsmaterialien und Ausrüstung getrennt zu halten, macht Berichte genauer.",
    helpFaqCriticalTitle: "Was bedeutet kritischer Bestand?",
    helpFaqCriticalText:
      "Er definiert den Mindestbestand. Wenn das Produkt diesen Wert erreicht, zeigt das System es als kritisch an und die Nachbestellung wird klar.",
    helpFaqCountingTitle: "Warum ist Inventur wichtig?",
    helpFaqCountingText:
      "Echter Bestand und Systembestand stimmen nicht immer überein. Die Inventurkorrektur zeigt den echten Lagerwert des Betriebs korrekt.",
    helpFaqSaleTitle: "Reduziert ein Verkauf den Bestand?",
    helpFaqSaleText:
      "Ja. Verkäufe aus dem Produktverkauf reduzieren den aktuellen Bestand. Wenn die Einstellung aktiv ist, wird auch die Kassenübertragung vorbereitet.",
    helpFaqBarcodeTitle: "Kann ich Barcode verwenden?",
    helpFaqBarcodeText:
      "Ja. Produkte mit ausgefülltem Barcode / Code sind für Schnellsuche und Barcode-Scanner vorbereitet.",
    helpFaqCashTitle: "Wie funktioniert die Kassenverbindung?",
    helpFaqCashText:
      "Verkäufe aus dem Lager werden als Einnahmen für Kasse & Zahlung vorbereitet. So können Bestand und Umsatz im gleichen Ablauf verfolgt werden.",
    helpBestPracticeTitle: "Empfohlene professionelle Nutzung",
    helpBestPracticeText:
      "Produkte zuerst mit Kategorien und Einheiten ordnen, kritische Werte nach echtem Bedarf eintragen, Verkäufe und Schwund täglich buchen, wöchentlich oder monatlich zählen und Berichte regelmäßig prüfen.",
    mobileHint:
      "Auf Mobilgeräten ist die Kartenansicht aktiv. Auf Desktop erscheinen breitere Filter und Berichte.",
  },
};

const DEFAULT_UNITS = [

  {
    id: "unit-adet",
    name: { tr: "Adet", en: "Piece", de: "Stück" },
    short: "adet",
    decimal: false,
    active: true,
  },
  {
    id: "unit-gram",
    name: { tr: "Gram", en: "Gram", de: "Gramm" },
    short: "gr",
    decimal: true,
    active: true,
  },
  {
    id: "unit-kg",
    name: { tr: "Kilogram", en: "Kilogram", de: "Kilogramm" },
    short: "kg",
    decimal: true,
    active: true,
  },
  {
    id: "unit-ml",
    name: { tr: "Mililitre", en: "Milliliter", de: "Milliliter" },
    short: "ml",
    decimal: true,
    active: true,
  },
  {
    id: "unit-litre",
    name: { tr: "Litre", en: "Liter", de: "Liter" },
    short: "lt",
    decimal: true,
    active: true,
  },
  {
    id: "unit-paket",
    name: { tr: "Paket", en: "Pack", de: "Paket" },
    short: "paket",
    decimal: false,
    active: true,
  },
  {
    id: "unit-kutu",
    name: { tr: "Kutu", en: "Box", de: "Box" },
    short: "kutu",
    decimal: false,
    active: true,
  },
];

const DEFAULT_CATEGORIES = [
  {
    id: "cat-hammadde",
    name: { tr: "Hammadde", en: "Raw Material", de: "Rohstoff" },
    module: "restaurant",
    color: "#1565C0",
    active: true,
  },
  {
    id: "cat-icecek",
    name: { tr: "İçecek", en: "Beverage", de: "Getränk" },
    module: "qr_menu",
    color: "#06B6D4",
    active: true,
  },
  {
    id: "cat-satis-urunu",
    name: { tr: "Satış Ürünü", en: "Sales Product", de: "Verkaufsprodukt" },
    module: "general",
    color: "#F58220",
    active: true,
  },
  {
    id: "cat-kuafor",
    name: { tr: "Kuaför Ürünleri", en: "Hair Products", de: "Friseurprodukte" },
    module: "beauty",
    color: "#EC4899",
    active: true,
  },
  {
    id: "cat-sarf",
    name: { tr: "Sarf Malzeme", en: "Consumable", de: "Verbrauchsmaterial" },
    module: "general",
    color: "#64748B",
    active: true,
  },
  {
    id: "cat-medikal",
    name: { tr: "Medikal", en: "Medical", de: "Medizinisch" },
    module: "clinic",
    color: "#10B981",
    active: true,
  },
  {
    id: "cat-temizlik",
    name: { tr: "Temizlik", en: "Cleaning", de: "Reinigung" },
    module: "general",
    color: "#8B5CF6",
    active: true,
  },
];

const DEFAULT_SUPPLIERS = [
  {
    id: "sup-default",
    name: "Genel Tedarikçi",
    authorized: "",
    phone: "",
    email: "",
    address: "",
    taxNo: "",
    active: true,
    createdAt: new Date().toISOString(),
  },
];

const BUSINESS_PRESETS = [
  {
    id: "restaurant",
    icon: "restaurant",
    labelKey: "templateRestaurant",
    color: "#1565C0",
    description:
      "Kebap, restoran ve paket servis işletmeleri için hammadde + içecek + satış ürünü.",
  },
  {
    id: "cafe",
    icon: "cafe",
    labelKey: "templateCafe",
    color: "#F58220",
    description:
      "Cafe, pastane ve self servis işletmeleri için kahve, süt, içecek, unlu mamul.",
  },
  {
    id: "beauty",
    icon: "beauty",
    labelKey: "templateBeauty",
    color: "#EC4899",
    description:
      "Kuaför, güzellik salonu ve lazer merkezleri için ürün satışı + sarf malzeme.",
  },
  {
    id: "clinic",
    icon: "clinic",
    labelKey: "templateClinic",
    color: "#10B981",
    description:
      "Klinik, estetik ve medikal işletmeler için sarf, medikal ürün ve cihaz malzemesi.",
  },
  {
    id: "market",
    icon: "market",
    labelKey: "templateMarket",
    color: "#0B4D8B",
    description:
      "Perakende satış yapan küçük işletmeler için barkodlu hızlı ürün satışı.",
  },
];

const TEMPLATE_ITEMS = {
  restaurant: [
    ["Kıyma", "cat-hammadde", "unit-kg", "raw_material", 5, 2, 250, 0, "restaurant"],
    ["Tavuk", "cat-hammadde", "unit-kg", "raw_material", 8, 3, 120, 0, "restaurant"],
    ["Domates", "cat-hammadde", "unit-kg", "raw_material", 10, 3, 60, 0, "restaurant"],
    ["Soğan", "cat-hammadde", "unit-kg", "raw_material", 10, 3, 40, 0, "restaurant"],
    ["Marul", "cat-hammadde", "unit-adet", "raw_material", 20, 5, 20, 0, "restaurant"],
    ["Hamburger Ekmeği", "cat-hammadde", "unit-adet", "raw_material", 50, 10, 15, 0, "restaurant"],
    ["Lavaş", "cat-hammadde", "unit-adet", "raw_material", 100, 20, 6, 0, "restaurant"],
    ["Ayran", "cat-icecek", "unit-adet", "sale_product", 80, 15, 12, 30, "qr_menu"],
    ["Kola 2.5L", "cat-icecek", "unit-adet", "sale_product", 30, 6, 45, 80, "qr_menu"],
    ["Su 0.5L", "cat-icecek", "unit-adet", "sale_product", 120, 24, 4, 10, "qr_menu"],
  ],
  cafe: [
    ["Espresso", "cat-hammadde", "unit-kg", "raw_material", 2, 0.5, 400, 0, "cafe"],
    ["Türk Kahvesi", "cat-hammadde", "unit-kg", "raw_material", 3, 0.5, 600, 0, "cafe"],
    ["Süt", "cat-hammadde", "unit-litre", "raw_material", 20, 5, 50, 0, "cafe"],
    ["Badem Sütü", "cat-hammadde", "unit-litre", "raw_material", 8, 2, 150, 0, "cafe"],
    ["Yulaf Sütü", "cat-hammadde", "unit-litre", "raw_material", 8, 2, 120, 0, "cafe"],
    ["Karton Bardak", "cat-sarf", "unit-adet", "consumable", 500, 100, 1.2, 0, "cafe"],
    ["Pipet", "cat-sarf", "unit-adet", "consumable", 500, 100, 0.3, 0, "cafe"],
    ["Kruvasan", "cat-satis-urunu", "unit-adet", "sale_product", 40, 8, 25, 75, "qr_menu"],
  ],
  beauty: [
    ["Şampuan", "cat-kuafor", "unit-adet", "sale_product", 20, 5, 120, 280, "beauty"],
    ["Saç Bakım Serumu", "cat-kuafor", "unit-adet", "sale_product", 15, 4, 180, 420, "beauty"],
    ["Saç Boyası", "cat-kuafor", "unit-adet", "consumable", 60, 15, 90, 0, "beauty"],
    ["Oksidan", "cat-kuafor", "unit-litre", "consumable", 8, 2, 85, 0, "beauty"],
    ["Lazer Jel", "cat-medikal", "unit-litre", "service_consumable", 10, 3, 70, 0, "beauty"],
    ["Eldiven", "cat-sarf", "unit-kutu", "consumable", 10, 2, 140, 0, "beauty"],
    ["Maske", "cat-sarf", "unit-kutu", "consumable", 5, 1, 90, 0, "beauty"],
    ["Cilt Bakım Maskesi", "cat-kuafor", "unit-adet", "sale_product", 25, 5, 60, 180, "beauty"],
  ],
  clinic: [
    ["Medikal Eldiven", "cat-medikal", "unit-kutu", "consumable", 12, 3, 160, 0, "clinic"],
    ["Dezenfektan", "cat-medikal", "unit-litre", "consumable", 10, 2, 75, 0, "clinic"],
    ["Maske", "cat-medikal", "unit-kutu", "consumable", 10, 2, 90, 0, "clinic"],
    ["Kağıt Havlu", "cat-sarf", "unit-paket", "consumable", 20, 5, 45, 0, "clinic"],
    ["Cilt Bakım Ampulü", "cat-medikal", "unit-adet", "sale_product", 30, 6, 110, 250, "clinic"],
  ],
  market: [
    ["Su 1.5L", "cat-icecek", "unit-adet", "sale_product", 100, 20, 8, 20, "general"],
    ["Kola 1L", "cat-icecek", "unit-adet", "sale_product", 50, 10, 22, 45, "general"],
    ["Çikolata", "cat-satis-urunu", "unit-adet", "sale_product", 80, 15, 12, 25, "general"],
    ["Cips", "cat-satis-urunu", "unit-adet", "sale_product", 60, 12, 20, 40, "general"],
    ["Sakız", "cat-satis-urunu", "unit-adet", "sale_product", 100, 20, 3, 8, "general"],
  ],
};

const MODULE_OPTIONS = [
  {
    value: "general",
    icon: "store",
    label: { tr: "Genel", en: "General", de: "Allgemein" },
  },
  {
    value: "qr_menu",
    icon: "qr",
    label: { tr: "QR Menü", en: "QR Menu", de: "QR-Menü" },
  },
  {
    value: "beauty",
    icon: "beauty",
    label: { tr: "Beauty", en: "Beauty", de: "Beauty" },
  },
  {
    value: "restaurant",
    icon: "restaurant",
    label: { tr: "Restoran", en: "Restaurant", de: "Restaurant" },
  },
  {
    value: "cafe",
    icon: "cafe",
    label: { tr: "Cafe", en: "Cafe", de: "Cafe" },
  },
  {
    value: "clinic",
    icon: "clinic",
    label: { tr: "Klinik", en: "Clinic", de: "Klinik" },
  },
];

const STOCK_TYPE_OPTIONS = [
  { value: "raw_material", labelKey: "rawMaterial", color: "#1565C0" },
  { value: "sale_product", labelKey: "saleProduct", color: "#F58220" },
  { value: "consumable", labelKey: "consumable", color: "#64748B" },
  { value: "equipment", labelKey: "equipment", color: "#8B5CF6" },
  { value: "service_consumable", labelKey: "serviceConsumable", color: "#10B981" },
  { value: "package_product", labelKey: "packageProduct", color: "#EC4899" },
];

const MOVEMENT_TYPES = [
  { value: "in", labelKey: "stockIn", icon: "add", effect: 1, color: "#10B981" },
  { value: "out", labelKey: "stockOut", icon: "remove", effect: -1, color: "#EF4444" },
  { value: "sale", labelKey: "sale", icon: "sale", effect: -1, color: "#F58220" },
  { value: "return", labelKey: "return", icon: "return", effect: 1, color: "#06B6D4" },
  { value: "waste", labelKey: "waste", icon: "fire", effect: -1, color: "#F59E0B" },
  {
    value: "count_adjustment",
    labelKey: "countAdjustment",
    icon: "count",
    effect: 0,
    color: "#8B5CF6",
  },
  { value: "transfer", labelKey: "transfer", icon: "transfer", effect: -1, color: "#0B4D8B" },
  { value: "production", labelKey: "production", icon: "production", effect: -1, color: "#64748B" },
];

const PAYMENT_METHODS = [
  { value: "cash", labelKey: "cash" },
  { value: "card", labelKey: "card" },
  { value: "bank", labelKey: "bank" },
  { value: "online", labelKey: "online" },
  { value: "mixed", labelKey: "mixed" },
];

const SORT_OPTIONS = [
  { value: "name", labelKey: "sortName" },
  { value: "stock_asc", labelKey: "sortStockAsc" },
  { value: "stock_desc", labelKey: "sortStockDesc" },
  { value: "value_desc", labelKey: "sortValueDesc" },
  { value: "critical", labelKey: "sortCritical" },
];

const STORAGE_KEYS = {
  items: "mesyat_stock_items_v2",
  movements: "mesyat_stock_movements_v2",
  categories: "mesyat_stock_categories_v2",
  units: "mesyat_stock_units_v2",
  suppliers: "mesyat_stock_suppliers_v2",
  settings: "mesyat_stock_settings_v2",
};

const defaultSettings = {
  allowNegativeStock: false,
  autoCashTransfer: true,
  autoCriticalWarning: true,
  defaultCurrency: "TRY",
  rowsPerPage: 12,
  showPassive: false,
  mobileCardView: true,
  moduleMode: "all",
};

const floatIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(18px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulseSoft = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(245,130,32,0.34);
  }
  70% {
    box-shadow: 0 0 0 12px rgba(245,130,32,0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(245,130,32,0);
  }
`;

const shine = keyframes`
  0% {
    background-position: -180% 0;
  }
  100% {
    background-position: 180% 0;
  }
`;

function getPanelLang() {
  const raw =
    localStorage.getItem("panel_dili") ||
    localStorage.getItem("app_lang") ||
    localStorage.getItem("lang") ||
    "tr";

  const lang = String(raw).slice(0, 2).toLowerCase();

  return ["tr", "en", "de"].includes(lang) ? lang : "tr";
}

function getLocale(lang) {
  if (lang === "en") return "en-US";
  if (lang === "de") return "de-DE";
  return "tr-TR";
}

function getTextValue(value, lang = "tr") {
  if (!value) return "";
  if (typeof value === "string") return value;
  return value[lang] || value.tr || value.en || value.de || "";
}

function safeLower(value) {
  return String(value || "").toLocaleLowerCase("tr-TR");
}

function uid(prefix = "id") {
  return `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function getToken() {
  return localStorage.getItem("token") || localStorage.getItem("auth_token") || "";
}

function normalizeCurrency(value) {
  const raw = String(value || "TRY").toUpperCase();
  if (raw === "TL") return "TRY";
  return raw;
}

function parseNumber(value) {
  if (value === null || value === undefined || value === "") return 0;

  if (typeof value === "number") {
    return Number.isFinite(value) ? value : 0;
  }

  const normalized = String(value).replace(/\./g, "").replace(",", ".");
  const n = Number(normalized);

  return Number.isFinite(n) ? n : 0;
}

function numberText(value, lang = "tr", digits = 2) {
  const n = parseNumber(value);

  return n.toLocaleString(getLocale(lang), {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

function moneyText(value, currency = "TRY", lang = "tr") {
  const n = parseNumber(value);

  return `${n.toLocaleString(getLocale(lang), {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} ${normalizeCurrency(currency)}`;
}

function percentText(value, lang = "tr") {
  const n = parseNumber(value);

  return `${n.toLocaleString(getLocale(lang), {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })}%`;
}

function dateText(value, lang = "tr") {
  if (!value) return "-";

  try {
    return new Intl.DateTimeFormat(getLocale(lang), {
      dateStyle: "short",
      timeStyle: "short",
    }).format(new Date(value));
  } catch {
    return "-";
  }
}

function todayKey() {
  return new Date().toISOString().slice(0, 10);
}

function monthKey() {
  return new Date().toISOString().slice(0, 7);
}

function readStorage(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;

    const parsed = JSON.parse(raw);
    return parsed || fallback;
  } catch {
    return fallback;
  }
}

function writeStorage(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // localStorage doluysa UI kırılmasın.
  }
}

async function apiGet(path) {
  const token = getToken();

  const res = await fetch(`${API_BASE}${path}`, {
    headers: {
      Authorization: token ? `Bearer ${token}` : "",
      "Content-Type": "application/json",
    },
  });

  const json = await res.json().catch(() => ({}));

  if (!res.ok || json.ok === false) {
    throw new Error(json.message || `HTTP ${res.status}`);
  }

  return json.data || json;
}

async function apiPost(path, body) {
  const token = getToken();

  const res = await fetch(`${API_BASE}${path}`, {
    method: "POST",
    headers: {
      Authorization: token ? `Bearer ${token}` : "",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body || {}),
  });

  const json = await res.json().catch(() => ({}));

  if (!res.ok || json.ok === false) {
    throw new Error(json.message || `HTTP ${res.status}`);
  }

  return json.data || json;
}

async function apiPut(path, body) {
  const token = getToken();

  const res = await fetch(`${API_BASE}${path}`, {
    method: "PUT",
    headers: {
      Authorization: token ? `Bearer ${token}` : "",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body || {}),
  });

  const json = await res.json().catch(() => ({}));

  if (!res.ok || json.ok === false) {
    throw new Error(json.message || `HTTP ${res.status}`);
  }

  return json.data || json;
}

async function apiDelete(path) {
  const token = getToken();

  const res = await fetch(`${API_BASE}${path}`, {
    method: "DELETE",
    headers: {
      Authorization: token ? `Bearer ${token}` : "",
      "Content-Type": "application/json",
    },
  });

  const json = await res.json().catch(() => ({}));

  if (!res.ok || json.ok === false) {
    throw new Error(json.message || `HTTP ${res.status}`);
  }

  return json.data || json;
}

function getCategoryName(categories, categoryId, lang) {
  const cat = categories.find((c) => String(c.id) === String(categoryId));
  return cat ? getTextValue(cat.name, lang) : "-";
}

function getUnitName(units, unitId, lang) {
  const unit = units.find((u) => String(u.id) === String(unitId));
  return unit ? unit.short || getTextValue(unit.name, lang) : "-";
}

function getSupplierName(suppliers, supplierId) {
  const supplier = suppliers.find((s) => String(s.id) === String(supplierId));
  return supplier ? supplier.name : "-";
}

function getCustomerName(customer) {
  if (!customer) return "";

  return (
    customer.full_name ||
    customer.name ||
    customer.customer_name ||
    customer.musteri_adi ||
    ""
  );
}

function getCustomerPhone(customer) {
  if (!customer) return "";

  return customer.phone || customer.telefon || "";
}

function getCustomerLabel(customer) {
  const name = getCustomerName(customer);
  const phone = getCustomerPhone(customer);

  if (!name && !phone) return "-";
  if (name && phone) return `${name} - ${phone}`;

  return name || phone;
}

function noCustomerText(lang = "tr") {
  if (lang === "en") return "No customer selected";
  if (lang === "de") return "Kein Kunde ausgewählt";

  return "Müşteri seçilmedi";
}

function uniqueCustomerList(customers = []) {
  const seen = new Set();

  return (customers || []).filter((customer) => {
    if (!customer || customer.aktif === false) return false;

    const name = safeLower(getCustomerName(customer));
    const phone = String(getCustomerPhone(customer) || "").replace(/\D/g, "");
    const key = phone ? `phone:${phone}` : name ? `name:${name}` : `id:${customer.id}`;

    if (seen.has(key)) return false;

    seen.add(key);
    return true;
  });
}

function getModuleLabel(value, lang) {
  const item = MODULE_OPTIONS.find((m) => m.value === value);
  return item ? getTextValue(item.label, lang) : value || "-";
}

function getStockTypeLabel(type, TT) {
  const item = STOCK_TYPE_OPTIONS.find((x) => x.value === type);
  return item ? TT[item.labelKey] : type || "-";
}

function getMovementLabel(type, TT) {
  const item = MOVEMENT_TYPES.find((x) => x.value === type);
  return item ? TT[item.labelKey] : type || "-";
}

function getMovementMeta(type) {
  return MOVEMENT_TYPES.find((x) => x.value === type) || MOVEMENT_TYPES[0];
}

function getStockStatus(item) {
  const stock = parseNumber(item.current_stock);
  const min = parseNumber(item.min_stock);
  const max = parseNumber(item.max_stock);

  if (stock <= 0) return "out";
  if (min > 0 && stock <= min) return "critical";
  if (max > 0 && stock >= max) return "over";

  return "normal";
}

function getStockStatusLabel(status, TT) {
  if (status === "out") return TT.outOfStock;
  if (status === "critical") return TT.lowStock;
  if (status === "over") return TT.overStock;

  return TT.normalStock;
}

function getStockStatusColor(status) {
  if (status === "out") return "error";
  if (status === "critical") return "warning";
  if (status === "over") return "info";

  return "success";
}

function calcStockValue(item) {
  return parseNumber(item.current_stock) * parseNumber(item.purchase_price);
}

function calcSaleValue(item) {
  return parseNumber(item.current_stock) * parseNumber(item.sale_price);
}

function calcProfit(item) {
  return calcSaleValue(item) - calcStockValue(item);
}

function calcProfitRate(item) {
  const sale = parseNumber(item.sale_price);
  const purchase = parseNumber(item.purchase_price);

  if (sale <= 0) return 0;

  return ((sale - purchase) / sale) * 100;
}

function createEmptyStockItem(overrides = {}) {
  const now = new Date().toISOString();

  return {
    id: uid("stock"),
    name: "",
    barcode: "",
    sku: "",
    category_id: "cat-satis-urunu",
    sub_category: "",
    unit_id: "unit-adet",
    brand: "",
    supplier_id: "sup-default",
    stock_type: "sale_product",
    module: "general",
    current_stock: 0,
    min_stock: 0,
    max_stock: 0,
    purchase_price: 0,
    sale_price: 0,
    vat_rate: 20,
    currency: "TRY",
    image_url: "",
    notes: "",
    active: true,
    backend_id: null,
    created_at: now,
    updated_at: now,
    last_movement_at: null,
    total_sold_qty: 0,
    total_sold_amount: 0,
    ...overrides,
  };
}

function templateRowToItem(row) {
  const [name, categoryId, unitId, stockType, qty, min, purchase, sale, module] =
    row;

  return createEmptyStockItem({
    id: uid("stock-tpl"),
    name,
    category_id: categoryId,
    unit_id: unitId,
    stock_type: stockType,
    current_stock: qty,
    min_stock: min,
    purchase_price: purchase,
    sale_price: sale,
    module,
    sku: `${module}-${safeLower(name).replace(/\s+/g, "-")}`,
  });
}

function createMovement(overrides = {}) {
  const now = new Date().toISOString();

  return {
    id: uid("move"),
    stock_id: "",
    stock_name: "",
    movement_type: "in",
    quantity: 0,
    previous_stock: 0,
    next_stock: 0,
    unit_price: 0,
    total_amount: 0,
    currency: "TRY",
    description: "",
    staff_name: "",
    customer_id: null,
    customer_name: "",
    payment_method: "cash",
    source_module: "stock",
    created_at: now,
    ...overrides,
  };
}

function createCashPayloadFromSale({
  item,
  qty,
  amount,
  paymentMethod,
  customerId,
  customerName,
  staffName,
}) {
  return {
    module_type:
      item.module === "beauty" ? "beauty" : item.module === "qr_menu" ? "menu" : "stock",
    transaction_type: "income",
    title: `Stok Ürün Satışı - ${item.name}`,
    category: "Stok Satışı",
    quantity: qty,
    unit_price: parseNumber(item.sale_price),
    total_amount: amount,
    currency: item.currency || "TRY",
    payment_method: paymentMethod || "cash",
    customer_id: customerId || null,
    customer_name: customerName || "",
    staff_name: staffName || "",
    note: `STOCK_ITEM_ID:${item.id} | Ürün: ${item.name}`,
    transaction_date: todayKey(),
  };
}

function downloadCsv(filename, rows) {
  const csv = rows
    .map((row) =>
      row
        .map((cell) => {
          const value = String(cell ?? "").replace(/"/g, '""');
          return `"${value}"`;
        })
        .join(";")
    )
    .join("\n");

  const blob = new Blob(["\ufeff" + csv], {
    type: "text/csv;charset=utf-8;",
  });

  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");

  a.href = url;
  a.download = filename;
  a.click();

  URL.revokeObjectURL(url);
}

function getIcon(name, sx = {}) {
  const props = { sx };

  switch (name) {
    case "restaurant":
      return <RestaurantIcon {...props} />;
    case "beauty":
      return <SpaIcon {...props} />;
    case "clinic":
      return <ScienceIcon {...props} />;
    case "qr":
      return <QrCode2Icon {...props} />;
    case "cafe":
      return <StorefrontIcon {...props} />;
    case "market":
      return <StorefrontIcon {...props} />;
    case "store":
      return <BusinessIcon {...props} />;
    case "add":
      return <AddIcon {...props} />;
    case "remove":
      return <RemoveIcon {...props} />;
    case "sale":
      return <PointOfSaleIcon {...props} />;
    case "return":
      return <AssignmentReturnIcon {...props} />;
    case "fire":
      return <LocalFireDepartmentIcon {...props} />;
    case "count":
      return <FactCheckIcon {...props} />;
    case "transfer":
      return <LowPriorityIcon {...props} />;
    case "production":
      return <WarehouseIcon {...props} />;
    default:
      return <InventoryIcon {...props} />;
  }
}

function BrandTitle({ title, subtitle, icon }) {
  const theme = useTheme();

  return (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      spacing={2}
      alignItems={{ xs: "flex-start", sm: "center" }}
    >
      <Avatar
        sx={{
          width: 56,
          height: 56,
          background: `linear-gradient(135deg, ${BRAND.blue}, ${BRAND.orange})`,
          boxShadow: "0 18px 40px rgba(11,77,139,0.24)",
        }}
      >
        {icon || <InventoryIcon />}
      </Avatar>

      <Box sx={{ minWidth: 0 }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 950,
            letterSpacing: -1,
            lineHeight: 1.08,
            background: `linear-gradient(135deg, ${BRAND.blue} 0%, ${BRAND.orange} 100%)`,
            backgroundClip: "text",
            WebkitBackgroundClip: "text",
            color: "transparent",
            [theme.breakpoints.down("sm")]: {
              fontSize: "1.65rem",
            },
          }}
        >
          {title}
        </Typography>

        <Typography
          variant="body2"
          sx={{
            mt: 0.8,
            color: "text.secondary",
            maxWidth: 900,
            lineHeight: 1.55,
          }}
        >
          {subtitle}
        </Typography>
      </Box>
    </Stack>
  );
}

function SummaryCard({
  icon,
  title,
  value,
  subtitle,
  tone = "blue",
  progress,
  action,
}) {
  const palette = {
    blue: BRAND.blue,
    orange: BRAND.orange,
    success: BRAND.success,
    danger: BRAND.danger,
    warning: BRAND.warning,
    info: BRAND.info,
    dark: BRAND.dark,
  };

  const main = palette[tone] || palette.blue;

  return (
    <Card
      sx={{
        height: "100%",
        minHeight: { xs: 132, sm: 146, md: 158 },
        borderRadius: 4,
        border: `1px solid ${alpha(main, 0.16)}`,
        background: `linear-gradient(180deg, ${alpha("#FFFFFF", 0.98)} 0%, ${alpha(
          main,
          0.045
        )} 100%)`,
        boxShadow: "0 14px 38px rgba(15,23,42,0.07)",
        overflow: "hidden",
        position: "relative",
        animation: `${floatIn} 0.35s ease`,
        display: "flex",
        flexDirection: "column",
        "&:before": {
          content: '""',
          position: "absolute",
          inset: 0,
          background: `linear-gradient(110deg, transparent 0%, ${alpha(
            "#fff",
            0.62
          )} 35%, transparent 70%)`,
          backgroundSize: "220% 100%",
          animation: `${shine} 4s linear infinite`,
          pointerEvents: "none",
        },
      }}
    >
      <CardContent
        sx={{
          position: "relative",
          zIndex: 1,
          p: { xs: 1.7, sm: 2, md: 2.25 },
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          spacing={1.4}
        >
          <Avatar
            sx={{
              bgcolor: alpha(main, 0.12),
              color: main,
              width: { xs: 38, md: 44 },
              height: { xs: 38, md: 44 },
            }}
          >
            {icon}
          </Avatar>

          {action}
        </Stack>

        <Typography
          variant="body2"
          sx={{
            mt: 1.55,
            color: "text.secondary",
            fontWeight: 900,
            lineHeight: 1.25,
            minHeight: 20,
          }}
        >
          {title}
        </Typography>

        <Typography
          component="div"
          sx={{
            mt: 0.45,
            fontWeight: 950,
            letterSpacing: -0.65,
            color: BRAND.dark,
            lineHeight: 1.05,
            fontSize: "clamp(1.35rem, 1.35vw + 0.9rem, 2.05rem)",
            maxWidth: "100%",
            overflowWrap: "anywhere",
            wordBreak: "normal",
          }}
        >
          {value}
        </Typography>

        {subtitle && (
          <Typography
            variant="caption"
            sx={{
              display: "block",
              mt: 0.7,
              color: "text.secondary",
              lineHeight: 1.35,
              fontWeight: 650,
              maxWidth: "100%",
              overflowWrap: "anywhere",
            }}
          >
            {subtitle}
          </Typography>
        )}

        {typeof progress === "number" && (
          <LinearProgress
            variant="determinate"
            value={Math.min(100, Math.max(0, progress))}
            sx={{
              mt: "auto",
              height: 7,
              borderRadius: 999,
              bgcolor: alpha(main, 0.1),
              "& .MuiLinearProgress-bar": {
                borderRadius: 999,
                bgcolor: main,
              },
            }}
          />
        )}
      </CardContent>
    </Card>
  );
}


function SectionCard({ title, subtitle, icon, action, children, sx = {} }) {
  return (
    <Card
      sx={{
        borderRadius: 4,
        border: `1px solid ${BRAND.border}`,
        boxShadow: "0 18px 45px rgba(15,23,42,0.07)",
        overflow: "hidden",
        ...sx,
      }}
    >
      <CardContent sx={{ p: { xs: 2, md: 2.6 } }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "center" }}
          spacing={2}
          sx={{ mb: 2.2 }}
        >
          <Stack direction="row" spacing={1.4} alignItems="center">
            {icon && (
              <Avatar
                sx={{
                  bgcolor: alpha(BRAND.blue, 0.1),
                  color: BRAND.blue,
                  width: 42,
                  height: 42,
                }}
              >
                {icon}
              </Avatar>
            )}

            <Box>
              <Typography
                variant="h6"
                sx={{ fontWeight: 950, letterSpacing: -0.4 }}
              >
                {title}
              </Typography>

              {subtitle && (
                <Typography
                  variant="body2"
                  sx={{ color: "text.secondary", mt: 0.3 }}
                >
                  {subtitle}
                </Typography>
              )}
            </Box>
          </Stack>

          {action}
        </Stack>

        {children}
      </CardContent>
    </Card>
  );
}

function StatusChip({ status, TT }) {
  const label = getStockStatusLabel(status, TT);
  const color = getStockStatusColor(status);

  return (
    <Chip
      size="small"
      color={color}
      icon={status === "normal" ? <CheckCircleIcon /> : <WarningAmberIcon />}
      label={label}
      sx={{
        fontWeight: 900,
        borderRadius: 999,
        ...(status === "critical" && {
          animation: `${pulseSoft} 1.8s infinite`,
        }),
      }}
    />
  );
}

function ModuleChip({ module, lang }) {
  const meta = MODULE_OPTIONS.find((m) => m.value === module) || MODULE_OPTIONS[0];

  return (
    <Chip
      size="small"
      icon={getIcon(meta.icon, { fontSize: 16 })}
      label={getTextValue(meta.label, lang)}
      sx={{
        borderRadius: 999,
        fontWeight: 800,
        bgcolor: alpha(BRAND.blue, 0.07),
        color: BRAND.blue,
      }}
    />
  );
}

function PriceBlock({ label, value, color, lang, currency }) {
  return (
    <Box
      sx={{
        minWidth: 0,
        width: "100%",
        px: 1.25,
        py: 1.15,
        borderRadius: 2.6,
        bgcolor: alpha(color, 0.07),
        border: `1px solid ${alpha(color, 0.16)}`,
      }}
    >
      <Typography
        variant="caption"
        sx={{
          display: "block",
          color: "text.secondary",
          fontWeight: 850,
          lineHeight: 1.15,
          mb: 0.55,
        }}
      >
        {label}
      </Typography>

      <Typography
        variant="body2"
        title={moneyText(value, currency, lang)}
        sx={{
          fontWeight: 950,
          color,
          lineHeight: 1.25,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {moneyText(value, currency, lang)}
      </Typography>
    </Box>
  );
}

function FilterSelect({ label, value, onChange, children, minWidth = 180 }) {
  return (
    <FormControl
      size="small"
      sx={{
        minWidth: 0,
        width: "100%",
        flex: {
          xs: "1 1 100%",
          sm: `1 1 ${minWidth}px`,
        },
      }}
    >
      <InputLabel>{label}</InputLabel>
      <Select label={label} value={value} onChange={(e) => onChange(e.target.value)}>
        {children}
      </Select>
    </FormControl>
  );
}

function InfoLine({ label, value }) {
  return (
    <Box
      sx={{
        minWidth: 0,
        width: "100%",
        px: 1.15,
        py: 0.95,
        borderRadius: 2.2,
        bgcolor: alpha(BRAND.bg, 0.85),
        border: `1px solid ${alpha(BRAND.border, 0.75)}`,
      }}
    >
      <Typography
        variant="caption"
        sx={{
          display: "block",
          color: "text.secondary",
          fontWeight: 850,
          lineHeight: 1.15,
          mb: 0.45,
          letterSpacing: 0.1,
        }}
      >
        {label}
      </Typography>

      <Typography
        variant="body2"
        title={value || "-"}
        sx={{
          fontWeight: 900,
          color: BRAND.ink,
          lineHeight: 1.25,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {value || "-"}
      </Typography>
    </Box>
  );
}

function StockItemCard({
  item,
  TT,
  lang,
  categories,
  units,
  suppliers,
  onEdit,
  onDelete,
  onMovement,
  onSale,
}) {
  const theme = useTheme();
  const status = getStockStatus(item);

  const statusColor =
    status === "out"
      ? BRAND.danger
      : status === "critical"
      ? BRAND.warning
      : status === "over"
      ? BRAND.info
      : BRAND.success;

  const categoryName = getCategoryName(categories, item.category_id, lang);
  const unitName = getUnitName(units, item.unit_id, lang);
  const supplierName = getSupplierName(suppliers, item.supplier_id);
  const stock = parseNumber(item.current_stock);
  const min = parseNumber(item.min_stock);

  const progress =
    min > 0 ? Math.min(100, Math.max(0, (stock / Math.max(min * 3, 1)) * 100)) : 100;

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 4,
        overflow: "hidden",
        height: "100%",
        borderColor: alpha(statusColor, 0.24),
        background:
          status === "critical" || status === "out"
            ? `linear-gradient(180deg, #fff 0%, ${alpha(statusColor, 0.055)} 100%)`
            : "#fff",
        boxShadow: "0 16px 38px rgba(15,23,42,0.07)",
        transition: "all 0.22s ease",
        animation: `${floatIn} 0.28s ease`,
        "&:hover": {
          transform: { xs: "none", sm: "translateY(-3px)" },
          boxShadow: "0 24px 58px rgba(15,23,42,0.12)",
        },
      }}
    >
      <Box
        sx={{
          height: 7,
          background: `linear-gradient(90deg, ${BRAND.blue}, ${BRAND.orange})`,
        }}
      />

            <CardContent
        sx={{
          p: { xs: 2.1, sm: 2.6 },
          "&:last-child": {
            pb: { xs: 2.1, sm: 2.6 },
          },
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          spacing={1.4}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 950,
                letterSpacing: -0.35,
                lineHeight: 1.18,
                color: BRAND.dark,
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {item.name || "-"}
            </Typography>

            <Stack
              direction="row"
              spacing={0.8}
              useFlexGap
              flexWrap="wrap"
              sx={{ mt: 1 }}
            >
              <ModuleChip module={item.module} lang={lang} />

              <Chip
                size="small"
                label={getStockTypeLabel(item.stock_type, TT)}
                sx={{
                  borderRadius: 999,
                  fontWeight: 800,
                  bgcolor: alpha(BRAND.orange, 0.08),
                  color: BRAND.orange,
                }}
              />
            </Stack>
          </Box>

          <StatusChip status={status} TT={TT} />
        </Stack>

        <Divider sx={{ my: 1.6, borderStyle: "dashed" }} />

                <Grid
          container
          spacing={1.15}
          sx={{
            width: "100%",
            minWidth: 0,
            "& > *": {
              minWidth: 0,
            },
          }}
        >
          <Grid item xs={6}>
            <InfoLine label={TT.category} value={categoryName} />
          </Grid>

          <Grid item xs={6}>
            <InfoLine label={TT.unit} value={unitName} />
          </Grid>

          <Grid item xs={6}>
            <InfoLine label={TT.barcode} value={item.barcode || "-"} />
          </Grid>

          <Grid item xs={6}>
            <InfoLine label={TT.supplier} value={supplierName} />
          </Grid>
        </Grid>

                <Box
          sx={{
            mt: 1.8,
            px: 1.1,
            py: 1.05,
            borderRadius: 2.6,
            bgcolor: alpha(statusColor, 0.045),
            border: `1px solid ${alpha(statusColor, 0.12)}`,
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-end"
            spacing={1.5}
          >
            <Box>
              <Typography
                variant="caption"
                sx={{ color: "text.secondary", fontWeight: 800 }}
              >
                {TT.currentStock}
              </Typography>

              <Typography
                variant="h4"
                sx={{
                  fontWeight: 950,
                  color: statusColor,
                  letterSpacing: -1,
                  lineHeight: 1,
                }}
              >
                {numberText(item.current_stock, lang, stock % 1 === 0 ? 0 : 2)}
              </Typography>
            </Box>

            <Box sx={{ textAlign: "right" }}>
              <Typography
                variant="caption"
                sx={{ color: "text.secondary", fontWeight: 800 }}
              >
                {TT.minStock}
              </Typography>

              <Typography variant="body1" sx={{ fontWeight: 900 }}>
                {numberText(item.min_stock, lang, min % 1 === 0 ? 0 : 2)}
              </Typography>
            </Box>
          </Stack>

          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              mt: 1.1,
              height: 8,
              borderRadius: 999,
              bgcolor: alpha(theme.palette.grey[500], 0.12),
              "& .MuiLinearProgress-bar": {
                borderRadius: 999,
                bgcolor: statusColor,
              },
            }}
          />
        </Box>

                <Grid
          container
          spacing={1.15}
          sx={{
            mt: 1.5,
            width: "100%",
            minWidth: 0,
            "& > *": {
              minWidth: 0,
            },
          }}
        >
          <Grid item xs={6}>
            <PriceBlock
              label={TT.stockValue}
              value={calcStockValue(item)}
              color={BRAND.blue}
              lang={lang}
              currency={item.currency}
            />
          </Grid>

          <Grid item xs={6}>
            <PriceBlock
              label={TT.potentialSaleValue}
              value={calcSaleValue(item)}
              color={BRAND.orange}
              lang={lang}
              currency={item.currency}
            />
          </Grid>
              </Grid>

        <Stack
          direction="row"
          spacing={1}
          flexWrap="wrap"
          sx={{ mt: 1.6 }}
        >
          <Button
            size="small"
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => onMovement(item, "in")}
            sx={{
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 900,
              bgcolor: BRAND.success,
              "&:hover": { bgcolor: "#059669" },
            }}
          >
            {TT.stockIn}
          </Button>

          <Button
            size="small"
            variant="outlined"
            startIcon={<RemoveIcon />}
            onClick={() => onMovement(item, "out")}
            sx={{
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 900,
              borderColor: alpha(BRAND.danger, 0.5),
              color: BRAND.danger,
            }}
          >
            {TT.stockOut}
          </Button>

          <Button
            size="small"
            variant="contained"
            startIcon={<PointOfSaleIcon />}
            onClick={() => onSale(item)}
            sx={{
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 900,
              bgcolor: BRAND.orange,
              "&:hover": { bgcolor: "#D96C0C" },
            }}
          >
            {TT.sale}
          </Button>

              <Button
            size="small"
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={() => onEdit(item)}
            sx={{
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 900,
              borderColor: alpha(BRAND.blue, 0.45),
              color: BRAND.blue,
              bgcolor: alpha(BRAND.blue, 0.035),
              "&:hover": {
                borderColor: BRAND.blue,
                bgcolor: alpha(BRAND.blue, 0.08),
              },
            }}
          >
            {TT.edit}
          </Button>

          <Button
            size="small"
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => onDelete(item)}
            sx={{
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 900,
              borderWidth: 1.5,
              "&:hover": {
                borderWidth: 1.5,
              },
            }}
          >
            {TT.delete}
          </Button>


        </Stack>
      </CardContent>
    </Card>
  );
}

function StockFormDialog({
  open,
  onClose,
  onSave,
  item,
  setItem,
  TT,
  lang,
  categories,
  units,
  suppliers,
}) {
  if (!item) return null;

  const handle = (field, value) => {
    setItem((prev) => ({
      ...prev,
      [field]: value,
      updated_at: new Date().toISOString(),
    }));
  };

  return (
 <Dialog
  open={open}
  onClose={onClose}
  maxWidth="md"
  fullWidth
  PaperProps={{
    sx: {
      borderRadius: { xs: 0, sm: 4 },
      m: { xs: 0, sm: 2 },
      width: { xs: "100%", sm: "calc(100% - 32px)" },
      maxHeight: { xs: "100%", sm: "calc(100% - 64px)" },
    },
  }}
>
      <DialogTitle
        sx={{
          fontWeight: 950,
          letterSpacing: -0.4,
          pb: 1,
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <span>{TT.addStockCard}</span>

          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ pt: "12px !important" }}>
        <Alert severity="info" sx={{ borderRadius: 3, mb: 2 }}>
          {TT.moduleCompatibilityText}
        </Alert>

        <Grid container spacing={2}>
<Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={TT.itemName}
              placeholder={TT.itemNamePlaceholder}
              value={item.name || ""}
              onChange={(e) => handle("name", e.target.value)}
            />
          </Grid>



          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={TT.barcode}
              value={item.barcode || ""}
              onChange={(e) => handle("barcode", e.target.value)}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label={TT.sku}
              value={item.sku || ""}
              onChange={(e) => handle("sku", e.target.value)}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>{TT.module}</InputLabel>

              <Select
                label={TT.module}
                value={item.module || "general"}
                onChange={(e) => handle("module", e.target.value)}
              >
                {MODULE_OPTIONS.map((m) => (
                  <MenuItem key={m.value} value={m.value}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      {getIcon(m.icon, { fontSize: 18 })}
                      <span>{getTextValue(m.label, lang)}</span>
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>{TT.stockType}</InputLabel>

              <Select
                label={TT.stockType}
                value={item.stock_type || "sale_product"}
                onChange={(e) => handle("stock_type", e.target.value)}
              >
                {STOCK_TYPE_OPTIONS.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {TT[type.labelKey]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>{TT.category}</InputLabel>

              <Select
                label={TT.category}
                value={item.category_id || ""}
                onChange={(e) => handle("category_id", e.target.value)}
              >
                {categories.map((cat) => (
                  <MenuItem key={cat.id} value={cat.id}>
                    {getTextValue(cat.name, lang)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label={TT.subCategory}
              value={item.sub_category || ""}
              onChange={(e) => handle("sub_category", e.target.value)}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>{TT.unit}</InputLabel>

              <Select
                label={TT.unit}
                value={item.unit_id || ""}
                onChange={(e) => handle("unit_id", e.target.value)}
              >
                {units.map((unit) => (
                  <MenuItem key={unit.id} value={unit.id}>
                    {getTextValue(unit.name, lang)} ({unit.short})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label={TT.currentStock}
              type="number"
              value={item.current_stock ?? 0}
              onChange={(e) => handle("current_stock", e.target.value)}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label={TT.minStock}
              type="number"
              value={item.min_stock ?? 0}
              onChange={(e) => handle("min_stock", e.target.value)}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label={TT.maxStock}
              type="number"
              value={item.max_stock ?? 0}
              onChange={(e) => handle("max_stock", e.target.value)}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label={TT.purchasePrice}
              type="number"
              value={item.purchase_price ?? 0}
              onChange={(e) => handle("purchase_price", e.target.value)}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label={TT.salePrice}
              type="number"
              value={item.sale_price ?? 0}
              onChange={(e) => handle("sale_price", e.target.value)}
            />
          </Grid>

          <Grid item xs={6} md={2}>
            <TextField
              fullWidth
              label={TT.vatRate}
              type="number"
              value={item.vat_rate ?? 20}
              onChange={(e) => handle("vat_rate", e.target.value)}
            />
          </Grid>

          <Grid item xs={6} md={2}>
            <TextField
              fullWidth
              label={TT.currency}
              value={item.currency || "TRY"}
              onChange={(e) => handle("currency", e.target.value.toUpperCase())}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label={TT.brand}
              value={item.brand || ""}
              onChange={(e) => handle("brand", e.target.value)}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>{TT.supplier}</InputLabel>

              <Select
                label={TT.supplier}
                value={item.supplier_id || "sup-default"}
                onChange={(e) => handle("supplier_id", e.target.value)}
              >
                {suppliers.map((supplier) => (
                  <MenuItem key={supplier.id} value={supplier.id}>
                    {supplier.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

      
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              minRows={3}
              label={TT.notes}
              value={item.notes || ""}
              onChange={(e) => handle("notes", e.target.value)}
            />
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(item.active)}
                  onChange={(e) => handle("active", e.target.checked)}
                />
              }
              label={item.active ? TT.active : TT.passive}
            />
          </Grid>
        </Grid>

        <Grid container spacing={1.4} sx={{ mt: 1 }}>
          <Grid item xs={12} md={4}>
            <PriceBlock
              label={TT.stockValue}
              value={calcStockValue(item)}
              color={BRAND.blue}
              lang={lang}
              currency={item.currency}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <PriceBlock
              label={TT.potentialSaleValue}
              value={calcSaleValue(item)}
              color={BRAND.orange}
              lang={lang}
              currency={item.currency}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <PriceBlock
              label={TT.profit}
              value={calcProfit(item)}
              color={BRAND.success}
              lang={lang}
              currency={item.currency}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{ borderRadius: 2, textTransform: "none", fontWeight: 850 }}
        >
          {TT.cancel}
        </Button>

        <Button
          onClick={onSave}
          variant="contained"
          startIcon={<SaveIcon />}
          sx={{
            borderRadius: 2,
            textTransform: "none",
            fontWeight: 900,
            bgcolor: BRAND.orange,
            "&:hover": { bgcolor: "#D96C0C" },
          }}
        >
          {TT.save}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function MovementDialog({
  open,
  onClose,
  item,
  movementType,
  setMovementType,
  onSave,
  TT,
  lang,
  settings,
  customers = [],
}) {
  const activeCustomers = useMemo(
    () => uniqueCustomerList(customers),
    [customers]
  );

  const [form, setForm] = useState(() =>
    createMovement({
      stock_id: item?.id || "",
      stock_name: item?.name || "",
      movement_type: movementType || "in",
      unit_price: item ? parseNumber(item.purchase_price) : 0,
      currency: item?.currency || settings.defaultCurrency || "TRY",
    })
  );

  useEffect(() => {
    if (open && item) {
      const type = movementType || "in";

      setForm(
        createMovement({
          stock_id: item.id,
          stock_name: item.name,
          movement_type: type,
          unit_price:
            type === "sale"
              ? parseNumber(item.sale_price)
              : parseNumber(item.purchase_price),
          currency: item.currency || settings.defaultCurrency || "TRY",
          payment_method: "cash",
        })
      );
    }
  }, [open, item, movementType, settings.defaultCurrency]);

  if (!item) return null;

  const meta = getMovementMeta(form.movement_type);

  const handle = (field, value) => {
    setForm((prev) => {
      const next = {
        ...prev,
        [field]: value,
      };

      const qty = parseNumber(field === "quantity" ? value : next.quantity);
      const unitPrice = parseNumber(field === "unit_price" ? value : next.unit_price);

      next.total_amount = qty * unitPrice;

      return next;
    });
  };

  const previewNextStock = () => {
    const qty = parseNumber(form.quantity);
    const current = parseNumber(item.current_stock);

    if (form.movement_type === "count_adjustment") {
      return qty;
    }

    return current + qty * meta.effect;
  };

  const nextStock = previewNextStock();
  const isNegative = nextStock < 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 4 } }}
    >
      <DialogTitle sx={{ fontWeight: 950 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" spacing={1} alignItems="center">
            {getIcon(meta.icon, { color: meta.color })}
            <span>{getMovementLabel(form.movement_type, TT)}</span>
          </Stack>

          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ pt: "10px !important" }}>
        <Alert severity={isNegative ? "error" : "info"} sx={{ borderRadius: 3, mb: 2 }}>
          <b>{item.name}</b> — {TT.previousStock}:{" "}
          {numberText(item.current_stock, lang, 2)} / {TT.nextStock}:{" "}
          {numberText(nextStock, lang, 2)}
        </Alert>

        {isNegative && !settings.allowNegativeStock && (
          <Alert severity="error" sx={{ borderRadius: 3, mb: 2 }}>
            {TT.insufficientStock}
          </Alert>
        )}

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>{TT.movementType}</InputLabel>

              <Select
                label={TT.movementType}
                value={form.movement_type}
                onChange={(e) => {
                  const nextType = e.target.value;

                  setMovementType(nextType);
                  handle("movement_type", nextType);
                }}
              >
                {MOVEMENT_TYPES.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      {getIcon(type.icon, {
                        fontSize: 18,
                        color: type.color,
                      })}
                      <span>{TT[type.labelKey]}</span>
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

           <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={TT.quantity}
              type="number"
              value={form.quantity}
              onChange={(e) => handle("quantity", e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{
                min: 0,
                step: 1,
                inputMode: "decimal",
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: 58,
                  borderRadius: 2.8,
                  bgcolor: "#fff",
                },
                "& .MuiInputBase-input": {
                  fontWeight: 950,
                  fontSize: "1.05rem",
                },
                "& .MuiInputLabel-root": {
                  fontWeight: 850,
                  bgcolor: "#fff",
                  px: 0.5,
                },
                "& input[type=number]": {
                  MozAppearance: "textfield",
                },
                "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button": {
                  WebkitAppearance: "none",
                  margin: 0,
                },
              }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={TT.unitPrice}
              type="number"
              value={form.unit_price}
              onChange={(e) => handle("unit_price", e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{
                min: 0,
                step: 0.01,
                inputMode: "decimal",
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: 58,
                  borderRadius: 2.8,
                  bgcolor: "#fff",
                },
                "& .MuiInputBase-input": {
                  fontWeight: 950,
                  fontSize: "1.05rem",
                },
                "& .MuiInputLabel-root": {
                  fontWeight: 850,
                  bgcolor: "#fff",
                  px: 0.5,
                },
                "& input[type=number]": {
                  MozAppearance: "textfield",
                },
                "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button": {
                  WebkitAppearance: "none",
                  margin: 0,
                },
              }}
            />
          </Grid>






                 {(form.movement_type === "sale" || form.movement_type === "return") && (
            <>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel
                    shrink
                    sx={{
                      fontWeight: 850,
                      bgcolor: "#fff",
                      px: 0.5,
                    }}
                  >
                    {TT.paymentMethod}
                  </InputLabel>

                  <Select
                    label={TT.paymentMethod}
                    value={form.payment_method}
                    onChange={(e) => handle("payment_method", e.target.value)}
                    sx={{
                      height: 58,
                      borderRadius: 2.8,
                      bgcolor: "#fff",
                      fontWeight: 900,
                    }}
                  >
                    {PAYMENT_METHODS.map((p) => (
                      <MenuItem key={p.value} value={p.value}>
                        {TT[p.labelKey]}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel
                    shrink
                    sx={{
                      fontWeight: 850,
                      bgcolor: "#fff",
                      px: 0.5,
                    }}
                  >
                    {TT.customer}
                  </InputLabel>

                  <Select
                    label={TT.customer}
                    value={form.customer_id || ""}
                    displayEmpty
                    onChange={(e) => {
                      const selectedId = e.target.value;
                      const customer =
                        activeCustomers.find(
                          (c) => String(c.id) === String(selectedId)
                        ) || null;

                      setForm((prev) => ({
                        ...prev,
                        customer_id: selectedId || null,
                        customer_name: getCustomerName(customer),
                      }));
                    }}
                    sx={{
                      height: 58,
                      borderRadius: 2.8,
                      bgcolor: "#fff",
                      fontWeight: 900,
                      "& .MuiSelect-select": {
                        py: 1.45,
                      },
                    }}
                  >
                    <MenuItem value="">
                      {noCustomerText(lang)}
                    </MenuItem>

                    {activeCustomers.map((customer) => (
                      <MenuItem key={customer.id} value={customer.id}>
                        {getCustomerLabel(customer)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </>
          )}



          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={TT.staff}
              value={form.staff_name}
              onChange={(e) => handle("staff_name", e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: 58,
                  borderRadius: 2.8,
                  bgcolor: "#fff",
                },
                "& .MuiInputBase-input": {
                  fontWeight: 900,
                },
                "& .MuiInputLabel-root": {
                  fontWeight: 850,
                  bgcolor: "#fff",
                  px: 0.5,
                },
              }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={TT.totalAmount}
              value={moneyText(form.total_amount, form.currency, lang)}
              InputProps={{ readOnly: true }}
              InputLabelProps={{ shrink: true }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: 58,
                  borderRadius: 2.8,
                  bgcolor: alpha(BRAND.orange, 0.06),
                },
                "& .MuiInputBase-input": {
                  fontWeight: 950,
                  color: BRAND.orange,
                },
                "& .MuiInputLabel-root": {
                  fontWeight: 850,
                  bgcolor: "#fff",
                  px: 0.5,
                },
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              minRows={3}
              label={TT.description}
              value={form.description}
              onChange={(e) => handle("description", e.target.value)}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{ borderRadius: 2, textTransform: "none", fontWeight: 850 }}
        >
          {TT.cancel}
        </Button>

        <Button
          disabled={isNegative && !settings.allowNegativeStock}
          onClick={() => onSave(form)}
          variant="contained"
          startIcon={<SaveIcon />}
          sx={{
            borderRadius: 2,
            textTransform: "none",
            fontWeight: 900,
            bgcolor: meta.color,
          }}
        >
          {TT.confirm}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function DeleteDialog({ open, onClose, onConfirm, TT, item }) {
  return (
    <Dialog open={open} onClose={onClose} PaperProps={{ sx: { borderRadius: 4 } }}>
      <DialogTitle sx={{ fontWeight: 950 }}>{TT.deleteTitle}</DialogTitle>

      <DialogContent>
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          {TT.deleteText}
        </Typography>

        {item && (
          <Alert severity="warning" sx={{ mt: 2, borderRadius: 3 }}>
            {item.name}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{ borderRadius: 2, textTransform: "none", fontWeight: 850 }}
        >
          {TT.cancel}
        </Button>

        <Button
          onClick={onConfirm}
          color="error"
          variant="contained"
          sx={{ borderRadius: 2, textTransform: "none", fontWeight: 900 }}
        >
          {TT.delete}
        </Button>
      </DialogActions>
    </Dialog>
  );
}


function QuickSalePanel({
  items,
  setItems,
  setMovements,
  TT,
  lang,
  settings,
  customers = [],
  setSnackbar,
  serverMode = false,
}) {
  const [query, setQuery] = useState("");
  const [basket, setBasket] = useState([]);
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [customerId, setCustomerId] = useState("");
  const [staffName, setStaffName] = useState("");
  const [lastSaleTotal, setLastSaleTotal] = useState(0);
  const [saleSaving, setSaleSaving] = useState(false);

  const activeCustomers = useMemo(
    () => uniqueCustomerList(customers),
    [customers]
  );

  const selectedCustomer = useMemo(
    () =>
      activeCustomers.find((c) => String(c.id) === String(customerId)) || null,
    [activeCustomers, customerId]
  );

  const customerName = getCustomerName(selectedCustomer);

  const saleItems = useMemo(() => {
    const term = safeLower(query);

    const source = items.filter((item) => {
      if (!item.active) return false;

      // Hammadde ve ekipman direkt satış listesinde görünmesin.
      // Satış fiyatı 0 olsa bile ürün listede görünsün, fiyat sepette girilir.
      return !["raw_material", "equipment"].includes(item.stock_type);
    });

    if (!term) return source.slice(0, 12);

    return source
      .filter((item) =>
        [item.name, item.barcode, item.sku, item.brand, item.category_id]
          .map(safeLower)
          .some((x) => x.includes(term))
      )
      .slice(0, 20);
  }, [items, query]);

  const basketTotal = basket.reduce(
    (sum, row) => sum + parseNumber(row.qty) * parseNumber(row.unit_price),
    0
  );

  const addToBasket = (item) => {
    setLastSaleTotal(0);

    setBasket((prev) => {
      const exists = prev.find((row) => row.item.id === item.id);

      if (exists) {
        return prev.map((row) =>
          row.item.id === item.id
            ? { ...row, qty: parseNumber(row.qty) + 1 }
            : row
        );
      }

      return [
        ...prev,
        {
          id: uid("basket"),
          item,
          qty: 1,
          unit_price: parseNumber(item.sale_price || 0),
        },
      ];
    });
  };

  const updateQty = (basketId, qty) => {
    setBasket((prev) =>
      prev
        .map((row) =>
          row.id === basketId ? { ...row, qty: parseNumber(qty) } : row
        )
        .filter((row) => parseNumber(row.qty) > 0)
    );
  };

  const updateBasketPrice = (basketId, price) => {
    setBasket((prev) =>
      prev.map((row) =>
        row.id === basketId
          ? { ...row, unit_price: parseNumber(price) }
          : row
      )
    );
  };



  const completeSale = async () => {
    if (saleSaving) return;

    if (basket.length === 0) {
      setSnackbar({
        open: true,
        severity: "warning",
        message: TT.emptyBasket,
      });
      return;
    }

    const invalid = basket.find(
      (row) =>
        !settings.allowNegativeStock &&
        parseNumber(row.qty) > parseNumber(row.item.current_stock)
    );

    if (invalid) {
      setSnackbar({
        open: true,
        severity: "error",
        message: `${invalid.item.name}: ${TT.insufficientStock}`,
      });
      return;
    }

    setSaleSaving(true);

    try {
      const completedTotal = basketTotal;

      if (serverMode) {
        const saleResult = await apiPost("/stock/sales", {
          items: basket.map((row) => ({
            stock_id: row.item.backend_id || row.item.id,
            quantity: parseNumber(row.qty),
            unit_price: parseNumber(row.unit_price),
            currency: row.item.currency || settings.defaultCurrency,
            source_module: row.item.module || "stock",
          })),
          payment_method: paymentMethod,
          customer_id: customerId || null,
          customer_name: customerName,
          staff_name: staffName,
          currency: settings.defaultCurrency,
          description: "Hızlı ürün satışı",
          allow_negative_stock: settings.allowNegativeStock === true,
        });

        const savedItems = Array.isArray(saleResult.items)
          ? saleResult.items
          : [];

        const savedMovements = Array.isArray(saleResult.movements)
          ? saleResult.movements
          : [];

        setItems((prev) =>
          prev.map((item) => {
            const saved = savedItems.find(
              (x) =>
                String(x.id) === String(item.id) ||
                String(x.backend_id) === String(item.backend_id) ||
                String(x.backend_id) === String(item.id)
            );

            return saved ? { ...item, ...saved } : item;
          })
        );

        if (savedMovements.length > 0) {
          setMovements((prev) => [...savedMovements, ...prev]);
        }
      } else {
        const now = new Date().toISOString();
        const newMovements = [];

        const nextItems = items.map((item) => {
          const row = basket.find((b) => b.item.id === item.id);

          if (!row) return item;

          const qty = parseNumber(row.qty);
          const unitPrice = parseNumber(row.unit_price);
          const previous = parseNumber(item.current_stock);
          const next = previous - qty;
          const total = qty * unitPrice;

          newMovements.push(
            createMovement({
              stock_id: item.id,
              stock_name: item.name,
              movement_type: "sale",
              quantity: qty,
              previous_stock: previous,
              next_stock: next,
              unit_price: unitPrice,
              total_amount: total,
              currency: item.currency || settings.defaultCurrency,
              description: "Hızlı ürün satışı",
              staff_name: staffName,
              customer_id: customerId || null,
              customer_name: customerName,
              payment_method: paymentMethod,
              source_module: item.module || "stock",
              created_at: now,
            })
          );

          return {
            ...item,
            current_stock: next,
            total_sold_qty: parseNumber(item.total_sold_qty) + qty,
            total_sold_amount: parseNumber(item.total_sold_amount) + total,
            last_movement_at: now,
            updated_at: now,
          };
        });

        setItems(nextItems);
        setMovements((prev) => [...newMovements, ...prev]);
      }

      setBasket([]);
      setCustomerId("");
      setStaffName("");
      setQuery("");
      setLastSaleTotal(completedTotal);

      setSnackbar({
        open: true,
        severity: "success",
        message: `${TT.saleCompleted} ${moneyText(
          completedTotal,
          settings.defaultCurrency,
          lang
        )}`,
      });
    } catch (err) {
      setSnackbar({
        open: true,
        severity: "error",
        message: err.message || "Satış kasaya aktarılamadı",
      });
    } finally {
      setSaleSaving(false);
    }
  };


     return (
    <Grid
      container
      spacing={2.5}
      sx={{
        width: "100%",
        minWidth: 0,
        m: 0,
        "& .MuiCard-root": {
          minWidth: 0,
          overflow: "hidden",
        },
        "& .MuiChip-root": {
          maxWidth: "100%",
        },
        "& .MuiChip-label": {
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        },
      }}
    >
      <Grid item xs={12} lg={4}>
        <SectionCard title={TT.category} subtitle={TT.addCategory} icon={<CategoryIcon />}>


          <Stack spacing={2}>
            <TextField
              fullWidth
              size="small"
              label={TT.scanBarcode}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: 54,
                  borderRadius: 2.8,
                  bgcolor: "#fff",
                },
                "& .MuiInputLabel-root": {
                  fontWeight: 850,
                  bgcolor: "#fff",
                  px: 0.5,
                },
              }}
            />

            {saleItems.length === 0 ? (
              <Alert severity="info" sx={{ borderRadius: 3 }}>
                {TT.noFilteredData}
              </Alert>
            ) : (
              <Grid container spacing={1.4}>
                {saleItems.map((item) => (
                  <Grid item xs={12} sm={6} key={item.id}>
                    <Card
                      variant="outlined"
                      onClick={() => addToBasket(item)}
                      sx={{
                        borderRadius: 3,
                        p: 1.4,
                        height: "100%",
                        cursor: "pointer",
                        transition: "0.2s ease",
                        "&:hover": {
                          borderColor: alpha(BRAND.orange, 0.55),
                          bgcolor: alpha(BRAND.orange, 0.04),
                        },
                      }}
                    >
                      <Stack spacing={1}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 950,
                            color: BRAND.dark,
                            lineHeight: 1.25,
                          }}
                        >
                          {item.name}
                        </Typography>

                                <Stack
          direction="row"
          spacing={0.8}
          useFlexGap
          flexWrap="wrap"
          sx={{
            mt: 1.8,
            pt: 1.1,
            borderTop: `1px dashed ${alpha(BRAND.border, 0.9)}`,
            "& .MuiButton-root": {
              minHeight: 34,
              px: 1.25,
            },
          }}
        >
                          <Chip
                            size="small"
                            label={`${TT.currentStock}: ${numberText(
                              item.current_stock,
                              lang,
                              2
                            )}`}
                            sx={{
                              fontWeight: 850,
                              borderRadius: 999,
                            }}
                          />

                          <Chip
                            size="small"
                            label={moneyText(
                              item.sale_price,
                              item.currency || settings.defaultCurrency,
                              lang
                            )}
                            sx={{
                              fontWeight: 900,
                              borderRadius: 999,
                              bgcolor: alpha(BRAND.orange, 0.1),
                              color: BRAND.orange,
                            }}
                          />
                        </Stack>

                        <Button
                          fullWidth
                          size="small"
                          variant="contained"
                          startIcon={<ShoppingCartIcon />}
                          onClick={(e) => {
                            e.stopPropagation();
                            addToBasket(item);
                          }}
                          sx={{
                            mt: "auto",
                            borderRadius: 2.4,
                            textTransform: "none",
                            fontWeight: 950,
                            bgcolor: BRAND.blue,
                            "&:hover": {
                              bgcolor: BRAND.blue2,
                            },
                          }}
                        >
                          {TT.addToBasket}
                        </Button>
                      </Stack>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </Stack>
        </SectionCard>
      </Grid>

      <Grid item xs={12} md={6}>
        <SectionCard
          title={TT.basket}
          subtitle={TT.backendReady}
          icon={<ShoppingCartIcon />}
        >
          <Stack spacing={2}>
            <Card
              variant="outlined"
              sx={{
                borderRadius: 3,
                p: 1.5,
                bgcolor: alpha(BRAND.blue, 0.045),
              }}
            >
              <Stack direction="row" justifyContent="space-between" spacing={2}>
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                      fontWeight: 800,
                    }}
                  >
                    {TT.totalAmount}
                  </Typography>

                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 950,
                      color: BRAND.blue,
                    }}
                  >
                    {moneyText(basketTotal, settings.defaultCurrency, lang)}
                  </Typography>
                </Box>

                
<Chip
  label={`${basket.length} ${
    basket.length === 1 ? TT.product : TT.products
  }`}
  sx={{
    borderRadius: 999,
    fontWeight: 950,
    bgcolor: alpha(BRAND.orange, 0.12),
    color: BRAND.orange,
  }}
/>
              </Stack>
            </Card>

            {basket.length === 0 ? (
              <Alert severity="info" sx={{ borderRadius: 3 }}>
                {TT.emptyBasket}
              </Alert>
            ) : (
              <Stack spacing={1}>
                {basket.map((row) => (
                  <Card
                    key={row.id}
                    variant="outlined"
                    sx={{
                      borderRadius: 3,
                      p: 1.2,
                    }}
                  >
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={1}
                      alignItems={{ xs: "stretch", sm: "center" }}
                    >
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 950,
                          }}
                        >
                          {row.item.name}
                        </Typography>

                        <Typography
                          variant="caption"
                          sx={{
                            color: "text.secondary",
                          }}
                        >
                          {moneyText(
                            row.unit_price,
                            row.item.currency || settings.defaultCurrency,
                            lang
                          )}
                        </Typography>
                      </Box>

                      <Stack
                        direction="row"
                        spacing={0.8}
                        alignItems="center"
                        justifyContent="flex-end"
                        sx={{
                          width: { xs: "100%", sm: "auto" },
                          minWidth: { xs: "100%", sm: 350 },
                          flexWrap: { xs: "wrap", sm: "nowrap" },
                        }}
                      >
                        <Box
                          sx={{
                            flex: { xs: 1, sm: "0 0 150px" },
                            height: 52,
                            borderRadius: 2.6,
                            bgcolor: "#fff",
                            border: `1px solid ${alpha(BRAND.blue, 0.18)}`,
                            display: "grid",
                            gridTemplateColumns: "38px 1fr 38px",
                            alignItems: "center",
                            overflow: "hidden",
                          }}
                        >
                          <IconButton
                            size="small"
                            onClick={() =>
                              updateQty(
                                row.id,
                                Math.max(0, parseNumber(row.qty) - 1)
                              )
                            }
                            sx={{
                              width: 38,
                              height: 52,
                              borderRadius: 0,
                              color: BRAND.blue,
                              bgcolor: alpha(BRAND.blue, 0.045),
                              "&:hover": {
                                bgcolor: alpha(BRAND.blue, 0.09),
                              },
                            }}
                          >
                            <RemoveIcon fontSize="small" />
                          </IconButton>

                          <TextField
                            variant="standard"
                            type="number"
                            value={row.qty}
                            onChange={(e) => updateQty(row.id, e.target.value)}
                            inputProps={{
                              min: 0,
                              step: 1,
                              inputMode: "numeric",
                              style: {
                                textAlign: "center",
                              },
                            }}
                            InputProps={{
                              disableUnderline: true,
                            }}
                            sx={{
                              px: 0.7,
                              "& .MuiInputBase-input": {
                                fontWeight: 950,
                                fontSize: "1.05rem",
                                lineHeight: 1.1,
                                py: 0.4,
                              },
                              "& input[type=number]": {
                                MozAppearance: "textfield",
                              },
                              "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button": {
                                WebkitAppearance: "none",
                                margin: 0,
                              },
                            }}
                          />

                          <IconButton
                            size="small"
                            onClick={() =>
                              updateQty(row.id, parseNumber(row.qty) + 1)
                            }
                            sx={{
                              width: 38,
                              height: 52,
                              borderRadius: 0,
                              color: BRAND.orange,
                              bgcolor: alpha(BRAND.orange, 0.08),
                              "&:hover": {
                                bgcolor: alpha(BRAND.orange, 0.14),
                              },
                            }}
                          >
                            <AddIcon fontSize="small" />
                          </IconButton>
                        </Box>

                        <TextField
                          size="small"
                          type="number"
                          label={TT.salePrice}
                          value={row.unit_price}
                          onChange={(e) =>
                            updateBasketPrice(row.id, e.target.value)
                          }
                          InputLabelProps={{
                            shrink: true,
                          }}
                          inputProps={{
                            min: 0,
                            step: 0.01,
                            inputMode: "decimal",
                          }}
                          sx={{
                            width: { xs: "100%", sm: 130 },
                            "& .MuiOutlinedInput-root": {
                              height: 52,
                              borderRadius: 2.6,
                              bgcolor: "#fff",
                            },
                            "& .MuiInputBase-input": {
                              fontWeight: 950,
                              fontSize: "1rem",
                            },
                            "& .MuiInputLabel-root": {
                              fontWeight: 850,
                              bgcolor: "#fff",
                              px: 0.5,
                            },
                            "& input[type=number]": {
                              MozAppearance: "textfield",
                            },
                            "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button": {
                              WebkitAppearance: "none",
                              margin: 0,
                            },
                          }}
                        />

                        <IconButton
                          color="error"
                          onClick={() => updateQty(row.id, 0)}
                          sx={{
                            width: 48,
                            height: 48,
                            borderRadius: 2.4,
                            flexShrink: 0,
                            bgcolor: alpha(BRAND.danger, 0.08),
                            "&:hover": {
                              bgcolor: alpha(BRAND.danger, 0.14),
                            },
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    </Stack>
                  </Card>
                ))}
              </Stack>
            )}

            <Divider sx={{ my: 0.5 }} />

            <Grid container spacing={1.4}>
              <Grid item xs={12}>
                <FormControl fullWidth size="small">
                  <InputLabel
                    shrink
                    sx={{
                      fontWeight: 850,
                      bgcolor: "#fff",
                      px: 0.5,
                    }}
                  >
                    {TT.customer}
                  </InputLabel>

                  <Select
                    label={TT.customer}
                    value={customerId}
                    onChange={(e) => setCustomerId(e.target.value)}
                    displayEmpty
                    sx={{
                      height: 54,
                      borderRadius: 2.6,
                      bgcolor: "#fff",
                      fontWeight: 900,
                    }}
                  >
                    <MenuItem value="">
                      {noCustomerText(lang)}
                    </MenuItem>

                    {activeCustomers.map((customer) => (
                      <MenuItem key={customer.id} value={customer.id}>
                        {getCustomerLabel(customer)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label={TT.staff}
                  value={staffName}
                  onChange={(e) => setStaffName(e.target.value)}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      height: 54,
                      borderRadius: 2.6,
                      bgcolor: "#fff",
                    },
                    "& .MuiInputLabel-root": {
                      fontWeight: 850,
                      bgcolor: "#fff",
                      px: 0.5,
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth size="small">
                  <InputLabel>{TT.paymentMethod}</InputLabel>

                  <Select
                    label={TT.paymentMethod}
                    value={paymentMethod}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                    sx={{
                      height: 54,
                      borderRadius: 2.6,
                      bgcolor: "#fff",
                      fontWeight: 900,
                    }}
                  >
                    {PAYMENT_METHODS.map((p) => (
                      <MenuItem key={p.value} value={p.value}>
                        {TT[p.labelKey]}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                               <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  startIcon={saleSaving ? <SyncIcon /> : <PaymentsIcon />}
                  onClick={completeSale}
                  disabled={saleSaving || basket.length === 0}
                  sx={{
                    minHeight: 56,
                    borderRadius: 3,
                    textTransform: "none",
                    fontWeight: 950,
                    bgcolor: BRAND.orange,
                    boxShadow: "0 12px 28px rgba(245,130,32,0.25)",
                    "&:hover": {
                      bgcolor: "#D96C0C",
                      boxShadow: "0 16px 34px rgba(217,108,12,0.30)",
                    },
                  }}
                >
                  {saleSaving ? "İşleniyor..." : TT.completeSale}
                </Button>
              </Grid>

              {lastSaleTotal > 0 && (
                <Grid item xs={12}>
                  <Button
                    fullWidth
                    variant="outlined"
                    color="primary"
                    startIcon={<PaymentsIcon />}
                    onClick={() => {
                      window.location.hash = "/kasa-tahsilat";
                    }}
                    sx={{
                      minHeight: 52,
                      borderRadius: 3,
                      textTransform: "none",
                      fontWeight: 950,
                      borderWidth: 2,
                      bgcolor: alpha(BRAND.blue, 0.035),
                      "&:hover": {
                        borderWidth: 2,
                        bgcolor: alpha(BRAND.blue, 0.08),
                      },
                    }}
                  >
                    💰 {TT.openCashPanel || "Kasada Aç"} ·{" "}
                    {moneyText(lastSaleTotal, settings.defaultCurrency, lang)}
                  </Button>
                </Grid>
              )}
            </Grid>
          </Stack>
        </SectionCard>
      </Grid>
    </Grid>
  );
}











function TemplatesPanel({ TT, items, setItems, setSnackbar, serverMode = false }) {
  const [preset, setPreset] = useState("restaurant");
  const [templateSaving, setTemplateSaving] = useState(false);

  const [selectedNames, setSelectedNames] = useState(() =>
    new Set((TEMPLATE_ITEMS.restaurant || []).map((row) => row[0]))
  );

  useEffect(() => {
    setSelectedNames(new Set((TEMPLATE_ITEMS[preset] || []).map((row) => row[0])));
  }, [preset]);

  const rows = TEMPLATE_ITEMS[preset] || [];

  const toggleName = (name) => {
    setSelectedNames((prev) => {
      const next = new Set(prev);

      if (next.has(name)) next.delete(name);
      else next.add(name);

      return next;
    });
  };

  const applyTemplate = async () => {
    if (templateSaving) return;

    const existingNames = new Set(
      items.filter((x) => x.active !== false).map((x) => safeLower(x.name))
    );

    const addItems = rows
      .filter((row) => selectedNames.has(row[0]))
      .filter((row) => !existingNames.has(safeLower(row[0])))
      .map(templateRowToItem);

    if (addItems.length === 0) {
      setSnackbar({
        open: true,
        severity: "warning",
        message: "Eklenecek yeni şablon ürünü yok.",
      });
      return;
    }

    setTemplateSaving(true);

    try {
      if (serverMode) {
        const savedItems = [];

        for (const item of addItems) {
          const saved = await apiPost("/stock/items", item);
          savedItems.push(saved);
        }

        setItems((prev) => [...savedItems, ...prev]);
      } else {
        setItems((prev) => [...addItems, ...prev]);
      }

      setSnackbar({
        open: true,
        severity: "success",
        message: `${TT.itemsAdded} (${addItems.length})`,
      });
    } catch (err) {
      setSnackbar({
        open: true,
        severity: "error",
        message: err.message || "Şablon ürünleri kaydedilemedi",
      });
    } finally {
      setTemplateSaving(false);
    }
  };

  return (
    <Grid container spacing={2.5}>
      <Grid item xs={12} md={4}>
        <SectionCard title={TT.templates} subtitle={TT.addFromTemplate} icon={<BoltIcon />}>
          <Stack spacing={1.4}>
            {BUSINESS_PRESETS.map((p) => {
              const active = preset === p.id;

              return (
                <Card
                  key={p.id}
                  variant="outlined"
                  onClick={() => setPreset(p.id)}
                  sx={{
                    p: 1.5,
                    borderRadius: 3,
                    cursor: "pointer",
                    borderColor: active ? p.color : "divider",
                    bgcolor: active ? alpha(p.color, 0.06) : "background.paper",
                  }}
                >
                  <Stack direction="row" spacing={1.4} alignItems="center">
                    <Avatar sx={{ bgcolor: alpha(p.color, 0.12), color: p.color }}>
                      {getIcon(p.icon)}
                    </Avatar>

                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 950 }}>
                        {TT[p.labelKey]}
                      </Typography>

                      <Typography variant="caption" sx={{ color: "text.secondary" }}>
                        {p.description}
                      </Typography>
                    </Box>
                  </Stack>
                </Card>
              );
            })}
          </Stack>
        </SectionCard>
      </Grid>

      <Grid item xs={12} md={8}>
        <SectionCard
          title={TT.applyTemplate}
          subtitle={`${TT.selected}: ${selectedNames.size}`}
          icon={<InventoryIcon />}
                    action={
            <Stack
              direction="row"
              spacing={1}
              useFlexGap
              flexWrap="wrap"
              sx={{
                justifyContent: { xs: "flex-start", md: "flex-end" },
                width: "100%",
                minWidth: 0,
                "& .MuiButton-root": {
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 900,
                  whiteSpace: "nowrap",
                },
              }}
            >
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  setSelectedNames(new Set(rows.map((row) => row[0])));
                  setSnackbar({
                    open: true,
                    severity: "info",
                    message: "Tüm şablon ürünleri seçildi.",
                  });
                }}
              >
                {TT.selectAll}
              </Button>

              <Button
                size="small"
                variant={selectedNames.size > 0 ? "contained" : "outlined"}
                color="warning"
                onClick={() => {
                  setSelectedNames(new Set());
                  setSnackbar({
                    open: true,
                    severity: "info",
                    message: "Şablon seçimi temizlendi.",
                  });
                }}
                disabled={templateSaving || selectedNames.size === 0}
                sx={{
                  bgcolor: selectedNames.size > 0 ? alpha(BRAND.orange, 0.95) : undefined,
                  color: selectedNames.size > 0 ? "#fff" : undefined,
                  "&:hover": {
                    bgcolor: selectedNames.size > 0 ? "#D96C0C" : undefined,
                  },
                }}
              >
                {TT.unselectAll}
              </Button>

              <Button
                size="small"
                variant="contained"
                onClick={applyTemplate}
                disabled={templateSaving || selectedNames.size === 0}
                startIcon={templateSaving ? <SyncIcon /> : <SaveIcon />}
                sx={{
                  bgcolor: BRAND.orange,
                  boxShadow: "0 8px 18px rgba(245,130,32,0.28)",
                  "&:hover": { bgcolor: "#D96C0C" },
                }}
              >
                {templateSaving
                  ? "Kaydediliyor..."
                  : selectedNames.size === 0
                  ? "Önce ürün seç"
                  : TT.applyTemplate}
              </Button>
            </Stack>
          }
        >
                    <Grid
            container
            spacing={1.3}
            sx={{
              width: "100%",
              minWidth: 0,
              "& > *": {
                minWidth: 0,
              },
            }}
          >
            {rows.map((row) => {
              const [name, , , stockType, qty, min, purchase, sale] = row;
              const checked = selectedNames.has(name);

              return (
                <Grid item xs={12} sm={6} key={name}>
                  <Card
                    variant="outlined"
                    onClick={() => toggleName(name)}
                    sx={{
                      p: 1.3,
                      borderRadius: 3,
                      cursor: "pointer",
                      transition: "all .18s ease",
                      borderColor: checked ? alpha(BRAND.orange, 0.65) : alpha(BRAND.border, 0.9),
                      bgcolor: checked ? alpha(BRAND.orange, 0.06) : alpha("#fff", 0.92),
                      opacity: checked ? 1 : 0.45,
                      filter: checked ? "none" : "grayscale(.15)",
                      "&:hover": {
                        opacity: 1,
                        borderColor: checked ? BRAND.orange : alpha(BRAND.blue, 0.35),
                        bgcolor: checked ? alpha(BRAND.orange, 0.08) : alpha(BRAND.blue, 0.035),
                      },
                    }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Checkbox
                        checked={checked}
                        onClick={(e) => e.stopPropagation()}
                        onChange={() => toggleName(name)}
                      />

                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 950,
                            overflowWrap: "anywhere",
                          }}
                        >
                          {name}
                        </Typography>

                        <Typography
                          variant="caption"
                          sx={{
                            color: "text.secondary",
                            display: "block",
                            fontWeight: 700,
                            overflowWrap: "anywhere",
                          }}
                        >
                          {getStockTypeLabel(stockType, TT)} · {TT.currentStock}: {qty} ·{" "}
                          {TT.minStock}: {min}
                        </Typography>
                      </Box>

                      <Box sx={{ textAlign: "right", flexShrink: 0 }}>
                        <Typography
                          variant="caption"
                          sx={{ display: "block", color: "text.secondary" }}
                        >
                          {TT.purchase}
                        </Typography>

                        <Typography variant="body2" sx={{ fontWeight: 950 }}>
                          {moneyText(purchase, "TRY", "tr")}
                        </Typography>

                        {sale > 0 && (
                          <Typography
                            variant="caption"
                            sx={{ color: BRAND.orange, fontWeight: 950 }}
                          >
                            {moneyText(sale, "TRY", "tr")}
                          </Typography>
                        )}
                      </Box>
                    </Stack>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </SectionCard>
      </Grid>
    </Grid>
  );
}





function DefinitionsPanel({
  TT,
  lang,
  categories,
  setCategories,
  units,
  setUnits,
  suppliers,
  setSuppliers,
}) {
  const [categoryName, setCategoryName] = useState("");
  const [unitName, setUnitName] = useState("");
  const [unitShort, setUnitShort] = useState("");
  const [supplierName, setSupplierName] = useState("");

  const addCategory = () => {
    const name = categoryName.trim();

    if (!name) return;

    setCategories((prev) => [
      ...prev,
      {
        id: uid("cat"),
        name: { tr: name, en: name, de: name },
        module: "general",
        color: BRAND.orange,
        active: true,
      },
    ]);

    setCategoryName("");
  };

  const addUnit = () => {
    const name = unitName.trim();

    if (!name) return;

    setUnits((prev) => [
      ...prev,
      {
        id: uid("unit"),
        name: { tr: name, en: name, de: name },
        short: unitShort.trim() || name,
        decimal: true,
        active: true,
      },
    ]);

    setUnitName("");
    setUnitShort("");
  };

  const addSupplier = () => {
    const name = supplierName.trim();

    if (!name) return;

    setSuppliers((prev) => [
      ...prev,
      {
        id: uid("sup"),
        name,
        authorized: "",
        phone: "",
        email: "",
        address: "",
        taxNo: "",
        active: true,
        createdAt: new Date().toISOString(),
      },
    ]);

    setSupplierName("");
  };

  const chipSx = {
    width: "100%",
    maxWidth: "100%",
    justifyContent: "flex-start",
    fontWeight: 850,
    minWidth: 0,
    borderRadius: 999,
    "& .MuiChip-label": {
      display: "block",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      minWidth: 0,
    },
  };

    const inputSx = {
    "& .MuiOutlinedInput-root": {
      minHeight: 58,
      borderRadius: 2.8,
      bgcolor: "#fff",
    },
    "& .MuiInputLabel-root": {
      fontWeight: 850,
      bgcolor: "#fff",
      px: 0.5,
    },
    "& .MuiInputBase-input": {
      fontWeight: 850,
    },
  };

  const definitionInputShellSx = {
    width: "100%",
    minHeight: 74,
    px: 1.45,
    py: 1.1,
    borderRadius: 3,
    bgcolor: alpha(BRAND.bg, 0.92),
    border: `1px solid ${alpha(BRAND.blue, 0.12)}`,
    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.8)",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    transition: "all .18s ease",
    "&:focus-within": {
      bgcolor: "#fff",
      borderColor: alpha(BRAND.blue2, 0.7),
      boxShadow: `0 0 0 3px ${alpha(BRAND.blue2, 0.1)}`,
    },
  };

  const DefinitionInput = ({ label, value, onChange, placeholder }) => (
    <Box sx={definitionInputShellSx}>
      <Typography
        variant="caption"
        sx={{
          display: "block",
          color: BRAND.muted,
          fontWeight: 950,
          lineHeight: 1,
          mb: 0.75,
        }}
      >
        {label}
      </Typography>

      <TextField
        fullWidth
        variant="standard"
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        autoComplete="off"
        InputProps={{
          disableUnderline: true,
        }}
        sx={{
          "& .MuiInputBase-root": {
            minHeight: 34,
          },
          "& .MuiInputBase-input": {
            p: 0,
            fontSize: "1rem",
            fontWeight: 900,
            color: BRAND.dark,
          },
          "& .MuiInputBase-input::placeholder": {
            color: alpha(BRAND.muted, 0.75),
            opacity: 1,
            fontWeight: 700,
          },
        }}
      />
    </Box>
  );

  return (
    <Grid
      container
      spacing={2.5}
      sx={{
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        overflow: "hidden",
        alignItems: "flex-start",
        "& > *": {
          minWidth: 0,
        },
        "& .MuiCard-root": {
          minWidth: 0,
          maxWidth: "100%",
          overflow: "hidden",
        },
      }}
    >
      <Grid item xs={12} lg={4}>
        <SectionCard
          title={TT.category}
          subtitle={TT.addCategory}
          icon={<CategoryIcon />}
        >
                    <Grid
            container
            spacing={1.15}
            sx={{
              mb: 1.7,
              width: "100%",
              minWidth: 0,
              alignItems: "stretch",
            }}
          >
            <Grid item xs={12} sm={10}>
              <DefinitionInput
                label={TT.categoryName}
                value={categoryName}
                onChange={(e) => setCategoryName(e.target.value)}
                placeholder="Örn: Hammadde"
              />
            </Grid>

            <Grid item xs={12} sm={2}>
              <Button
                fullWidth
                variant="contained"
                onClick={addCategory}
                sx={{
                  height: "100%",
                  minHeight: 74,
                  borderRadius: 3,
                  boxShadow: "0 10px 22px rgba(21,101,192,0.24)",
                }}
              >
                <AddIcon />
              </Button>
            </Grid>
          </Grid>

          <Stack
            spacing={1}
            sx={{
              width: "100%",
              minWidth: 0,
              maxHeight: 360,
              overflowY: "auto",
              pr: 0.5,
            }}
          >
            {categories.map((cat) => (
              <Chip
                key={cat.id}
                label={getTextValue(cat.name, lang)}
                sx={{
                  ...chipSx,
                  bgcolor: alpha(cat.color || BRAND.blue, 0.08),
                  color: cat.color || BRAND.blue,
                }}
              />
            ))}
          </Stack>
        </SectionCard>
      </Grid>

      <Grid item xs={12} lg={4}>
        <SectionCard
          title={TT.unit}
          subtitle={TT.addUnit}
          icon={<StraightenIcon />}
        >
                    <Grid
            container
            spacing={1.15}
            sx={{
              width: "100%",
              minWidth: 0,
              mb: 1.7,
              "& > *": {
                minWidth: 0,
              },
            }}
          >
            <Grid item xs={12}>
              <DefinitionInput
                label={TT.unitName}
                value={unitName}
                onChange={(e) => setUnitName(e.target.value)}
                placeholder="Örn: Kilogram"
              />
            </Grid>

            <Grid item xs={12}>
              <DefinitionInput
                label={TT.unitShort}
                value={unitShort}
                onChange={(e) => setUnitShort(e.target.value)}
                placeholder="Örn: kg"
              />
            </Grid>

            <Grid item xs={12}>
              <Button
                fullWidth
                variant="contained"
                onClick={addUnit}
                sx={{
                  minHeight: 54,
                  borderRadius: 3,
                  textTransform: "none",
                  fontWeight: 950,
                  boxShadow: "0 10px 22px rgba(21,101,192,0.24)",
                }}
              >
                {TT.addUnit}
              </Button>
            </Grid>
          </Grid>

          <Stack
            spacing={1}
            sx={{
              width: "100%",
              minWidth: 0,
              maxHeight: 360,
              overflowY: "auto",
              pr: 0.5,
            }}
          >
            {units.map((unit) => (
              <Chip
                key={unit.id}
                label={`${getTextValue(unit.name, lang)} (${unit.short})`}
                sx={chipSx}
              />
            ))}
          </Stack>
        </SectionCard>
      </Grid>

      <Grid item xs={12} lg={4}>
        <SectionCard
          title={TT.supplier}
          subtitle={TT.addSupplier}
          icon={<LocalShippingIcon />}
        >
                    <Grid
            container
            spacing={1.15}
            sx={{
              mb: 1.7,
              width: "100%",
              minWidth: 0,
              alignItems: "stretch",
            }}
          >
            <Grid item xs={12} sm={10}>
              <DefinitionInput
                label={TT.supplierName}
                value={supplierName}
                onChange={(e) => setSupplierName(e.target.value)}
                placeholder="Örn: Genel Tedarikçi"
              />
            </Grid>

            <Grid item xs={12} sm={2}>
              <Button
                fullWidth
                variant="contained"
                onClick={addSupplier}
                sx={{
                  height: "100%",
                  minHeight: 74,
                  borderRadius: 3,
                  boxShadow: "0 10px 22px rgba(21,101,192,0.24)",
                }}
              >
                <AddIcon />
              </Button>
            </Grid>
          </Grid>

          <Stack
            spacing={1}
            sx={{
              width: "100%",
              minWidth: 0,
              maxHeight: 360,
              overflowY: "auto",
              pr: 0.5,
            }}
          >
            {suppliers.map((supplier) => (
              <Chip
                key={supplier.id}
                label={supplier.name}
                icon={<BusinessIcon />}
                sx={chipSx}
              />
            ))}
          </Stack>
        </SectionCard>
      </Grid>
    </Grid>
  );
}



function CountingPanel({
  items,
  setItems,
  setMovements,
  TT,
  lang,
  settings,
  setSnackbar,
  serverMode = false,
}) {







    const [counts, setCounts] = useState({});
  const [savingCountId, setSavingCountId] = useState("");

  const applyOne = async (item) => {
    const previous = parseNumber(item.current_stock);
    const rawRealQty = counts[item.id];

    const realQty =
      rawRealQty === undefined || rawRealQty === null || rawRealQty === ""
        ? previous
        : parseNumber(rawRealQty);

    const diff = realQty - previous;
    const now = new Date().toISOString();

    setSavingCountId(String(item.id));

    const movement = createMovement({
      stock_id: item.id,
      stock_name: item.name,
      movement_type: "count_adjustment",
      quantity: realQty,
      previous_stock: previous,
      next_stock: realQty,
      unit_price: parseNumber(item.purchase_price),
      total_amount: diff * parseNumber(item.purchase_price),
      currency: item.currency || settings.defaultCurrency,
      description: "Stok sayımı düzeltmesi",
      created_at: now,
    });

    try {
      if (serverMode) {
        const saved = await apiPost(
          `/stock/items/${item.backend_id || item.id}/movement`,
          movement
        );

        if (saved?.item) {
          setItems((prev) =>
            prev.map((x) => {
              const same =
                String(x.id) === String(item.id) ||
                String(x.backend_id) === String(item.backend_id) ||
                String(x.id) === String(saved.item.id) ||
                String(x.backend_id) === String(saved.item.backend_id);

              return same ? { ...x, ...saved.item } : x;
            })
          );
        }

        if (saved?.movement) {
          setMovements((prev) => [saved.movement, ...prev]);
        }
      } else {
        setItems((prev) =>
          prev.map((x) =>
            x.id === item.id
              ? {
                  ...x,
                  current_stock: realQty,
                  updated_at: now,
                  last_movement_at: now,
                }
              : x
          )
        );

        setMovements((prev) => [movement, ...prev]);
      }

      setSnackbar({
        open: true,
        severity: "success",
        message: TT.movementSaved,
      });
    } catch (err) {
      setSnackbar({
        open: true,
        severity: "error",
        message: err.message || "Sayım kaydedilemedi",
      });
    } finally {
      setSavingCountId("");
    }
  };











  return (
    <SectionCard
      title={TT.counting}
      subtitle={TT.countAdjustment}
      icon={<FactCheckIcon />}
    >
      {items.length === 0 ? (
        <Alert severity="info" sx={{ borderRadius: 3 }}>
          {TT.noData}
        </Alert>
      ) : (
        <Grid container spacing={1.6}>
          {items
            .filter((item) => item.active)
            .slice(0, 80)
            .map((item) => {
              const previous = parseNumber(item.current_stock);
              const realQty = counts[item.id] === undefined ? previous : parseNumber(counts[item.id]);
              const diff = realQty - previous;

              const diffColor =
                diff > 0 ? BRAND.success : diff < 0 ? BRAND.danger : BRAND.muted;

              return (
                <Grid item xs={12} md={6} key={item.id}>
                  <Card
                    variant="outlined"
                    sx={{
                      borderRadius: 4,
                      p: { xs: 1.5, md: 1.9 },
                      borderColor: alpha(diffColor, diff === 0 ? 0.12 : 0.35),
                      bgcolor:
                        diff === 0
                          ? "#fff"
                          : alpha(diffColor, 0.055),
                    }}
                  >
                    <Stack spacing={1.5}>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="flex-start"
                        spacing={1.2}
                      >
                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            variant="subtitle1"
                            sx={{
                              fontWeight: 950,
                              color: BRAND.dark,
                              lineHeight: 1.2,
                            }}
                          >
                            {item.name}
                          </Typography>

                          <Typography
                            variant="caption"
                            sx={{
                              color: "text.secondary",
                              fontWeight: 750,
                            }}
                          >
                            {TT.unit}: {item.unit_id || "-"}
                          </Typography>
                        </Box>

                        <Chip
                          label={
                            diff === 0
                              ? "Fark yok"
                              : `${diff > 0 ? "+" : ""}${numberText(diff, lang, 2)}`
                          }
                          sx={{
                            borderRadius: 999,
                            fontWeight: 950,
                            bgcolor: alpha(diffColor, 0.12),
                            color: diffColor,
                          }}
                        />
                      </Stack>

                      <Grid container spacing={1.2} alignItems="stretch">
                        <Grid item xs={12} sm={4}>
                          <Box
                            sx={{
                              p: 1.4,
                              height: "100%",
                              borderRadius: 3,
                              bgcolor: alpha(BRAND.blue, 0.06),
                              border: `1px solid ${alpha(BRAND.blue, 0.12)}`,
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ color: "text.secondary", fontWeight: 850 }}
                            >
                              {TT.countSystemQty}
                            </Typography>

                            <Typography
                              variant="h5"
                              sx={{
                                mt: 0.5,
                                fontWeight: 950,
                                color: BRAND.blue,
                              }}
                            >
                              {numberText(previous, lang, 2)}
                            </Typography>
                          </Box>
                        </Grid>

                                                <Grid item xs={12} sm={4}>
                          <Box
                            sx={{
                              p: 1.4,
                              height: "100%",
                              minHeight: 82,
                              borderRadius: 3,
                              bgcolor: "#fff",
                              border: `1px solid ${alpha(BRAND.blue, 0.16)}`,
                              display: "flex",
                              flexDirection: "column",
                              justifyContent: "center",
                              overflow: "hidden",
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                color: "text.secondary",
                                fontWeight: 900,
                                mb: 0.4,
                              }}
                            >
                              {TT.countRealQty}
                            </Typography>

                            <TextField
                              fullWidth
                              variant="standard"
                              type="number"
                              value={counts[item.id] ?? previous}
                              onChange={(e) =>
                                setCounts((prev) => ({
                                  ...prev,
                                  [item.id]: e.target.value,
                                }))
                              }
                              InputProps={{
                                disableUnderline: true,
                              }}
                              inputProps={{
                                min: 0,
                                step: 1,
                                inputMode: "decimal",
                                style: {
                                  textAlign: "center",
                                },
                              }}
                              sx={{
                                "& .MuiInputBase-root": {
                                  minHeight: 34,
                                },
                                "& .MuiInputBase-input": {
                                  p: 0,
                                  textAlign: "center",
                                  fontWeight: 950,
                                  fontSize: "1.55rem",
                                  lineHeight: 1.1,
                                  color: BRAND.dark,
                                },
                                "& input[type=number]": {
                                  MozAppearance: "textfield",
                                },
                                "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button": {
                                  WebkitAppearance: "none",
                                  margin: 0,
                                },
                              }}
                            />
                          </Box>
                        </Grid>

                        <Grid item xs={12} sm={4}>
                          <Box
                            sx={{
                              p: 1.4,
                              height: "100%",
                              borderRadius: 3,
                              bgcolor: alpha(diffColor, 0.1),
                              border: `1px solid ${alpha(diffColor, 0.22)}`,
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ color: "text.secondary", fontWeight: 850 }}
                            >
                              {TT.countDiff}
                            </Typography>

                            <Typography
                              variant="h5"
                              sx={{
                                mt: 0.5,
                                fontWeight: 950,
                                color: diffColor,
                              }}
                            >
                              {diff > 0 ? "+" : ""}
                              {numberText(diff, lang, 2)}
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>

                                           <Button
                        fullWidth
                        variant="contained"
                        startIcon={
                          savingCountId === String(item.id) ? (
                            <SyncIcon />
                          ) : (
                            <FactCheckIcon />
                          )
                        }
                        onClick={() => applyOne(item)}
                        disabled={savingCountId === String(item.id)}


                        sx={{
                          minHeight: 50,
                          borderRadius: 3,
                          textTransform: "none",
                          fontWeight: 950,
                          bgcolor: diff === 0 ? BRAND.blue : diffColor,
                          "&:hover": {
                            bgcolor: diff === 0 ? BRAND.blue2 : diffColor,
                          },
                        }}
                      >
                                               {savingCountId === String(item.id)
                          ? "İşleniyor..."
                          : TT.applyCount}

                      </Button>
                    </Stack>
                  </Card>
                </Grid>
              );
            })}
        </Grid>
      )}
     </SectionCard>
  );
}

function MovementsPanel({ movements = [], items = [], TT, lang }) {
  const itemMap = useMemo(() => {
    const map = new Map();
    items.forEach((item) => {
      map.set(String(item.id), item);
    });
    return map;
  }, [items]);

  const sortedMovements = useMemo(
    () =>
      [...movements].sort(
        (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)
      ),
    [movements]
  );

  return (
    <SectionCard
      title={TT.movements}
      subtitle={TT.recentMovements}
      icon={<HistoryIcon />}
      action={
        <Chip
          icon={<ReceiptLongIcon />}
          label={`${sortedMovements.length} ${TT.records}`}
          sx={{
            borderRadius: 999,
            fontWeight: 900,
            bgcolor: alpha(BRAND.blue, 0.08),
            color: BRAND.blue,
          }}
        />
      }
    >
      {sortedMovements.length === 0 ? (
        <Alert severity="info" sx={{ borderRadius: 3 }}>
          {TT.noData}
        </Alert>
      ) : (
        <Stack spacing={1.2}>
          {sortedMovements.slice(0, 120).map((move) => {
            const meta = getMovementMeta(move.movement_type);
            const item = itemMap.get(String(move.stock_id));
            const stockName = move.stock_name || item?.name || "-";

            return (
              <Card
                key={move.id}
                variant="outlined"
                sx={{
                  borderRadius: 3,
                  borderColor: alpha(meta.color, 0.22),
                  bgcolor: alpha(meta.color, 0.035),
                }}
              >
                <CardContent sx={{ p: { xs: 1.5, md: 1.8 } }}>
                  <Stack
                    direction={{ xs: "column", md: "row" }}
                    spacing={1.4}
                    alignItems={{ xs: "flex-start", md: "center" }}
                    justifyContent="space-between"
                  >
                    <Stack direction="row" spacing={1.2} alignItems="center">
                      <Avatar
                        sx={{
                          width: 42,
                          height: 42,
                          bgcolor: alpha(meta.color, 0.13),
                          color: meta.color,
                        }}
                      >
                        {getIcon(meta.icon, { fontSize: 22 })}
                      </Avatar>

                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 950,
                            color: BRAND.dark,
                            overflowWrap: "anywhere",
                          }}
                        >
                          {stockName}
                        </Typography>

                        <Typography
                          variant="caption"
                          sx={{ color: "text.secondary", fontWeight: 750 }}
                        >
                          {dateText(move.created_at, lang)}
                        </Typography>
                      </Box>
                    </Stack>

                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={1}
                      alignItems={{ xs: "flex-start", sm: "center" }}
                      sx={{ width: { xs: "100%", md: "auto" } }}
                    >
                      <Chip
                        size="small"
                        label={getMovementLabel(move.movement_type, TT)}
                        sx={{
                          borderRadius: 999,
                          fontWeight: 900,
                          bgcolor: alpha(meta.color, 0.12),
                          color: meta.color,
                        }}
                      />

                      <Chip
                        size="small"
                        label={`${numberText(move.quantity, lang, 2)} ${TT.quantity}`}
                        sx={{ borderRadius: 999, fontWeight: 850 }}
                      />

                      <Chip
                        size="small"
                        label={moneyText(
                          move.total_amount,
                          move.currency || "TRY",
                          lang
                        )}
                        sx={{
                          borderRadius: 999,
                          fontWeight: 900,
                          bgcolor: alpha(BRAND.orange, 0.1),
                          color: BRAND.orange,
                        }}
                      />
                    </Stack>
                  </Stack>

                  {(move.description || move.staff_name || move.customer_name) && (
                    <Box
                      sx={{
                        mt: 1.2,
                        pt: 1.2,
                        borderTop: `1px dashed ${BRAND.border}`,
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          display: "block",
                          color: "text.secondary",
                          fontWeight: 750,
                          overflowWrap: "anywhere",
                        }}
                      >
                        {move.description || "-"}
                        {move.staff_name ? ` • ${TT.staff}: ${move.staff_name}` : ""}
                        {move.customer_name
                          ? ` • ${TT.customer}: ${move.customer_name}`
                          : ""}
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      )}
    </SectionCard>
  );
}


function ReportsPanel({ items = [], movements = [], TT, lang, categories = [] }) {
  const [period, setPeriod] = useState("month");

  const currency =
    items.find((item) => item.currency)?.currency ||
    movements.find((move) => move.currency)?.currency ||
    "TRY";

  const filteredMovements = useMemo(() => {
    const now = new Date();

    return movements.filter((move) => {
      if (period === "all") return true;

      const d = new Date(move.created_at);

      if (Number.isNaN(d.getTime())) return false;

      const diffDays = Math.floor((now - d) / (1000 * 60 * 60 * 24));
      const dayKey = d.toISOString().slice(0, 10);
      const month = d.toISOString().slice(0, 7);

      if (period === "today") return dayKey === todayKey();
      if (period === "week") return diffDays <= 7;
      if (period === "month") return month === monthKey();

      return true;
    });
  }, [movements, period]);

  const saleMovements = useMemo(
    () => filteredMovements.filter((move) => move.movement_type === "sale"),
    [filteredMovements]
  );

  const stockInMovements = useMemo(
    () =>
      filteredMovements.filter((move) =>
        ["in", "return", "count_adjustment"].includes(move.movement_type)
      ),
    [filteredMovements]
  );

  const stockOutMovements = useMemo(
    () =>
      filteredMovements.filter((move) =>
        ["out", "waste", "transfer", "production"].includes(move.movement_type)
      ),
    [filteredMovements]
  );

    const activeItems = useMemo(
    () => items.filter((item) => item.active !== false),
    [items]
  );

  const totalStockValue = useMemo(
    () => activeItems.reduce((sum, item) => sum + calcStockValue(item), 0),
    [activeItems]
  );

  const potentialSaleValue = useMemo(
    () => activeItems.reduce((sum, item) => sum + calcSaleValue(item), 0),
    [activeItems]
  );

  const potentialProfit = potentialSaleValue - totalStockValue;

  const totalSales = saleMovements.reduce(
    (sum, move) => sum + parseNumber(move.total_amount),
    0
  );

  const totalStockInAmount = stockInMovements.reduce(
    (sum, move) => sum + Math.abs(parseNumber(move.total_amount)),
    0
  );

  const totalStockOutAmount = stockOutMovements.reduce(
    (sum, move) => sum + Math.abs(parseNumber(move.total_amount)),
    0
  );



  const outItems = activeItems.filter((item) => getStockStatus(item) === "out");
  const criticalItems = activeItems.filter(
    (item) => getStockStatus(item) === "critical"
  );
  const overItems = activeItems.filter((item) => getStockStatus(item) === "over");
  const normalItems = activeItems.filter(
    (item) => getStockStatus(item) === "normal"
  );

  const stockHealth =
    activeItems.length > 0
      ? Math.round((normalItems.length / activeItems.length) * 100)
      : 0;

  const byCategory = useMemo(() => {
    const map = new Map();

    activeItems.forEach((item) => {
      const key = item.category_id || "none";

      const prev = map.get(key) || {
        category_id: key,
        count: 0,
        value: 0,
        saleValue: 0,
        profit: 0,
        critical: 0,
        out: 0,
      };

      const value = calcStockValue(item);
      const saleValue = calcSaleValue(item);
      const status = getStockStatus(item);

      prev.count += 1;
      prev.value += value;
      prev.saleValue += saleValue;
      prev.profit += saleValue - value;

      if (status === "critical") prev.critical += 1;
      if (status === "out") prev.out += 1;

      map.set(key, prev);
    });

    return Array.from(map.values()).sort((a, b) => b.value - a.value);
  }, [activeItems]);

  const topValueItems = useMemo(
    () =>
      [...activeItems]
        .sort((a, b) => calcStockValue(b) - calcStockValue(a))
        .slice(0, 10),
    [activeItems]
  );

  const mostCriticalItems = useMemo(
    () =>
      [...criticalItems, ...outItems]
        .sort((a, b) => parseNumber(a.current_stock) - parseNumber(b.current_stock))
        .slice(0, 10),
    [criticalItems, outItems]
  );

  const salesByProduct = useMemo(() => {
    const map = new Map();

    saleMovements.forEach((move) => {
      const key = String(move.stock_id || move.stock_name || "unknown");
      const item = items.find((x) => String(x.id) === key);

      const prev = map.get(key) || {
        name: move.stock_name || item?.name || "-",
        qty: 0,
        amount: 0,
        currency: move.currency || currency,
      };

      prev.qty += parseNumber(move.quantity);
      prev.amount += parseNumber(move.total_amount);

      map.set(key, prev);
    });

    return Array.from(map.values())
      .sort((a, b) => b.amount - a.amount)
      .slice(0, 10);
  }, [saleMovements, items, currency]);

  const customerRows = useMemo(() => {
    const map = new Map();

    saleMovements.forEach((move) => {
      const name = String(move.customer_name || "").trim() || "Müşteri belirtilmedi";

      const prev = map.get(name) || {
        name,
        count: 0,
        qty: 0,
        amount: 0,
      };

      prev.count += 1;
      prev.qty += parseNumber(move.quantity);
      prev.amount += parseNumber(move.total_amount);

      map.set(name, prev);
    });

    return Array.from(map.values())
      .sort((a, b) => b.amount - a.amount)
      .slice(0, 10);
  }, [saleMovements]);

  const movementTypeRows = useMemo(() => {
    return MOVEMENT_TYPES.map((type) => {
      const rows = filteredMovements.filter(
        (move) => move.movement_type === type.value
      );

      return {
        ...type,
        label: TT[type.labelKey],
        count: rows.length,
        qty: rows.reduce((sum, move) => sum + parseNumber(move.quantity), 0),
        amount: rows.reduce(
          (sum, move) => sum + Math.abs(parseNumber(move.total_amount)),
          0
        ),
      };
    }).filter((row) => row.count > 0);
  }, [filteredMovements, TT]);

  const slowMovingItems = useMemo(() => {
    const soldNames = new Set(
      saleMovements.map((move) => safeLower(move.stock_name))
    );

    return activeItems
      .filter((item) => {
        const soldQty = parseNumber(item.total_sold_qty);
        const name = safeLower(item.name);

        return soldQty <= 0 && !soldNames.has(name);
      })
      .sort((a, b) => calcStockValue(b) - calcStockValue(a))
      .slice(0, 10);
  }, [activeItems, saleMovements]);

  const exportReportCsv = () => {
    const rows = [
      ["Başlık", "Değer", "Açıklama"],
      ["Dönem", period, ""],
      ["Toplam stok kartı", activeItems.length, ""],
      ["Toplam stok değeri", numberText(totalStockValue, lang, 2), currency],
      ["Satış potansiyeli", numberText(potentialSaleValue, lang, 2), currency],
      ["Tahmini kâr", numberText(potentialProfit, lang, 2), currency],
      ["Dönem satış cirosu", numberText(totalSales, lang, 2), currency],
      ["Stok giriş tutarı", numberText(totalStockInAmount, lang, 2), currency],
      ["Stok çıkış/fire tutarı", numberText(totalStockOutAmount, lang, 2), currency],
      ["Kritik stok", criticalItems.length, ""],
      ["Tükenen stok", outItems.length, ""],
      [],
      ["En çok satan ürünler", "Miktar", "Tutar"],
      ...salesByProduct.map((row) => [
        row.name,
        numberText(row.qty, lang, 2),
        numberText(row.amount, lang, 2),
      ]),
      [],
      ["En çok alış yapan müşteriler", "İşlem", "Tutar"],
      ...customerRows.map((row) => [
        row.name,
        row.count,
        numberText(row.amount, lang, 2),
      ]),
      [],
      ["Kategori", "Kart", "Stok Değeri"],
      ...byCategory.map((row) => [
        getCategoryName(categories, row.category_id, lang),
        row.count,
        numberText(row.value, lang, 2),
      ]),
    ];

    downloadCsv(`stok-profesyonel-rapor-${todayKey()}.csv`, rows);
  };

  const printReport = () => {
    const periodLabel =
      period === "today"
        ? "Günlük"
        : period === "week"
        ? "Haftalık"
        : period === "month"
        ? "Aylık"
        : "Tüm Zamanlar";

    const html = `
      <html>
        <head>
          <title>Stok Profesyonel Rapor</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              padding: 26px;
              color: #0F172A;
            }

            h1 {
              margin: 0;
              font-size: 24px;
              color: #0B4D8B;
            }

            .sub {
              color: #64748B;
              margin-top: 6px;
              margin-bottom: 18px;
            }

            .grid {
              display: grid;
              grid-template-columns: repeat(4, 1fr);
              gap: 12px;
              margin-bottom: 18px;
            }

            .box {
              border: 1px solid #E2E8F0;
              border-radius: 14px;
              padding: 13px;
              background: #F8FAFC;
            }

            .label {
              color: #64748B;
              font-size: 11px;
              font-weight: bold;
            }

            .value {
              margin-top: 5px;
              font-size: 18px;
              font-weight: 900;
            }

            table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 12px;
              margin-bottom: 22px;
              font-size: 12px;
            }

            th {
              background: #0B4D8B;
              color: white;
              text-align: left;
              padding: 9px;
            }

            td {
              border-bottom: 1px solid #E2E8F0;
              padding: 8px;
            }

            h2 {
              font-size: 16px;
              margin-top: 18px;
              color: #F58220;
            }

            @media print {
              .no-print { display: none; }
              body { padding: 10px; }
            }
          </style>
        </head>

        <body>
          <h1>Stok Profesyonel Rapor</h1>
          <div class="sub">
            Dönem: ${periodLabel} · Rapor Tarihi: ${new Date().toLocaleString("tr-TR")}
          </div>

          <div class="grid">
            <div class="box">
              <div class="label">Stok Değeri</div>
              <div class="value">${moneyText(totalStockValue, currency, lang)}</div>
            </div>

            <div class="box">
              <div class="label">Satış Cirosu</div>
              <div class="value">${moneyText(totalSales, currency, lang)}</div>
            </div>

            <div class="box">
              <div class="label">Tahmini Kâr</div>
              <div class="value">${moneyText(potentialProfit, currency, lang)}</div>
            </div>

            <div class="box">
              <div class="label">Stok Sağlığı</div>
              <div class="value">%${stockHealth}</div>
            </div>
          </div>

          <h2>En Çok Satan Ürünler</h2>
          <table>
            <thead>
              <tr>
                <th>Ürün</th>
                <th>Miktar</th>
                <th>Tutar</th>
              </tr>
            </thead>
            <tbody>
              ${salesByProduct
                .map(
                  (row) => `
                    <tr>
                      <td>${row.name}</td>
                      <td>${numberText(row.qty, lang, 2)}</td>
                      <td>${moneyText(row.amount, row.currency || currency, lang)}</td>
                    </tr>
                  `
                )
                .join("")}
            </tbody>
          </table>

          <h2>En Çok Alış Yapan Müşteriler</h2>
          <table>
            <thead>
              <tr>
                <th>Müşteri</th>
                <th>İşlem</th>
                <th>Tutar</th>
              </tr>
            </thead>
            <tbody>
              ${customerRows
                .map(
                  (row) => `
                    <tr>
                      <td>${row.name}</td>
                      <td>${row.count}</td>
                      <td>${moneyText(row.amount, currency, lang)}</td>
                    </tr>
                  `
                )
                .join("")}
            </tbody>
          </table>

          <h2>Kategori Bazlı Stok Değeri</h2>
          <table>
            <thead>
              <tr>
                <th>Kategori</th>
                <th>Kart</th>
                <th>Stok Değeri</th>
                <th>Kritik</th>
                <th>Tükenen</th>
              </tr>
            </thead>
            <tbody>
              ${byCategory
                .map(
                  (row) => `
                    <tr>
                      <td>${getCategoryName(categories, row.category_id, lang)}</td>
                      <td>${row.count}</td>
                      <td>${moneyText(row.value, currency, lang)}</td>
                      <td>${row.critical}</td>
                      <td>${row.out}</td>
                    </tr>
                  `
                )
                .join("")}
            </tbody>
          </table>
        </body>
      </html>
    `;

    const win = window.open("", "_blank", "width=1200,height=850");

    if (!win) return;

    win.document.write(html);
    win.document.close();
    win.focus();
    win.print();
  };

  return (
    <Grid container spacing={2.5}>
      <Grid item xs={12}>
        <SectionCard
          title="Profesyonel Stok Raporu"
          subtitle="Stok değeri, satış cirosu, kritik ürünler, müşteri bazlı satış ve kategori analizini tek ekranda görün."
          icon={<BarChartIcon />}
          action={
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Dönem</InputLabel>

                <Select
                  label="Dönem"
                  value={period}
                  onChange={(e) => setPeriod(e.target.value)}
                  sx={{
                    borderRadius: 2.4,
                    fontWeight: 850,
                    bgcolor: "#fff",
                  }}
                >
                  <MenuItem value="today">Günlük</MenuItem>
                  <MenuItem value="week">Haftalık</MenuItem>
                  <MenuItem value="month">Aylık</MenuItem>
                  <MenuItem value="all">Tüm Zamanlar</MenuItem>
                </Select>
              </FormControl>

              <Button
                variant="outlined"
                startIcon={<FileDownloadIcon />}
                onClick={exportReportCsv}
                sx={{
                  borderRadius: 2.4,
                  textTransform: "none",
                  fontWeight: 900,
                }}
              >
                CSV
              </Button>

              <Button
                variant="contained"
                startIcon={<ReceiptLongIcon />}
                onClick={printReport}
                sx={{
                  borderRadius: 2.4,
                  textTransform: "none",
                  fontWeight: 900,
                  bgcolor: BRAND.blue,
                  "&:hover": {
                    bgcolor: BRAND.blue2,
                  },
                }}
              >
                PDF / Yazdır
              </Button>
            </Stack>
          }
        >
          <Grid container spacing={1.6}>
            <Grid item xs={12} sm={6} lg={3}>
              <SummaryCard
                title="Stok Değeri"
                value={moneyText(totalStockValue, currency, lang)}
                subtitle={`${activeItems.length} aktif stok kartı`}
                icon={<InventoryIcon />}
                tone="blue"
                progress={stockHealth}
              />
            </Grid>

            <Grid item xs={12} sm={6} lg={3}>
              <SummaryCard
                title="Satış Cirosu"
                value={moneyText(totalSales, currency, lang)}
                subtitle={`${saleMovements.length} satış hareketi`}
                icon={<PointOfSaleIcon />}
                tone="orange"
              />
            </Grid>

            <Grid item xs={12} sm={6} lg={3}>
              <SummaryCard
                title="Tahmini Kâr"
                value={moneyText(potentialProfit, currency, lang)}
                subtitle={`Satış potansiyeli: ${moneyText(
                  potentialSaleValue,
                  currency,
                  lang
                )}`}
                icon={<TrendingUpIcon />}
                tone={potentialProfit >= 0 ? "success" : "danger"}
              />
            </Grid>

            <Grid item xs={12} sm={6} lg={3}>
              <SummaryCard
                title="Riskli Stok"
                value={`${criticalItems.length + outItems.length}`}
                subtitle={`Kritik: ${criticalItems.length} · Tükenen: ${outItems.length}`}
                icon={<WarningAmberIcon />}
                tone={
                  criticalItems.length + outItems.length > 0 ? "warning" : "success"
                }
              />
            </Grid>
          </Grid>
        </SectionCard>
      </Grid>

      <Grid item xs={12} lg={8}>
        <SectionCard
          title="Satış Performansı"
          subtitle="Dönem bazlı en çok satan ürünler ve ciro katkısı."
          icon={<SellIcon />}
        >
          {salesByProduct.length === 0 ? (
            <Alert severity="info" sx={{ borderRadius: 3 }}>
              Bu dönemde satış hareketi bulunamadı.
            </Alert>
          ) : (
            <Stack spacing={1}>
              {salesByProduct.map((row, index) => {
                const ratio = totalSales > 0 ? (row.amount / totalSales) * 100 : 0;

                return (
                  <Card
                    key={`${row.name}-${index}`}
                    variant="outlined"
                    sx={{
                      borderRadius: 3,
                      p: 1.4,
                      borderColor: alpha(BRAND.orange, 0.18),
                    }}
                  >
                    <Stack spacing={1}>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        spacing={1.2}
                      >
                        <Stack direction="row" spacing={1.2} alignItems="center">
                          <Avatar
                            sx={{
                              width: 36,
                              height: 36,
                              bgcolor: alpha(BRAND.orange, 0.12),
                              color: BRAND.orange,
                              fontWeight: 950,
                            }}
                          >
                            {index + 1}
                          </Avatar>

                          <Box>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 950, color: BRAND.dark }}
                            >
                              {row.name}
                            </Typography>

                            <Typography
                              variant="caption"
                              sx={{ color: "text.secondary" }}
                            >
                              {TT.quantity}: {numberText(row.qty, lang, 2)}
                            </Typography>
                          </Box>
                        </Stack>

                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 950,
                            color: BRAND.orange,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {moneyText(row.amount, row.currency || currency, lang)}
                        </Typography>
                      </Stack>

                      <LinearProgress
                        variant="determinate"
                        value={Math.min(100, Math.max(0, ratio))}
                        sx={{
                          height: 8,
                          borderRadius: 999,
                          bgcolor: alpha(BRAND.orange, 0.12),
                          "& .MuiLinearProgress-bar": {
                            borderRadius: 999,
                            bgcolor: BRAND.orange,
                          },
                        }}
                      />
                    </Stack>
                  </Card>
                );
              })}
            </Stack>
          )}
        </SectionCard>
      </Grid>

      <Grid item xs={12} lg={4}>
        <SectionCard
          title="Müşteri Satış Analizi"
          subtitle="En çok alış yapan müşteriler."
          icon={<PersonIcon />}
        >
          {customerRows.length === 0 ? (
            <Alert severity="info" sx={{ borderRadius: 3 }}>
              Bu dönemde müşteri bağlantılı satış bulunamadı.
            </Alert>
          ) : (
            <Stack spacing={1}>
              {customerRows.map((row, index) => (
                <ReportLine
                  key={`${row.name}-${index}`}
                  index={index + 1}
                  title={row.name}
                  subtitle={`${row.count} işlem · ${TT.quantity}: ${numberText(
                    row.qty,
                    lang,
                    2
                  )}`}
                  value={moneyText(row.amount, currency, lang)}
                  color={BRAND.blue}
                />
              ))}
            </Stack>
          )}
        </SectionCard>
      </Grid>

      <Grid item xs={12} lg={6}>
        <SectionCard
          title="Kategori Bazlı Stok"
          subtitle="Kategoriye göre stok değeri, potansiyel satış ve risk."
          icon={<CategoryIcon />}
        >
          {byCategory.length === 0 ? (
            <Alert severity="info" sx={{ borderRadius: 3 }}>
              {TT.noData}
            </Alert>
          ) : (
            <Stack spacing={1}>
              {byCategory.map((row, index) => (
                <Card
                  key={row.category_id}
                  variant="outlined"
                  sx={{
                    borderRadius: 3,
                    p: 1.35,
                    borderColor: alpha(BRAND.success, 0.18),
                  }}
                >
                  <Stack spacing={1}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      spacing={1.2}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 950,
                            color: BRAND.dark,
                          }}
                        >
                          {getCategoryName(categories, row.category_id, lang)}
                        </Typography>

                        <Typography
                          variant="caption"
                          sx={{ color: "text.secondary" }}
                        >
                          {row.count} {TT.record} · Kritik: {row.critical} · Tükenen:{" "}
                          {row.out}
                        </Typography>
                      </Box>

                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 950,
                          color: BRAND.success,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {moneyText(row.value, currency, lang)}
                      </Typography>
                    </Stack>

                    <LinearProgress
                      variant="determinate"
                      value={
                        totalStockValue > 0
                          ? Math.min(100, (row.value / totalStockValue) * 100)
                          : 0
                      }
                      sx={{
                        height: 7,
                        borderRadius: 999,
                        bgcolor: alpha(BRAND.success, 0.1),
                        "& .MuiLinearProgress-bar": {
                          borderRadius: 999,
                          bgcolor: BRAND.success,
                        },
                      }}
                    />
                  </Stack>
                </Card>
              ))}
            </Stack>
          )}
        </SectionCard>
      </Grid>

      <Grid item xs={12} lg={6}>
        <SectionCard
          title="Stok Risk ve Hareketsiz Ürünler"
          subtitle="Tükenen, kritik ve uzun süredir satılmayan ürünleri izleyin."
          icon={<WarningAmberIcon />}
        >
          <Grid container spacing={1.2} sx={{ mb: 1.5 }}>
            <Grid item xs={12} sm={4}>
              <Card
                variant="outlined"
                sx={{
                  borderRadius: 3,
                  p: 1.4,
                  bgcolor: alpha(BRAND.danger, 0.06),
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ color: "text.secondary", fontWeight: 850 }}
                >
                  Tükenen
                </Typography>

                <Typography
                  variant="h5"
                  sx={{ fontWeight: 950, color: BRAND.danger }}
                >
                  {outItems.length}
                </Typography>
              </Card>
            </Grid>

            <Grid item xs={12} sm={4}>
              <Card
                variant="outlined"
                sx={{
                  borderRadius: 3,
                  p: 1.4,
                  bgcolor: alpha(BRAND.warning, 0.08),
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ color: "text.secondary", fontWeight: 850 }}
                >
                  Kritik
                </Typography>

                <Typography
                  variant="h5"
                  sx={{ fontWeight: 950, color: BRAND.warning }}
                >
                  {criticalItems.length}
                </Typography>
              </Card>
            </Grid>

            <Grid item xs={12} sm={4}>
              <Card
                variant="outlined"
                sx={{
                  borderRadius: 3,
                  p: 1.4,
                  bgcolor: alpha(BRAND.info, 0.08),
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ color: "text.secondary", fontWeight: 850 }}
                >
                  Fazla Stok
                </Typography>

                <Typography
                  variant="h5"
                  sx={{ fontWeight: 950, color: BRAND.info }}
                >
                  {overItems.length}
                </Typography>
              </Card>
            </Grid>
          </Grid>

          <Stack spacing={1}>
            {mostCriticalItems.slice(0, 6).map((item, index) => (
              <ReportLine
                key={item.id}
                index={index + 1}
                title={item.name}
                subtitle={`${TT.currentStock}: ${numberText(
                  item.current_stock,
                  lang,
                  2
                )} · ${TT.minStock}: ${numberText(item.min_stock, lang, 2)}`}
                value={getStockStatusLabel(getStockStatus(item), TT)}
                color={
                  getStockStatus(item) === "out" ? BRAND.danger : BRAND.warning
                }
              />
            ))}

            {mostCriticalItems.length === 0 && (
              <Alert severity="success" sx={{ borderRadius: 3 }}>
                Kritik veya tükenen stok bulunmuyor.
              </Alert>
            )}
          </Stack>
        </SectionCard>
      </Grid>

      <Grid item xs={12} lg={6}>
        <SectionCard
          title="Hareket Tipi Analizi"
          subtitle="Stok giriş, çıkış, fire, iade ve satış dağılımı."
          icon={<HistoryIcon />}
        >
          {movementTypeRows.length === 0 ? (
            <Alert severity="info" sx={{ borderRadius: 3 }}>
              {TT.noData}
            </Alert>
          ) : (
            <Stack spacing={1}>
              {movementTypeRows.map((row, index) => (
                <Card
                  key={row.value}
                  variant="outlined"
                  sx={{
                    borderRadius: 3,
                    p: 1.35,
                    borderColor: alpha(row.color, 0.22),
                  }}
                >
                  <Stack
                    direction="row"
                    spacing={1.2}
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Stack direction="row" spacing={1.2} alignItems="center">
                      <Avatar
                        sx={{
                          bgcolor: alpha(row.color, 0.12),
                          color: row.color,
                        }}
                      >
                        {getIcon(row.icon)}
                      </Avatar>

                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 950 }}>
                          {row.label}
                        </Typography>

                        <Typography
                          variant="caption"
                          sx={{ color: "text.secondary" }}
                        >
                          {row.count} işlem · {TT.quantity}:{" "}
                          {numberText(row.qty, lang, 2)}
                        </Typography>
                      </Box>
                    </Stack>

                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 950,
                        color: row.color,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {moneyText(row.amount, currency, lang)}
                    </Typography>
                  </Stack>
                </Card>
              ))}
            </Stack>
          )}
        </SectionCard>
      </Grid>

      <Grid item xs={12} lg={6}>
        <SectionCard
          title="Hareketsiz Stok"
          subtitle="Satış hareketi olmayan ama stok değeri taşıyan ürünler."
          icon={<LowPriorityIcon />}
        >
          {slowMovingItems.length === 0 ? (
            <Alert severity="success" sx={{ borderRadius: 3 }}>
              Hareketsiz stok görünmüyor.
            </Alert>
          ) : (
            <Stack spacing={1}>
              {slowMovingItems.map((item, index) => (
                <ReportLine
                  key={item.id}
                  index={index + 1}
                  title={item.name}
                  subtitle={`${TT.currentStock}: ${numberText(
                    item.current_stock,
                    lang,
                    2
                  )}`}
                  value={moneyText(calcStockValue(item), item.currency, lang)}
                  color={BRAND.muted}
                />
              ))}
            </Stack>
          )}
        </SectionCard>
      </Grid>

      <Grid item xs={12}>
        <SectionCard
          title="Son Hareket Özeti"
          subtitle="Seçili dönemdeki son stok hareketleri."
          icon={<ReceiptLongIcon />}
        >
          {filteredMovements.length === 0 ? (
            <Alert severity="info" sx={{ borderRadius: 3 }}>
              {TT.noData}
            </Alert>
          ) : (
            <Stack spacing={1}>
              {filteredMovements.slice(0, 12).map((move, index) => {
                const meta = getMovementMeta(move.movement_type);

                return (
                  <ReportLine
                    key={move.id}
                    index={index + 1}
                    title={move.stock_name}
                    subtitle={`${getMovementLabel(move.movement_type, TT)} · ${dateText(
                      move.created_at,
                      lang
                    )} · ${TT.quantity}: ${numberText(move.quantity, lang, 2)}`}
                    value={moneyText(move.total_amount, move.currency, lang)}
                    color={meta.color}
                  />
                );
              })}
            </Stack>
          )}
        </SectionCard>
      </Grid>
    </Grid>
  );
}

function ReportLine({ index, title, subtitle, value, color }) {
  return (
    <Card variant="outlined" sx={{ borderRadius: 3, p: 1.2 }}>
      <Stack direction="row" spacing={1.2} alignItems="center">
        <Avatar
          sx={{
            width: 32,
            height: 32,
            bgcolor: alpha(color, 0.12),
            color,
            fontSize: 14,
            fontWeight: 950,
          }}
        >
          {index}
        </Avatar>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 950,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {title || "-"}
          </Typography>

          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {subtitle || "-"}
          </Typography>
        </Box>

        <Typography variant="body2" sx={{ fontWeight: 950, color, whiteSpace: "nowrap" }}>
          {value}
        </Typography>
      </Stack>
    </Card>
  );
}

function SettingsPanel({ settings, setSettings, TT }) {
  const handle = (field, value) => {
    setSettings((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const rowsPerPage = Number(settings.rowsPerPage || 12);

  const handleRowsPerPage = (value) => {
    const n = Math.max(6, Math.min(60, Number(value || 12)));
    handle("rowsPerPage", n);
  };

  const SettingShell = ({ icon, title, text, color = BRAND.blue, children }) => (
    <Card
      variant="outlined"
      sx={{
        height: "100%",
        borderRadius: 4,
        borderColor: alpha(color, 0.18),
        background: `linear-gradient(180deg, #fff 0%, ${alpha(color, 0.045)} 100%)`,
        boxShadow: "0 16px 38px rgba(15,23,42,0.06)",
      }}
    >
      <CardContent sx={{ p: { xs: 1.8, md: 2.2 } }}>
        <Stack direction="row" spacing={1.3} alignItems="flex-start" sx={{ mb: 1.7 }}>
          <Avatar
            sx={{
              bgcolor: alpha(color, 0.12),
              color,
              width: 42,
              height: 42,
            }}
          >
            {icon}
          </Avatar>

          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 950, letterSpacing: -0.35 }}>
              {title}
            </Typography>

            <Typography
              variant="body2"
              sx={{ mt: 0.35, color: "text.secondary", lineHeight: 1.55 }}
            >
              {text}
            </Typography>
          </Box>
        </Stack>

        <Stack spacing={1.25}>{children}</Stack>
      </CardContent>
    </Card>
  );

  const SettingSwitch = ({ checked, onChange, title, text }) => (
    <Box
      sx={{
        p: 1.35,
        borderRadius: 3,
        bgcolor: alpha("#fff", 0.82),
        border: `1px solid ${BRAND.border}`,
      }}
    >
      <FormControlLabel
        sx={{
          m: 0,
          width: "100%",
          alignItems: "flex-start",
          ".MuiFormControlLabel-label": { width: "100%" },
        }}
        control={
          <Switch
            checked={Boolean(checked)}
            onChange={(e) => onChange(e.target.checked)}
          />
        }
        label={
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 950, color: BRAND.ink }}>
              {title}
            </Typography>

            <Typography
              variant="caption"
              sx={{ display: "block", mt: 0.25, color: "text.secondary", lineHeight: 1.45 }}
            >
              {text}
            </Typography>
          </Box>
        }
      />
    </Box>
  );

  return (
    <Stack spacing={2.5}>
      <SectionCard
        title={TT.settings}
        subtitle={TT.settingsSubtitle}
        icon={<SettingsIcon />}
        sx={{
          background:
            "linear-gradient(135deg, rgba(11,77,139,0.05) 0%, rgba(245,130,32,0.06) 100%)",
        }}
      >
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <SettingShell
              icon={<ShieldIconFallback />}
              title={TT.stockRules}
              text={TT.stockRulesText}
              color={BRAND.warning}
            >
              <SettingSwitch
                checked={settings.allowNegativeStock}
                onChange={(value) => handle("allowNegativeStock", value)}
                title={TT.allowNegativeStock}
                text={TT.negativeStockText}
              />

              <SettingSwitch
                checked={settings.autoCriticalWarning}
                onChange={(value) => handle("autoCriticalWarning", value)}
                title={TT.autoCriticalWarning}
                text={TT.criticalWarningText}
              />
            </SettingShell>
          </Grid>

          <Grid item xs={12} md={4}>
            <SettingShell
              icon={<PaymentsIcon />}
              title={TT.salesAndFinance}
              text={TT.salesAndFinanceText}
              color={BRAND.success}
            >
              <SettingSwitch
                checked={settings.autoCashTransfer}
                onChange={(value) => handle("autoCashTransfer", value)}
                title={TT.autoCashTransfer}
                text={TT.cashTransferText}
              />

              <TextField
                fullWidth
                size="small"
                label={TT.defaultCurrency}
                value={settings.defaultCurrency || "TRY"}
                onChange={(e) =>
                  handle("defaultCurrency", String(e.target.value || "TRY").toUpperCase())
                }
                helperText={TT.currencyText}
              />
            </SettingShell>
          </Grid>

          <Grid item xs={12} md={4}>
            <SettingShell
              icon={<TuneIcon />}
              title={TT.screenPreferences}
              text={TT.screenPreferencesText}
              color={BRAND.blue}
            >
              <FormControl fullWidth size="small">
                <InputLabel>{TT.pageRows}</InputLabel>
                <Select
                  label={TT.pageRows}
                  value={rowsPerPage}
                  onChange={(e) => handleRowsPerPage(e.target.value)}
                >
                  {[6, 12, 18, 24, 36, 48, 60].map((n) => (
                    <MenuItem key={n} value={n}>
                      {n}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Typography
                variant="caption"
                sx={{ mt: -0.5, color: "text.secondary", lineHeight: 1.45 }}
              >
                {TT.rowsPerPageText}
              </Typography>

              <SettingSwitch
                checked={settings.showPassive}
                onChange={(value) => handle("showPassive", value)}
                title={TT.passiveRecords}
                text={TT.passiveRecordsText}
              />

              <SettingSwitch
                checked={settings.mobileCardView}
                onChange={(value) => handle("mobileCardView", value)}
                title={TT.mobileCardViewLabel}
                text={TT.mobileCardViewText}
              />
            </SettingShell>
          </Grid>
        </Grid>

        <Alert
          severity="info"
          icon={<InfoOutlinedIcon />}
          sx={{
            mt: 2.4,
            borderRadius: 3,
            border: `1px solid ${alpha(BRAND.info, 0.18)}`,
            bgcolor: alpha(BRAND.info, 0.07),
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 900 }}>
            {TT.settingsAutoSaveTitle}
          </Typography>
          <Typography variant="caption" sx={{ display: "block", mt: 0.25 }}>
            {TT.settingsAutoSaveText} {TT.savedLocal}
          </Typography>
        </Alert>
      </SectionCard>
    </Stack>
  );
}

function ShieldIconFallback() {
  return <FactCheckIcon />;
}



function HelpPanel({ TT }) {
  const [openItem, setOpenItem] = useState("start");

  const flowItems = [
    {
      id: "start",
      icon: <InventoryIcon />,
      title: TT.helpStartTitle,
      text: TT.helpStartText,
      color: BRAND.blue,
    },
    {
      id: "sale",
      icon: <PointOfSaleIcon />,
      title: TT.helpSaleTitle,
      text: TT.helpSaleText,
      color: BRAND.orange,
    },
    {
      id: "count",
      icon: <FactCheckIcon />,
      title: TT.helpCountTitle,
      text: TT.helpCountText,
      color: BRAND.success,
    },
    {
      id: "report",
      icon: <BarChartIcon />,
      title: TT.helpReportTitle,
      text: TT.helpReportText,
      color: BRAND.info,
    },
  ];

  const businessCards = [
    {
      icon: <RestaurantIcon />,
      title: TT.restaurant,
      text: TT.helpRestaurantText,
      color: BRAND.orange,
    },
    {
      icon: <StorefrontIcon />,
      title: TT.cafe,
      text: TT.helpCafeText,
      color: BRAND.blue,
    },
    {
      icon: <SpaIcon />,
      title: TT.beauty,
      text: TT.helpBeautyText,
      color: "#EC4899",
    },
    {
      icon: <ScienceIcon />,
      title: TT.clinic,
      text: TT.helpClinicText,
      color: BRAND.success,
    },
    {
      icon: <ShoppingCartIcon />,
      title: TT.templateMarket,
      text: TT.helpRetailText,
      color: BRAND.dark,
    },
  ];

  const faqItems = [
    {
      id: "stock-card",
      title: TT.helpFaqStockCardTitle,
      text: TT.helpFaqStockCardText,
    },
    {
      id: "critical",
      title: TT.helpFaqCriticalTitle,
      text: TT.helpFaqCriticalText,
    },
    {
      id: "counting",
      title: TT.helpFaqCountingTitle,
      text: TT.helpFaqCountingText,
    },
    {
      id: "quick-sale",
      title: TT.helpFaqSaleTitle,
      text: TT.helpFaqSaleText,
    },
    {
      id: "barcode",
      title: TT.helpFaqBarcodeTitle,
      text: TT.helpFaqBarcodeText,
    },
    {
      id: "cash",
      title: TT.helpFaqCashTitle,
      text: TT.helpFaqCashText,
    },
  ];

  const AccordionCard = ({ item }) => {
    const open = openItem === item.id;
    const color = item.color || BRAND.blue;

    return (
      <Card
        variant="outlined"
        sx={{
          borderRadius: 3.5,
          overflow: "hidden",
          borderColor: open ? alpha(color, 0.34) : BRAND.border,
          boxShadow: open
            ? `0 16px 38px ${alpha(color, 0.12)}`
            : "0 10px 28px rgba(15,23,42,0.04)",
          transition: "all 0.2s ease",
        }}
      >
        <Button
          fullWidth
          onClick={() => setOpenItem(open ? "" : item.id)}
          sx={{
            p: 1.7,
            justifyContent: "space-between",
            textAlign: "left",
            color: "inherit",
            textTransform: "none",
          }}
        >
          <Stack direction="row" spacing={1.3} alignItems="center" sx={{ minWidth: 0 }}>
            <Avatar
              sx={{
                bgcolor: alpha(color, 0.12),
                color,
                width: 40,
                height: 40,
              }}
            >
              {item.icon || <InfoOutlinedIcon />}
            </Avatar>

            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body1" sx={{ fontWeight: 950, color: BRAND.ink }}>
                {item.title}
              </Typography>

              <Typography
                variant="caption"
                sx={{ display: "block", color: "text.secondary" }}
              >
                {open ? TT.close : TT.openCashPanel.replace("Kasada Aç", "Detayı Aç")}
              </Typography>
            </Box>
          </Stack>

          <Avatar
            sx={{
              width: 30,
              height: 30,
              bgcolor: open ? alpha(color, 0.16) : alpha(BRAND.muted, 0.08),
              color: open ? color : BRAND.muted,
            }}
          >
            {open ? <RemoveIcon fontSize="small" /> : <AddIcon fontSize="small" />}
          </Avatar>
        </Button>

        {open && (
          <Box
            sx={{
              px: 2,
              pb: 2,
              pt: 0.2,
              borderTop: `1px solid ${alpha(color, 0.12)}`,
              bgcolor: alpha(color, 0.045),
            }}
          >
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", lineHeight: 1.75 }}
            >
              {item.text}
            </Typography>
          </Box>
        )}
      </Card>
    );
  };

  return (
    <Stack spacing={2.5}>
      <SectionCard
        title={TT.helpHeroTitle}
        subtitle={TT.helpHeroText}
        icon={<InfoOutlinedIcon />}
        sx={{
          background:
            "linear-gradient(135deg, rgba(11,77,139,0.06) 0%, rgba(245,130,32,0.07) 100%)",
        }}
      >
        <Grid container spacing={2}>
          <Grid item xs={12} md={7}>
            <Card
              sx={{
                borderRadius: 4,
                p: { xs: 2, md: 2.5 },
                height: "100%",
                background: `linear-gradient(135deg, ${BRAND.blue} 0%, ${BRAND.blue2} 55%, ${BRAND.orange} 130%)`,
                color: "#fff",
                boxShadow: "0 20px 50px rgba(11,77,139,0.22)",
              }}
            >
              <Stack spacing={1.4}>
                <Avatar
                  sx={{
                    bgcolor: alpha("#fff", 0.18),
                    color: "#fff",
                    width: 52,
                    height: 52,
                  }}
                >
                  <WarehouseIcon />
                </Avatar>

                <Typography variant="h5" sx={{ fontWeight: 950, letterSpacing: -0.55 }}>
                  {TT.helpUseTitle}
                </Typography>

                <Typography variant="body2" sx={{ lineHeight: 1.75, opacity: 0.94 }}>
                  {TT.helpUseText}
                </Typography>

                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ pt: 0.6 }}>
                  {[TT.stockList, TT.movements, TT.quickSale, TT.counting, TT.reports].map(
                    (label) => (
                      <Chip
                        key={label}
                        label={label}
                        sx={{
                          bgcolor: alpha("#fff", 0.16),
                          color: "#fff",
                          fontWeight: 900,
                          borderRadius: 999,
                        }}
                      />
                    )
                  )}
                </Stack>
              </Stack>
            </Card>
          </Grid>

          <Grid item xs={12} md={5}>
            <Stack spacing={1.25}>
              {flowItems.map((item) => (
                <AccordionCard key={item.id} item={item} />
              ))}
            </Stack>
          </Grid>
        </Grid>
      </SectionCard>

      <SectionCard
        title={TT.helpBusinessTitle}
        subtitle={TT.moduleCompatibilityText}
        icon={<BusinessIcon />}
      >
        <Grid container spacing={1.8}>
          {businessCards.map((card) => (
            <Grid item xs={12} sm={6} md={card.title === TT.templateMarket ? 12 : 3} key={card.title}>
              <Card
                variant="outlined"
                sx={{
                  height: "100%",
                  borderRadius: 3.5,
                  p: 1.8,
                  borderColor: alpha(card.color, 0.18),
                  bgcolor: alpha(card.color, 0.045),
                }}
              >
                <Avatar
                  sx={{
                    bgcolor: alpha(card.color, 0.12),
                    color: card.color,
                    width: 42,
                    height: 42,
                  }}
                >
                  {card.icon}
                </Avatar>

                <Typography variant="body1" sx={{ mt: 1.2, fontWeight: 950 }}>
                  {card.title}
                </Typography>

                <Typography
                  variant="body2"
                  sx={{ mt: 0.55, color: "text.secondary", lineHeight: 1.6 }}
                >
                  {card.text}
                </Typography>
              </Card>
            </Grid>
          ))}
        </Grid>
      </SectionCard>

      <SectionCard title={TT.helpFaqTitle} subtitle={TT.helpBestPracticeText} icon={<FactCheckIcon />}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={8}>
            <Stack spacing={1.2}>
              {faqItems.map((item) => (
                <AccordionCard
                  key={item.id}
                  item={{
                    ...item,
                    icon: <InfoOutlinedIcon />,
                    color: BRAND.blue,
                  }}
                />
              ))}
            </Stack>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card
              sx={{
                height: "100%",
                borderRadius: 4,
                p: 2.2,
                border: `1px solid ${alpha(BRAND.success, 0.18)}`,
                bgcolor: alpha(BRAND.success, 0.055),
              }}
            >
              <Avatar sx={{ bgcolor: alpha(BRAND.success, 0.14), color: BRAND.success }}>
                <CheckCircleIcon />
              </Avatar>

              <Typography variant="h6" sx={{ mt: 1.4, fontWeight: 950 }}>
                {TT.helpBestPracticeTitle}
              </Typography>

              <Typography
                variant="body2"
                sx={{ mt: 0.7, color: "text.secondary", lineHeight: 1.75 }}
              >
                {TT.helpBestPracticeText}
              </Typography>
            </Card>
          </Grid>
        </Grid>
      </SectionCard>
    </Stack>
  );
}


function FeaturePill({ icon, title, text, color }) {
  return (
    <Card
      variant="outlined"
      sx={{
        p: 1.4,
        borderRadius: 3,
        borderColor: alpha(color, 0.16),
        bgcolor: alpha(color, 0.045),
      }}
    >
      <Stack direction="row" spacing={1.2} alignItems="center">
        <Avatar sx={{ bgcolor: alpha(color, 0.12), color, width: 38, height: 38 }}>
          {icon}
        </Avatar>

        <Box>
          <Typography variant="body2" sx={{ fontWeight: 950 }}>
            {title}
          </Typography>

          <Typography
            variant="caption"
            sx={{ color: "text.secondary", lineHeight: 1.45 }}
          >
            {text}
          </Typography>
        </Box>
      </Stack>
    </Card>
  );
}


export default function StokTakip() {
  useEffect(() => {
    if (typeof document === "undefined") return;

    const oldStyle = document.getElementById("stok-takip-mobile-style");
    if (oldStyle) oldStyle.remove();

    const stokStyleTag = document.createElement("style");
    stokStyleTag.id = "stok-takip-mobile-style";

    stokStyleTag.innerHTML = `
      .stok-page,
      .stok-page * {
        box-sizing: border-box;
      }

      .stok-page {
        width: 100% !important;
        max-width: 100% !important;
        overflow-x: hidden !important;
      }

      .stok-shell {
        width: 100% !important;
        max-width: 100% !important;
        min-width: 0 !important;
        overflow-x: hidden !important;
      }

      .stok-page .MuiCard-root,
      .stok-page .MuiBox-root,
      .stok-page .MuiStack-root,
      .stok-page .MuiCardContent-root {
        max-width: 100%;
        min-width: 0;
      }

      .stok-header-card {
        width: 100% !important;
        max-width: 100% !important;
        min-width: 0 !important;
      }

      .stok-header-card .MuiTypography-root {
        max-width: 100% !important;
        min-width: 0 !important;
        overflow-wrap: anywhere;
      }

      .stok-header-actions {
        max-width: 100% !important;
      }

      .stok-summary-grid,
      .stok-products-grid {
        width: 100% !important;
        max-width: 100% !important;
        min-width: 0 !important;
      }

      .stok-summary-grid > *,
      .stok-products-grid > * {
        min-width: 0 !important;
        max-width: 100% !important;
      }

      .stok-tabs-card {
        width: 100% !important;
        max-width: 100% !important;
        min-width: 0 !important;
        overflow: hidden !important;
      }

      .stok-tabs-card .MuiTabs-root,
      .stok-tabs-card .MuiTabs-scroller,
      .stok-tabs-card .MuiTabs-flexContainer {
        max-width: 100% !important;
      }

      .stok-filter-card {
        width: 100% !important;
        max-width: 100% !important;
        overflow: hidden !important;
      }

      .stok-filter-stack {
        width: 100% !important;
        max-width: 100% !important;
      }

      .stok-filter-stack > * {
        min-width: 0 !important;
        max-width: 100% !important;
      }

      .stok-page input,
      .stok-page select,
      .stok-page textarea,
      .stok-page .MuiInputBase-root,
      .stok-page .MuiFormControl-root,
      .stok-page .MuiTextField-root {
        max-width: 100% !important;
        min-width: 0 !important;
      }

      @media (min-width: 901px) and (max-width: 1300px) {
        .stok-summary-grid > * {
          grid-column: span 6 / span 6 !important;
        }

        .stok-products-grid > * {
          grid-column: span 6 / span 6 !important;
        }
      }

      @media (max-width: 900px) {
        html,
        body {
          overflow-x: hidden !important;
        }

        .stok-page {
          padding: 10px !important;
          width: 100% !important;
          max-width: 100% !important;
          overflow-x: hidden !important;
        }

        .stok-shell {
          width: 100% !important;
          max-width: 100% !important;
          overflow-x: hidden !important;
        }

        .stok-header-card {
          border-radius: 18px !important;
          padding: 14px !important;
          margin-bottom: 12px !important;
        }

        .stok-header-card h4,
        .stok-header-card .MuiTypography-h4 {
          font-size: 23px !important;
          line-height: 1.12 !important;
          word-break: break-word !important;
        }

        .stok-header-card .MuiTypography-body2 {
          font-size: 12.5px !important;
          line-height: 1.45 !important;
        }

        .stok-header-actions {
          width: 100% !important;
          display: grid !important;
          grid-template-columns: 1fr !important;
          gap: 8px !important;
        }

        .stok-header-actions .MuiButton-root {
          width: 100% !important;
          min-height: 44px !important;
        }

        .stok-header-actions .MuiIconButton-root {
          width: 44px !important;
          height: 44px !important;
        }

        .stok-summary-grid {
          display: grid !important;
          grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
          gap: 10px !important;
          margin-bottom: 12px !important;
        }

        .stok-summary-grid > * {
          grid-column: auto !important;
          width: 100% !important;
          max-width: 100% !important;
          min-width: 0 !important;
        }

        .stok-summary-grid .MuiCard-root {
          border-radius: 18px !important;
          min-height: 118px !important;
          box-shadow: 0 8px 22px rgba(15,23,42,.07) !important;
        }

        .stok-summary-grid .MuiCardContent-root {
          padding: 13px !important;
        }

        .stok-summary-grid .MuiAvatar-root {
          width: 36px !important;
          height: 36px !important;
        }

        .stok-summary-grid .MuiTypography-body2 {
          font-size: 12px !important;
          line-height: 1.25 !important;
        }

        .stok-summary-grid .MuiTypography-h4 {
          font-size: 20px !important;
          line-height: 1.08 !important;
          word-break: break-word !important;
        }

        .stok-summary-grid .MuiTypography-caption {
          font-size: 11px !important;
          line-height: 1.25 !important;
        }

        .stok-tabs-card {
          border-radius: 16px !important;
          margin-bottom: 12px !important;
          overflow: visible !important;
        }

        .stok-tabs-card .MuiTabs-root {
          width: 100% !important;
          max-width: 100% !important;
          overflow: visible !important;
        }

        .stok-tabs-card .MuiTabs-scroller {
          overflow: visible !important;
          transform: none !important;
        }

        .stok-tabs-card .MuiTabs-flexContainer {
          width: 100% !important;
          max-width: 100% !important;
          display: grid !important;
          grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
          gap: 8px !important;
          padding: 8px !important;
          transform: none !important;
        }

        .stok-tabs-card .MuiTabs-indicator {
          display: none !important;
        }

        .stok-tabs-card .MuiTab-root {
          width: 100% !important;
          max-width: 100% !important;
          min-width: 0 !important;
          min-height: 44px !important;
          padding: 8px 8px !important;
          font-size: 12px !important;
          line-height: 1.15 !important;
          white-space: normal !important;
          text-align: center !important;
          border-radius: 12px !important;
          border: 1px solid rgba(15,23,42,.10) !important;
          background: #f8fafc !important;
          color: #334155 !important;
        }

        .stok-tabs-card .MuiTab-root.Mui-selected {
          background: #fff3e6 !important;
          color: #f58220 !important;
          border-color: rgba(245,130,32,.45) !important;
          font-weight: 950 !important;
        }

        .stok-tabs-card .MuiTab-iconWrapper {
          margin-right: 5px !important;
          font-size: 17px !important;
        }

        .stok-filter-card {
          border-radius: 16px !important;
          margin-bottom: 12px !important;
        }

        .stok-filter-card .MuiCardContent-root {
          padding: 10px !important;
        }

        .stok-filter-stack {
          display: grid !important;
          grid-template-columns: 1fr !important;
          gap: 10px !important;
          width: 100% !important;
        }

        .stok-filter-stack > * {
          width: 100% !important;
          max-width: 100% !important;
          min-width: 0 !important;
          flex: 1 1 100% !important;
        }

        .stok-filter-stack .MuiButton-root {
          width: 100% !important;
          min-height: 44px !important;
        }

        .stok-products-grid {
          display: grid !important;
          grid-template-columns: 1fr !important;
          gap: 12px !important;
          width: 100% !important;
          max-width: 100% !important;
        }

        .stok-products-grid > * {
          grid-column: auto !important;
          width: 100% !important;
          max-width: 100% !important;
          min-width: 0 !important;
        }

        .stok-products-grid .MuiCard-root {
          width: 100% !important;
          max-width: 100% !important;
          border-radius: 18px !important;
        }

        .stok-page .MuiDialog-paper {
          margin: 0 !important;
          width: 100% !important;
          max-width: 100% !important;
          max-height: 100% !important;
          border-radius: 0 !important;
        }
      }

      @media (max-width: 430px) {
        .stok-page {
          padding: 8px !important;
        }

        .stok-summary-grid {
          grid-template-columns: 1fr !important;
        }

        .stok-header-card h4,
        .stok-header-card .MuiTypography-h4 {
          font-size: 21px !important;
        }

        .stok-summary-grid .MuiTypography-h4 {
          font-size: 21px !important;
        }

        .stok-tabs-card .MuiTabs-flexContainer {
          grid-template-columns: 1fr !important;
        }
      }
      /* =====================================================
         ✅ PROFESYONEL MOBİL STOK TASARIMI - FINAL
         ===================================================== */

      @media (max-width: 900px) {
        .stok-page {
          padding: 8px !important;
          background: #f4f7fb !important;
        }

        .stok-shell {
          padding: 0 !important;
        }

        /* Header daha kurumsal ve kısa */
        .stok-header-card {
          padding: 14px !important;
          border-radius: 20px !important;
          margin-bottom: 10px !important;
          box-shadow: 0 10px 28px rgba(15,23,42,.08) !important;
        }

        .stok-header-card .MuiAvatar-root {
          width: 42px !important;
          height: 42px !important;
        }

        .stok-header-card .MuiTypography-h4 {
          font-size: 21px !important;
          line-height: 1.15 !important;
          letter-spacing: -.5px !important;
        }

        .stok-header-card .MuiTypography-body2 {
          font-size: 12px !important;
          line-height: 1.4 !important;
          display: -webkit-box !important;
          -webkit-line-clamp: 2 !important;
          -webkit-box-orient: vertical !important;
          overflow: hidden !important;
        }

        .stok-header-card .MuiAlert-root {
          margin-top: 10px !important;
          padding: 6px 10px !important;
          font-size: 12px !important;
          border-radius: 12px !important;
        }

        /* Header butonları küçük ve yan yana */
        .stok-header-actions {
          width: 100% !important;
          display: grid !important;
          grid-template-columns: 44px 1fr 1fr !important;
          gap: 8px !important;
          align-items: center !important;
        }

        .stok-header-actions .MuiIconButton-root {
          width: 44px !important;
          height: 42px !important;
          border-radius: 12px !important;
        }

        .stok-header-actions .MuiButton-root {
          width: 100% !important;
          min-height: 42px !important;
          border-radius: 12px !important;
          font-size: 12px !important;
          padding: 8px 10px !important;
          white-space: nowrap !important;
        }

        .stok-header-actions .MuiButton-startIcon {
          margin-right: 4px !important;
        }

        /* Özet kartları mobilde 2 kolon, daha sıkı */
        .stok-summary-grid {
          display: grid !important;
          grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
          gap: 8px !important;
          margin-bottom: 10px !important;
        }

        .stok-summary-grid > * {
          grid-column: auto !important;
          min-width: 0 !important;
        }

        .stok-summary-grid .MuiCard-root {
          min-height: 104px !important;
          border-radius: 16px !important;
          box-shadow: 0 8px 20px rgba(15,23,42,.06) !important;
        }

        .stok-summary-grid .MuiCardContent-root {
          padding: 11px !important;
        }

        .stok-summary-grid .MuiAvatar-root {
          width: 32px !important;
          height: 32px !important;
        }

        .stok-summary-grid .MuiTypography-body2 {
          margin-top: 8px !important;
          font-size: 11px !important;
          line-height: 1.2 !important;
        }

        .stok-summary-grid .MuiTypography-h4 {
          font-size: 19px !important;
          line-height: 1.05 !important;
          margin-top: 4px !important;
        }

        .stok-summary-grid .MuiTypography-caption {
          font-size: 10px !important;
          line-height: 1.2 !important;
        }

        .stok-summary-grid .MuiLinearProgress-root {
          height: 6px !important;
          margin-top: 8px !important;
        }

        /* Tab alanı artık dev kart değil, kompakt profesyonel menü */
        .stok-tabs-card {
          border-radius: 18px !important;
          margin-bottom: 10px !important;
          padding: 8px !important;
          overflow: hidden !important;
          box-shadow: 0 8px 22px rgba(15,23,42,.06) !important;
        }

        .stok-tabs-card .MuiTabs-root {
          min-height: unset !important;
          width: 100% !important;
        }

        .stok-tabs-card .MuiTabs-scroller {
          overflow: visible !important;
        }

        .stok-tabs-card .MuiTabs-flexContainer {
          display: grid !important;
          grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
          gap: 7px !important;
          width: 100% !important;
          transform: none !important;
          padding: 0 !important;
        }

        .stok-tabs-card .MuiTabs-indicator {
          display: none !important;
        }

        .stok-tabs-card .MuiTab-root {
          min-width: 0 !important;
          width: 100% !important;
          max-width: 100% !important;
          min-height: 40px !important;
          height: 40px !important;
          padding: 5px 6px !important;
          border-radius: 12px !important;
          border: 1px solid rgba(15,23,42,.09) !important;
          background: #f8fafc !important;
          color: #334155 !important;
          font-size: 10.8px !important;
          font-weight: 850 !important;
          line-height: 1.1 !important;
          text-transform: none !important;
          white-space: nowrap !important;
          overflow: hidden !important;
        }

        .stok-tabs-card .MuiTab-root.Mui-selected {
          background: linear-gradient(135deg, #fff7ed, #ffffff) !important;
          color: #f58220 !important;
          border-color: rgba(245,130,32,.45) !important;
          box-shadow: inset 0 0 0 1px rgba(245,130,32,.12) !important;
        }

        .stok-tabs-card .MuiTab-iconWrapper {
          margin-right: 3px !important;
          font-size: 15px !important;
        }

        /* Filtre alanı daha kısa */
        .stok-filter-card {
          border-radius: 18px !important;
          margin-bottom: 10px !important;
          box-shadow: 0 8px 22px rgba(15,23,42,.06) !important;
        }

        .stok-filter-card .MuiCardContent-root {
          padding: 10px !important;
        }

        .stok-filter-stack {
          display: grid !important;
          grid-template-columns: 1fr 1fr !important;
          gap: 8px !important;
        }

        .stok-filter-stack > * {
          width: 100% !important;
          min-width: 0 !important;
          max-width: 100% !important;
        }

        .stok-filter-stack > .MuiTextField-root {
          grid-column: 1 / -1 !important;
        }

        .stok-filter-stack .MuiInputBase-root {
          min-height: 42px !important;
          border-radius: 12px !important;
          font-size: 12px !important;
        }

        .stok-filter-stack .MuiInputLabel-root {
          font-size: 12px !important;
        }

        .stok-filter-stack .MuiButton-root {
          grid-column: 1 / -1 !important;
          width: 100% !important;
          min-height: 42px !important;
          border-radius: 12px !important;
          font-size: 12px !important;
        }

        /* Ürün kartları profesyonel tek kolon */
        .stok-products-grid {
          display: grid !important;
          grid-template-columns: 1fr !important;
          gap: 10px !important;
        }

        .stok-products-grid > * {
          grid-column: auto !important;
          width: 100% !important;
          max-width: 100% !important;
          min-width: 0 !important;
        }

        .stok-products-grid .MuiCard-root {
          border-radius: 18px !important;
          box-shadow: 0 8px 22px rgba(15,23,42,.07) !important;
        }

        .stok-products-grid .MuiCardContent-root {
          padding: 14px !important;
        }

        .stok-products-grid .MuiTypography-subtitle1 {
          font-size: 15px !important;
        }

        .stok-products-grid .MuiTypography-h4 {
          font-size: 24px !important;
        }

        .stok-products-grid .MuiButton-root {
          min-height: 36px !important;
          border-radius: 10px !important;
          font-size: 11px !important;
          padding: 6px 9px !important;
        }

        .stok-products-grid .MuiChip-root {
          height: 24px !important;
          font-size: 10.5px !important;
        }
      }

      @media (max-width: 430px) {
        .stok-summary-grid {
          grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
        }

        .stok-tabs-card .MuiTabs-flexContainer {
          grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
        }

        .stok-filter-stack {
          grid-template-columns: 1fr !important;
        }

        .stok-header-actions {
          grid-template-columns: 44px 1fr !important;
        }

        .stok-header-actions .MuiButton-root:last-child {
          grid-column: 1 / -1 !important;
        }
      }

    `;

    document.head.appendChild(stokStyleTag);
  }, []);

  const theme = useTheme();

  const [lang, setLang] = useState(getPanelLang);
  const TT = STOCK_TEXT[lang] || STOCK_TEXT.tr;

  const [activeTab, setActiveTab] = useState("dashboard");
  const [items, setItems] = useState([]);
  const [movements, setMovements] = useState([]);
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
  const [units, setUnits] = useState(DEFAULT_UNITS);
const [suppliers, setSuppliers] = useState(DEFAULT_SUPPLIERS);
const [settings, setSettings] = useState(defaultSettings);
const [customers, setCustomers] = useState([]);

  const [loading, setLoading] = useState(true);
  const [serverMode, setServerMode] = useState(false);
  const [backendError, setBackendError] = useState("");

  const [search, setSearch] = useState("");
  const [moduleFilter, setModuleFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortBy, setSortBy] = useState("critical");
  const [page, setPage] = useState(1);

  const [formOpen, setFormOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  const [movementOpen, setMovementOpen] = useState(false);
  const [movementItem, setMovementItem] = useState(null);
  const [movementType, setMovementType] = useState("in");

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);

  const [snackbar, setSnackbar] = useState({
    open: false,
    severity: "info",
    message: "",
  });

  const hydrationRef = useRef(false);

  useEffect(() => {
    const syncLang = () => setLang(getPanelLang());

    window.addEventListener("app_lang_change", syncLang);
    window.addEventListener("storage", syncLang);

    return () => {
      window.removeEventListener("app_lang_change", syncLang);
      window.removeEventListener("storage", syncLang);
    };
  }, []);

useEffect(() => {
  let mounted = true;

  async function loadCustomers() {
    try {
      const data = await apiGet("/beaty/pro/customers");

      if (mounted) {
        setCustomers(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.warn("Müşteri listesi alınamadı:", err.message);
      if (mounted) setCustomers([]);
    }
  }

  loadCustomers();

  return () => {
    mounted = false;
  };
}, []);

  const loadLocalData = useCallback(() => {
    const localItems = readStorage(STORAGE_KEYS.items, []);
    const localMovements = readStorage(STORAGE_KEYS.movements, []);
    const localCategories = readStorage(STORAGE_KEYS.categories, DEFAULT_CATEGORIES);
    const localUnits = readStorage(STORAGE_KEYS.units, DEFAULT_UNITS);
    const localSuppliers = readStorage(STORAGE_KEYS.suppliers, DEFAULT_SUPPLIERS);
    const localSettings = readStorage(STORAGE_KEYS.settings, defaultSettings);

    setItems(localItems);
    setMovements(localMovements);
    setCategories(localCategories);
    setUnits(localUnits);
    setSuppliers(localSuppliers);
    setSettings({ ...defaultSettings, ...localSettings });
  }, []);

  const loadServerData = useCallback(async () => {
    const [
      serverItems,
      serverMovements,
      serverCategories,
      serverUnits,
      serverSuppliers,
    ] = await Promise.all([
      apiGet("/stock/items"),
      apiGet("/stock/movements"),
      apiGet("/stock/categories"),
      apiGet("/stock/units"),
      apiGet("/stock/suppliers"),
    ]);

    setItems(Array.isArray(serverItems) ? serverItems : []);
    setMovements(Array.isArray(serverMovements) ? serverMovements : []);
    setCategories(
      Array.isArray(serverCategories) && serverCategories.length
        ? serverCategories
        : DEFAULT_CATEGORIES
    );
    setUnits(
      Array.isArray(serverUnits) && serverUnits.length ? serverUnits : DEFAULT_UNITS
    );
    setSuppliers(
      Array.isArray(serverSuppliers) && serverSuppliers.length
        ? serverSuppliers
        : DEFAULT_SUPPLIERS
    );
  }, []);

  const refreshAll = useCallback(async () => {
    setLoading(true);
    setBackendError("");

    try {
      await loadServerData();
      setServerMode(true);
    } catch (err) {
      setServerMode(false);
      setBackendError(err.message || "Backend not ready");
      loadLocalData();
    } finally {
      setLoading(false);
      hydrationRef.current = true;
    }
  }, [loadLocalData, loadServerData]);

  useEffect(() => {
    refreshAll();
  }, [refreshAll]);

  useEffect(() => {
    if (!hydrationRef.current) return;
    if (serverMode) return;

    writeStorage(STORAGE_KEYS.items, items);
  }, [items, serverMode]);

  useEffect(() => {
    if (!hydrationRef.current) return;
    if (serverMode) return;

    writeStorage(STORAGE_KEYS.movements, movements);
  }, [movements, serverMode]);

  useEffect(() => {
    if (!hydrationRef.current) return;
    if (serverMode) return;

    writeStorage(STORAGE_KEYS.categories, categories);
  }, [categories, serverMode]);

  useEffect(() => {
    if (!hydrationRef.current) return;
    if (serverMode) return;

    writeStorage(STORAGE_KEYS.units, units);
  }, [units, serverMode]);

  useEffect(() => {
    if (!hydrationRef.current) return;
    if (serverMode) return;

    writeStorage(STORAGE_KEYS.suppliers, suppliers);
  }, [suppliers, serverMode]);

  useEffect(() => {
    if (!hydrationRef.current) return;

    writeStorage(STORAGE_KEYS.settings, settings);
  }, [settings]);

  const summary = useMemo(() => {
    const activeItems = items.filter((x) => x.active !== false);
    const critical = activeItems.filter((item) => getStockStatus(item) === "critical");
    const out = activeItems.filter((item) => getStockStatus(item) === "out");

    const totalValue = activeItems.reduce((sum, item) => sum + calcStockValue(item), 0);
    const totalSaleValue = activeItems.reduce((sum, item) => sum + calcSaleValue(item), 0);
    const profit = totalSaleValue - totalValue;

    const today = todayKey();
    const month = monthKey();

    const todayMoves = movements.filter(
      (m) => String(m.created_at || "").slice(0, 10) === today
    );

    const todaySales = todayMoves
      .filter((m) => m.movement_type === "sale")
      .reduce((sum, m) => sum + parseNumber(m.total_amount), 0);

    const monthlySales = movements
      .filter((m) => m.movement_type === "sale")
      .filter((m) => String(m.created_at || "").slice(0, 7) === month)
      .reduce((sum, m) => sum + parseNumber(m.total_amount), 0);

    const health = activeItems.length
      ? ((activeItems.length - critical.length - out.length) / activeItems.length) * 100
      : 100;

    return {
      totalCards: activeItems.length,
      criticalCount: critical.length,
      outCount: out.length,
      totalValue,
      totalSaleValue,
      profit,
      todayMovements: todayMoves.length,
      todaySales,
      monthlySales,
      health,
    };
  }, [items, movements]);

  const filteredItems = useMemo(() => {
    const term = safeLower(search);

    let result = [...items];

    if (!settings.showPassive) {
      result = result.filter((item) => item.active !== false);
    }

    if (moduleFilter !== "all") {
      result = result.filter((item) => item.module === moduleFilter);
    }

    if (categoryFilter !== "all") {
      result = result.filter((item) => String(item.category_id) === String(categoryFilter));
    }

    if (typeFilter !== "all") {
      result = result.filter((item) => item.stock_type === typeFilter);
    }

  if (statusFilter !== "all") {
  result = result.filter((item) => {
    const status = getStockStatus(item);

    if (statusFilter === "critical") {
      return status === "critical" || status === "out";
    }

    return status === statusFilter;
  });
}

    if (term) {
      result = result.filter((item) => {
        const categoryName = getCategoryName(categories, item.category_id, lang);
        const supplierName = getSupplierName(suppliers, item.supplier_id);

        return [
          item.name,
          item.barcode,
          item.sku,
          item.brand,
          item.sub_category,
          categoryName,
          supplierName,
        ]
          .map(safeLower)
          .some((x) => x.includes(term));
      });
    }

    result.sort((a, b) => {
      if (sortBy === "stock_asc") {
        return parseNumber(a.current_stock) - parseNumber(b.current_stock);
      }

      if (sortBy === "stock_desc") {
        return parseNumber(b.current_stock) - parseNumber(a.current_stock);
      }

      if (sortBy === "value_desc") {
        return calcStockValue(b) - calcStockValue(a);
      }

      if (sortBy === "critical") {
        const order = { out: 0, critical: 1, over: 2, normal: 3 };

        return order[getStockStatus(a)] - order[getStockStatus(b)];
      }

      return String(a.name || "").localeCompare(String(b.name || ""), "tr");
    });

    return result;
  }, [
    items,
    search,
    moduleFilter,
    categoryFilter,
    typeFilter,
    statusFilter,
    sortBy,
    settings.showPassive,
    categories,
    suppliers,
    lang,
  ]);

  const pageSize = settings.rowsPerPage || 12;
  const pageCount = Math.max(1, Math.ceil(filteredItems.length / pageSize));

  const pagedItems = filteredItems.slice(
    (page - 1) * pageSize,
    page * pageSize
  );

  useEffect(() => {
    setPage(1);
  }, [search, moduleFilter, categoryFilter, typeFilter, statusFilter, sortBy]);

  const openNewForm = () => {
    setEditingItem(createEmptyStockItem({ currency: settings.defaultCurrency }));
    setFormOpen(true);
  };

  const openEditForm = (item) => {
    setEditingItem({ ...item });
    setFormOpen(true);
  };

  const saveStockItem = async () => {
    if (!editingItem) return;

    const name = String(editingItem.name || "").trim();

    if (!name) {
      setSnackbar({
        open: true,
        severity: "error",
        message: TT.requiredName,
      });

      return;
    }

    const payload = {
      ...editingItem,
      name,
      current_stock: parseNumber(editingItem.current_stock),
      min_stock: parseNumber(editingItem.min_stock),
      max_stock: parseNumber(editingItem.max_stock),
      purchase_price: parseNumber(editingItem.purchase_price),
      sale_price: parseNumber(editingItem.sale_price),
      vat_rate: parseNumber(editingItem.vat_rate),
      updated_at: new Date().toISOString(),
    };

      try {
      let finalPayload = payload;

      if (serverMode) {
        if (payload.backend_id || !String(payload.id).startsWith("stock-")) {
          finalPayload = await apiPut(
            `/stock/items/${payload.backend_id || payload.id}`,
            payload
          );
        } else {
          finalPayload = await apiPost("/stock/items", payload);
        }
      }

finalPayload =
  finalPayload?.item ||
  finalPayload?.stock_item ||
  finalPayload?.data ||
  finalPayload;

finalPayload = {
  ...payload,
  ...finalPayload,
  id: finalPayload?.id || payload.id,
  backend_id:
    finalPayload?.backend_id ||
    finalPayload?.id ||
    payload.backend_id ||
    null,
  name: finalPayload?.name || payload.name,
  active: finalPayload?.active !== false,
};

      setItems((prev) => {
        const exists = prev.some(
          (x) =>
            String(x.id) === String(payload.id) ||
            String(x.id) === String(finalPayload.id) ||
            String(x.backend_id) === String(finalPayload.backend_id)
        );

        if (exists) {
          return prev.map((x) => {
            const same =
              String(x.id) === String(payload.id) ||
              String(x.id) === String(finalPayload.id) ||
              String(x.backend_id) === String(finalPayload.backend_id);

            return same ? { ...x, ...finalPayload } : x;
          });
        }

        return [finalPayload, ...prev];
      });

setSearch("");
setModuleFilter("all");
setCategoryFilter("all");
setTypeFilter("all");
setStatusFilter("all");
setActiveTab("list");
setPage(1);



      setFormOpen(false);
      setEditingItem(null);

      setSnackbar({
        open: true,
        severity: "success",
        message: TT.stockSaved,
      });
    } catch (err) {
      setSnackbar({
        open: true,
        severity: "error",
        message: err.message || "Kaydedilemedi",
      });
    }
  };

  const openDelete = (item) => {
    setDeleteItem(item);
    setDeleteOpen(true);
  };

   const confirmDelete = async () => {
    if (!deleteItem) return;

    const deletedId = String(deleteItem.id);
    const deletedBackendId = String(deleteItem.backend_id || "");

    try {
      if (serverMode) {
        await apiDelete(`/stock/items/${deleteItem.backend_id || deleteItem.id}`);
      }

      // Ekrandan tamamen kaldırıyoruz.
      // Backend tarafında geçmiş hareketler korunur, ürün active=false olur.
      // Frontend tarafında liste, dashboard, kritik stok ve rapor hesaplarından çıkar.
      setItems((prev) =>
        prev.filter((x) => {
          const sameFrontend = String(x.id) === deletedId;
          const sameBackend =
            deletedBackendId && String(x.backend_id || "") === deletedBackendId;

          return !sameFrontend && !sameBackend;
        })
      );

      setDeleteOpen(false);
      setDeleteItem(null);

      setSnackbar({
        open: true,
        severity: "success",
        message: TT.stockDeleted,
      });
    } catch (err) {
      setSnackbar({
        open: true,
        severity: "error",
        message: err.message || "Silinemedi",
      });
    }
  };

  const openMovement = (item, type = "in") => {
    setMovementItem(item);
    setMovementType(type);
    setMovementOpen(true);
  };

  const saveMovement = async (movementForm) => {
    if (!movementItem) return;

    const qty = parseNumber(movementForm.quantity);

    if (qty <= 0) {
      setSnackbar({
        open: true,
        severity: "error",
        message: TT.requiredQty,
      });

      return;
    }

    const meta = getMovementMeta(movementForm.movement_type);
    const previous = parseNumber(movementItem.current_stock);

    const next =
      movementForm.movement_type === "count_adjustment"
        ? qty
        : previous + qty * meta.effect;

    if (next < 0 && !settings.allowNegativeStock) {
      setSnackbar({
        open: true,
        severity: "error",
        message: TT.insufficientStock,
      });

      return;
    }

    const now = new Date().toISOString();
    const total = qty * parseNumber(movementForm.unit_price);

    const movement = {
      ...movementForm,
      id: movementForm.id || uid("move"),
      stock_id: movementItem.id,
      stock_name: movementItem.name,
      quantity: qty,
      previous_stock: previous,
      next_stock: next,
      unit_price: parseNumber(movementForm.unit_price),
      total_amount: total,
      currency: movementItem.currency || settings.defaultCurrency,
      source_module: movementItem.module || "stock",
      created_at: now,
    };

    if (movement.movement_type === "sale") {
      movement.cash_payload = createCashPayloadFromSale({
        item: movementItem,
        qty,
        amount: total,
        paymentMethod: movement.payment_method,
        customerName: movement.customer_name,
        staffName: movement.staff_name,
      });
    }

       try {
      if (serverMode) {
        const saved = await apiPost(
          `/stock/items/${movementItem.backend_id || movementItem.id}/movement`,
          {
            ...movement,
            allow_negative_stock: settings.allowNegativeStock === true,
          }
        );

        if (saved?.item) {
          setItems((prev) =>
            prev.map((item) => {
              const same =
                String(item.id) === String(movementItem.id) ||
                String(item.backend_id) === String(movementItem.backend_id) ||
                String(item.id) === String(saved.item.id) ||
                String(item.backend_id) === String(saved.item.backend_id);

              return same ? { ...item, ...saved.item } : item;
            })
          );
        }

        if (saved?.movement) {
          setMovements((prev) => [saved.movement, ...prev]);
        }
      } else {
        setItems((prev) =>
          prev.map((item) => {
            if (item.id !== movementItem.id) return item;

            const soldQty =
              movement.movement_type === "sale"
                ? parseNumber(item.total_sold_qty) + qty
                : parseNumber(item.total_sold_qty);

            const soldAmount =
              movement.movement_type === "sale"
                ? parseNumber(item.total_sold_amount) + total
                : parseNumber(item.total_sold_amount);

            return {
              ...item,
              current_stock: next,
              total_sold_qty: soldQty,
              total_sold_amount: soldAmount,
              updated_at: now,
              last_movement_at: now,
            };
          })
        );

        setMovements((prev) => [movement, ...prev]);
      }




      setMovementOpen(false);
      setMovementItem(null);

      setSnackbar({
        open: true,
        severity: "success",
        message: movement.movement_type === "sale" ? TT.saleCompleted : TT.movementSaved,
      });
    } catch (err) {
      setSnackbar({
        open: true,
        severity: "error",
        message: err.message || "Hareket kaydedilemedi",
      });
    }
  };

  const exportItems = () => {
    const rows = [
      [
        TT.itemName,
        TT.barcode,
        TT.category,
        TT.module,
        TT.stockType,
        TT.currentStock,
        TT.minStock,
        TT.purchasePrice,
        TT.salePrice,
        TT.stockValue,
      ],
      ...filteredItems.map((item) => [
        item.name,
        item.barcode,
        getCategoryName(categories, item.category_id, lang),
        getModuleLabel(item.module, lang),
        getStockTypeLabel(item.stock_type, TT),
        item.current_stock,
        item.min_stock,
        item.purchase_price,
        item.sale_price,
        calcStockValue(item),
      ]),
    ];

    downloadCsv(`mesyat-stok-${todayKey()}.csv`, rows);

    setSnackbar({
      open: true,
      severity: "success",
      message: TT.exported,
    });
  };

  const clearFilters = () => {
    setSearch("");
    setModuleFilter("all");
    setCategoryFilter("all");
    setTypeFilter("all");
    setStatusFilter("all");
    setSortBy("critical");
  };

  if (loading) {
    return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        overflowX: "hidden",
        }}
      >
        <Box sx={{ maxWidth: 1500, mx: "auto" }}>
          <Skeleton
            variant="rounded"
            height={120}
            sx={{ borderRadius: 4, mb: 2 }}
          />

          <Grid container spacing={2}>
            {[1, 2, 3, 4].map((i) => (
              <Grid key={i} item xs={12} md={3}>
                <Skeleton variant="rounded" height={150} sx={{ borderRadius: 4 }} />
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>
    );
  }



  return (
    <Box
      className="stok-page"
      sx={{
        width: "100%",
        maxWidth: "100%",
        minHeight: "100vh",
        overflowX: "hidden",
        background:
          "radial-gradient(circle at top left, rgba(21,101,192,0.10), transparent 32%), radial-gradient(circle at top right, rgba(245,130,32,0.12), transparent 30%), linear-gradient(145deg, #f8fafc 0%, #eef2f7 100%)",
        p: { xs: 1, sm: 1.5, md: 2.5 },
        pb: { xs: 5, md: 7 },
      }}
    >
          <Box
        className="stok-shell"
        sx={{
          width: "100%",
          maxWidth: 1500,
          mx: "auto",
          minWidth: 0,
          overflowX: "hidden",
          "& *": {
            boxSizing: "border-box",
          },
          "& .MuiCard-root": {
            minWidth: 0,
          },
          "& .MuiStack-root": {
            minWidth: 0,
          },
          "& .MuiTypography-root": {
            overflowWrap: "anywhere",
          },
        }}
      >
        <Card
          className="stok-header-card"
          sx={{
            borderRadius: { xs: 3, md: 5 },
            p: { xs: 1.5, sm: 2, md: 3 },
            mb: { xs: 1.5, md: 2.5 },
            border: `1px solid ${BRAND.border}`,
            boxShadow: {
              xs: "0 10px 28px rgba(15,23,42,0.08)",
              md: "0 24px 70px rgba(15,23,42,0.10)",
            },
            overflow: "hidden",
            position: "relative",
            width: "100%",
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              position: "absolute",
              right: { xs: -95, md: -70 },
              top: { xs: -95, md: -70 },
              width: { xs: 170, md: 210 },
              height: { xs: 170, md: 210 },
              borderRadius: "50%",
              bgcolor: alpha(BRAND.orange, 0.13),
              pointerEvents: "none",
            }}
          />

          <Stack
            direction={{ xs: "column", lg: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", lg: "center" }}
            spacing={2}
            sx={{
              position: "relative",
              width: "100%",
              minWidth: 0,
            }}
          >
            <BrandTitle
              title={TT.pageTitle}
              subtitle={TT.pageSubtitle}
              icon={<WarehouseIcon />}
            />

            <Stack
              className="stok-header-actions"
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              flexWrap="wrap"
              useFlexGap
              sx={{
                width: { xs: "100%", sm: "auto" },
                minWidth: 0,
                "& .MuiButton-root": {
                  width: { xs: "100%", sm: "auto" },
                  justifyContent: "center",
                  whiteSpace: "nowrap",
                },
                "& .MuiIconButton-root": {
                  alignSelf: { xs: "flex-start", sm: "center" },
                },
              }}
            >
              <Tooltip title={TT.refresh}>
                <IconButton
                  onClick={refreshAll}
                  sx={{
                    bgcolor: alpha(BRAND.blue, 0.08),
                    color: BRAND.blue,
                    flexShrink: 0,
                  }}
                >
                  <RefreshIcon />
                </IconButton>
              </Tooltip>

              <Button
                variant="outlined"
                startIcon={<FileDownloadIcon />}
                onClick={exportItems}
                sx={{
                  borderRadius: 3,
                  textTransform: "none",
                  fontWeight: 900,
                  minHeight: 42,
                }}
              >
                {TT.exportCsv}
              </Button>

              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={openNewForm}
                sx={{
                  borderRadius: 3,
                  textTransform: "none",
                  fontWeight: 950,
                  minHeight: 42,
                  bgcolor: BRAND.orange,
                  boxShadow: "0 16px 35px rgba(245,130,32,0.28)",
                  "&:hover": { bgcolor: "#D96C0C" },
                }}
              >
                {TT.addStockCard}
              </Button>
            </Stack>
          </Stack>

          {!serverMode && (
            <Alert
              severity="warning"
              sx={{
                mt: 2.4,
                borderRadius: 3,
                position: "relative",
                zIndex: 1,
                "& .MuiAlert-message": {
                  minWidth: 0,
                  overflowWrap: "anywhere",
                },
              }}
            >
              {TT.savedLocal} {backendError ? `(${backendError})` : ""}
            </Alert>
          )}
        </Card>



        <Grid className="stok-summary-grid" container spacing={2} sx={{ mb: 2.5 }}>          <Grid item xs={12} sm={6} lg={3}>
            <SummaryCard
              title={TT.totalStockCards}
              value={summary.totalCards}
              subtitle={TT.inventory}
              icon={<InventoryIcon />}
              tone="blue"
            />
          </Grid>

          <Grid item xs={12} sm={6} lg={3}>
            <SummaryCard
              title={TT.totalValue}
              value={moneyText(summary.totalValue, settings.defaultCurrency, lang)}
              subtitle={`${TT.profit}: ${moneyText(
                summary.profit,
                settings.defaultCurrency,
                lang
              )}`}
              icon={<TrendingUpIcon />}
              tone="orange"
            />
          </Grid>

          <Grid item xs={12} sm={6} lg={3}>
            <SummaryCard
              title={TT.criticalCount}
              value={summary.criticalCount}
              subtitle={`${TT.outCount}: ${summary.outCount}`}
              icon={<WarningAmberIcon />}
              tone={summary.criticalCount || summary.outCount ? "danger" : "success"}
              progress={summary.health}
            />
          </Grid>

          <Grid item xs={12} sm={6} lg={3}>
            <SummaryCard
              title={TT.todaySales}
              value={moneyText(summary.todaySales, settings.defaultCurrency, lang)}
              subtitle={`${TT.monthlySales}: ${moneyText(
                summary.monthlySales,
                settings.defaultCurrency,
                lang
              )}`}
              icon={<PointOfSaleIcon />}
              tone="success"
            />
          </Grid>
        </Grid>




               <Card
          className="stok-tabs-card"
          sx={{
            mb: 2.5,
            borderRadius: 4,
            border: `1px solid ${BRAND.border}`,
            boxShadow: "0 14px 35px rgba(15,23,42,0.06)",
          }}
        >
         
        <Tabs
          value={activeTab}
          onChange={(_, value) => {
            if (value === "list") {
              setSearch("");
              setModuleFilter("all");
              setCategoryFilter("all");
              setTypeFilter("all");
              setStatusFilter("all");
              setPage(1);
            }

            setActiveTab(value);
          }}
  variant="scrollable"
  scrollButtons="auto"
  allowScrollButtonsMobile
  sx={{
    px: { xs: 0.5, md: 1 },
    maxWidth: "100%",
    overflowX: "auto",
              "& .MuiTab-root": {
  textTransform: "none",
  fontWeight: 900,
  minHeight: { xs: 52, md: 58 },
  minWidth: { xs: 112, sm: 130 },
  px: { xs: 1, md: 2 },
  fontSize: { xs: "0.78rem", sm: "0.86rem" },
  whiteSpace: "nowrap",
},
              "& .Mui-selected": {
                color: `${BRAND.orange} !important`,
              },
              "& .MuiTabs-indicator": {
                bgcolor: BRAND.orange,
                height: 3,
                borderRadius: 999,
              },
            }}
          >
            <Tab value="dashboard" icon={<BarChartIcon />} iconPosition="start" label={TT.dashboard} />
            <Tab value="list" icon={<InventoryIcon />} iconPosition="start" label={TT.stockList} />
            <Tab value="sale" icon={<PointOfSaleIcon />} iconPosition="start" label={TT.quickSale} />
            <Tab value="movements" icon={<HistoryIcon />} iconPosition="start" label={TT.movements} />
            <Tab value="counting" icon={<FactCheckIcon />} iconPosition="start" label={TT.counting} />
            <Tab value="templates" icon={<BoltIcon />} iconPosition="start" label={TT.templates} />
            <Tab value="definitions" icon={<TuneIcon />} iconPosition="start" label={TT.definitions} />
            <Tab value="reports" icon={<ReceiptLongIcon />} iconPosition="start" label={TT.reports} />
            <Tab value="settings" icon={<SettingsIcon />} iconPosition="start" label={TT.settings} />
            <Tab value="help" icon={<InfoOutlinedIcon />} iconPosition="start" label={TT.help} />
          </Tabs>
        </Card>

        {(activeTab === "dashboard" || activeTab === "list") && (
                    <Card
            className="stok-filter-card"
            sx={{
              borderRadius: 4,
              mb: 2.5,
              border: `1px solid ${BRAND.border}`,
              boxShadow: "0 14px 35px rgba(15,23,42,0.06)",
            }}
          >
            <CardContent>
                      <Stack
                  className="stok-filter-stack"
                  direction="row"
                  spacing={1.2}
                  useFlexGap
                  flexWrap="wrap"
  alignItems="center"
  sx={{
    width: "100%",
    minWidth: 0,

    "& > .MuiTextField-root": {
      flex: {
        xs: "1 1 100%",
        sm: "1 1 280px",
        lg: "2 1 320px",
      },
      minWidth: 0,
    },

    "& > .MuiFormControl-root": {
      flex: {
        xs: "1 1 100%",
        sm: "1 1 210px",
        lg: "1 1 190px",
      },
      minWidth: "0 !important",
    },

    "& > .MuiButton-root": {
      flex: {
        xs: "1 1 100%",
        sm: "0 0 auto",
      },
      minWidth: { xs: "100%", sm: 170 },
      whiteSpace: "nowrap",
    },
  }}
>
                <TextField
                  fullWidth
                  size="small"
                  placeholder={TT.searchPlaceholder}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                    endAdornment: search ? (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setSearch("")}>
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ) : null,
                  }}
                />

                <FilterSelect
                  label={TT.moduleFilter}
                  value={moduleFilter}
                  onChange={setModuleFilter}
                >
                  <MenuItem value="all">{TT.all}</MenuItem>

                  {MODULE_OPTIONS.map((m) => (
                    <MenuItem key={m.value} value={m.value}>
                      {getTextValue(m.label, lang)}
                    </MenuItem>
                  ))}
                </FilterSelect>

                <FilterSelect
                  label={TT.categoryFilter}
                  value={categoryFilter}
                  onChange={setCategoryFilter}
                >
                  <MenuItem value="all">{TT.all}</MenuItem>

                  {categories.map((cat) => (
                    <MenuItem key={cat.id} value={cat.id}>
                      {getTextValue(cat.name, lang)}
                    </MenuItem>
                  ))}
                </FilterSelect>

                <FilterSelect
                  label={TT.stockType}
                  value={typeFilter}
                  onChange={setTypeFilter}
                >
                  <MenuItem value="all">{TT.all}</MenuItem>

                  {STOCK_TYPE_OPTIONS.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {TT[type.labelKey]}
                    </MenuItem>
                  ))}
                </FilterSelect>

                <FilterSelect
                  label={TT.stockStatusFilter}
                  value={statusFilter}
                  onChange={setStatusFilter}
                >
                  <MenuItem value="all">{TT.all}</MenuItem>
                  <MenuItem value="out">{TT.outOfStock}</MenuItem>
                  <MenuItem value="critical">{TT.lowStock}</MenuItem>
                  <MenuItem value="normal">{TT.normalStock}</MenuItem>
                  <MenuItem value="over">{TT.overStock}</MenuItem>
                </FilterSelect>

                <FilterSelect label={TT.sortBy} value={sortBy} onChange={setSortBy}>
                  {SORT_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {TT[opt.labelKey]}
                    </MenuItem>
                  ))}
                </FilterSelect>

                <Button
                  variant="outlined"
                  startIcon={<FilterAltIcon />}
                  onClick={clearFilters}
                  sx={{
                    borderRadius: 2.5,
                    textTransform: "none",
                    fontWeight: 900,
                    whiteSpace: "nowrap",
                    width: { xs: "100%", lg: "auto" },
                  }}
                >
                  {TT.clearFilters}
                </Button>
              </Stack>
            </CardContent>
          </Card>
        )}

        {activeTab === "dashboard" && (
          <Grid container spacing={2.5}>
            <Grid item xs={12} lg={8}>
              <SectionCard
                title={TT.critical}
                subtitle={`${TT.criticalCount}: ${summary.criticalCount} · ${TT.outCount}: ${summary.outCount}`}
                icon={<WarningAmberIcon />}
                action={
             <Button
  variant="outlined"
  onClick={() => {
    setSearch("");
    setModuleFilter("all");
    setCategoryFilter("all");
    setTypeFilter("all");
    setStatusFilter("all");
    setPage(1);
    setActiveTab("list");
  }}
  sx={{ borderRadius: 2, textTransform: "none", fontWeight: 900 }}
>
  {TT.stockList}
</Button>
                }
              >
                <Grid container spacing={1.4}>
                                  {items
                    .filter((item) => item.active !== false)
                    .filter((item) => ["critical", "out"].includes(getStockStatus(item)))
                    .slice(0, 6)
                    .map((item) => (

                      <Grid item xs={12} md={6} key={item.id}>
                        <Card
                          variant="outlined"
                          sx={{
                            borderRadius: 3,
                            p: 1.4,
                            bgcolor: alpha(BRAND.danger, 0.04),
                            borderColor: alpha(BRAND.danger, 0.18),
                          }}
                        >
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                            spacing={1}
                          >
                            <Box sx={{ minWidth: 0 }}>
                              <Typography variant="body2" sx={{ fontWeight: 950 }}>
                                {item.name}
                              </Typography>

                              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                {getCategoryName(categories, item.category_id, lang)}
                              </Typography>
                            </Box>

                            <StatusChip status={getStockStatus(item)} TT={TT} />
                          </Stack>

                          <LinearProgress
                            variant="determinate"
                            value={Math.max(
                              0,
                              Math.min(
                                100,
                                (parseNumber(item.current_stock) /
                                  Math.max(parseNumber(item.min_stock), 1)) *
                                  100
                              )
                            )}
                            sx={{
                              mt: 1.2,
                              height: 8,
                              borderRadius: 999,
                              bgcolor: alpha(BRAND.danger, 0.12),
                              "& .MuiLinearProgress-bar": {
                                bgcolor: BRAND.danger,
                              },
                            }}
                          />
                        </Card>
                      </Grid>
                    ))}

                                  {items
                    .filter((item) => item.active !== false)
                    .filter((item) =>
                      ["critical", "out"].includes(getStockStatus(item))
                    ).length === 0 && (

                    <Grid item xs={12}>
                      <Alert severity="success" sx={{ borderRadius: 3 }}>
                        {TT.normalStock}
                      </Alert>
                    </Grid>
                  )}
                </Grid>
              </SectionCard>
            </Grid>

            <Grid item xs={12} lg={4}>
              <SectionCard
                title={TT.moduleCompatibility}
                subtitle={TT.moduleCompatibilityText}
                icon={<SyncIcon />}
              >
                <Stack spacing={1.2}>
                  <FeaturePill
                    icon={<QrCode2Icon />}
                    title={TT.qrMenu}
                    text={TT.stockWillDrop}
                    color={BRAND.blue}
                  />

                  <FeaturePill
                    icon={<ContentCutIcon />}
                    title={TT.beauty}
                    text={TT.staffCommissionReadyText}
                    color={BRAND.orange}
                  />

                  <FeaturePill
                    icon={<RestaurantIcon />}
                    title={TT.recipeReady}
                    text={TT.recipeReadyText}
                    color={BRAND.success}
                  />
                </Stack>
              </SectionCard>
            </Grid>

            <Grid item xs={12}>
              <ReportsPanel
                items={items}
                movements={movements}
                TT={TT}
                lang={lang}
                categories={categories}
              />
            </Grid>
          </Grid>
        )}

       
        {activeTab === "list" && (
          <>
            <Grid
              className="stok-products-grid"
              container
              spacing={2}
              sx={{
                width: "100%",
                maxWidth: "100%",
                minWidth: 0,
                overflow: "hidden",
                "& > *": {
                  minWidth: 0,
                },
                "& .MuiCard-root": {
                  minWidth: 0,
                  maxWidth: "100%",
                  overflow: "hidden",
                },
                "& .MuiTypography-root": {
                  overflowWrap: "anywhere",
                },
              }}
            >
              {pagedItems.map((item) => (
                <Grid
                  item
                  xs={12}
                  sm={6}
                  lg={4}
                  xl={3}
                  key={item.backend_id || item.id}
                  sx={{
                    minWidth: 0,
                    maxWidth: "100%",
                  }}
                >
                  <StockItemCard
                    item={item}
                    TT={TT}
                    lang={lang}
                    categories={categories}
                    units={units}
                    suppliers={suppliers}
                    onEdit={openEditForm}
                    onDelete={openDelete}
                    onMovement={openMovement}
                    onSale={(selected) => openMovement(selected, "sale")}
                  />
                </Grid>
              ))}

              {pagedItems.length === 0 && (
                <Grid item xs={12}>
                  <Card
                    sx={{
                      borderRadius: 4,
                      py: 6,
                      px: 2,
                      textAlign: "center",
                      minWidth: 0,
                      overflow: "hidden",
                    }}
                  >
                    <InventoryIcon
                      sx={{
                        fontSize: 58,
                        color: alpha(BRAND.blue, 0.25),
                        mb: 1,
                      }}
                    />

                    <Typography variant="h6" sx={{ fontWeight: 950 }}>
                      {filteredItems.length === 0 &&
                      items.filter((x) => x.active !== false).length === 0
                        ? TT.noData
                        : TT.noFilteredData}
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{
                        color: "text.secondary",
                        mt: 0.5,
                        overflowWrap: "anywhere",
                      }}
                    >
                      {TT.mobileHint}
                    </Typography>

                    <Button
                      sx={{
                        mt: 2,
                        borderRadius: 2,
                        textTransform: "none",
                        fontWeight: 900,
                      }}
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={openNewForm}
                    >
                      {TT.addStockCard}
                    </Button>
                  </Card>
                </Grid>
              )}
            </Grid>

            {pageCount > 1 && (
              <Stack alignItems="center" sx={{ mt: 3, minWidth: 0 }}>
                <Pagination
                  count={pageCount}
                  page={page}
                  onChange={(_, val) => setPage(val)}
                  color="primary"
                  size="large"
                />
              </Stack>
            )}
          </>
        )}

           {activeTab === "sale" && (
          <QuickSalePanel
            items={items}
            setItems={setItems}
            setMovements={setMovements}
            TT={TT}
            lang={lang}
            settings={settings}
            customers={customers}
            setSnackbar={setSnackbar}
            serverMode={serverMode}
          />
        )}

        {activeTab === "movements" && (
          <MovementsPanel movements={movements} items={items} TT={TT} lang={lang} />
        )}

             {activeTab === "counting" && (
          <CountingPanel
            items={items}
            setItems={setItems}
            setMovements={setMovements}
            TT={TT}
            lang={lang}
            settings={settings}
            setSnackbar={setSnackbar}
            serverMode={serverMode}
          />
        )}


              {activeTab === "templates" && (
          <TemplatesPanel
            TT={TT}
            items={items}
            setItems={setItems}
            setSnackbar={setSnackbar}
            serverMode={serverMode}
          />
        )}

        {activeTab === "definitions" && (
          <DefinitionsPanel
            TT={TT}
            lang={lang}
            categories={categories}
            setCategories={setCategories}
            units={units}
            setUnits={setUnits}
            suppliers={suppliers}
            setSuppliers={setSuppliers}
          />
        )}

        {activeTab === "reports" && (
          <ReportsPanel
            items={items}
            movements={movements}
            TT={TT}
            lang={lang}
            categories={categories}
          />
        )}

        {activeTab === "settings" && (
          <SettingsPanel settings={settings} setSettings={setSettings} TT={TT} />
        )}

        {activeTab === "help" && <HelpPanel TT={TT} />}
      </Box>

      <StockFormDialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSave={saveStockItem}
        item={editingItem}
        setItem={setEditingItem}
        TT={TT}
        lang={lang}
        categories={categories}
        units={units}
        suppliers={suppliers}
      />

     <MovementDialog
  open={movementOpen}
  onClose={() => setMovementOpen(false)}
  item={movementItem}
  movementType={movementType}
  setMovementType={setMovementType}
  onSave={saveMovement}
  TT={TT}
  lang={lang}
  settings={settings}
  customers={customers}
/>

      <DeleteDialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={confirmDelete}
        TT={TT}
        item={deleteItem}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4200}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        sx={{ mb: { xs: 1.5, md: 2.5 } }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
       sx={{
  width: "100%",
  borderRadius: 3,
  boxShadow: "0 14px 38px rgba(15,23,42,0.18)",
}}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}